/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, runWithAuditReason } from '../../api/client';
import '../backlog/backlog.css';
import { LoaderSymbol, BusyIcon } from '../../components/loading';
import { StepHint } from '../../components/StepHint/StepHint';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { FormTitle } from '../../components/FormTitle';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { AppDataGrid, type AppColumn, useColumnManager, useHeaderMenu } from '../../components/AppDataGrid';
import { useSort, SortableHeader } from '../../components/SortableHeader';
import { statusVariant } from '../../components/statusPill';
import {
  ColFilterButton,
  type ColumnFilterValue,
} from '../../components/ColumnFilterDropdown';
import { ReportProgressOverlay } from './ReportProgressOverlay';
import { useNotify } from '../../components/notifications';
import { AppToolbar } from '../../components/AppToolbar';
import { useSaveFlow, useReasonGate, useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { useNavActions } from '../../hooks/useNavActions';
import { fmtLocalDate, fmtLabDate, fmtLabDateTime } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface MorReport {
  id:          string;
  reportId:    string;
  reportName:  string;
  createdBy:   string;
  createdDate: string;
}

/** Full detail returned from GET /api/v1/mor-adhoc/{id} — used to render
 *  the read-only "View Saved Report" panel. */
interface MorSavedReportDetail {
  id:                  string;
  reportId:            string;
  reportName:          string;
  createdBy:           string;
  createdDate:         string;
  modifiedBy:          string | null;
  modifiedDate:        string | null;
  dateCollectedFrom:   string | null;
  dateCollectedTo:     string | null;
  dateReceivedFrom:    string | null;
  dateReceivedTo:      string | null;
  dateAnalyzedFrom:    string | null;
  dateAnalyzedTo:      string | null;
  filtersJson:         string | null;
  selectedRowIdsJson:  string | null;
  reportTemplateName:  string | null;
}

// IdName removed — Job lookup is now JobLookup (id + name + projectId + clientId).
interface ClientLookup       { id: string; name: string; address: string; }
interface ProjectLookup      { id: string; projectId: string; projectName: string; customerId: string | null; }
interface TestLookup         { id: string; testName: string; methodNumber: string; testCode: string; isGroup: boolean; matrixName: string; }
interface SiteLookup         { id: string; name: string; projectId: string; clientId: string | null; }
interface ParameterLookup    { id: string; code: string; name: string; tests: string; methods: string; }
interface CategoryLookup     { id: number; name: string; }
interface SubCategoryLookup  { id: number; categoryId: number; categoryName: string; name: string; }

interface Lookups {
  clients:       ClientLookup[];
  projects:      ProjectLookup[];
  tests:         TestLookup[];
  parameters:    ParameterLookup[];
  sampleSites:   SiteLookup[];
  categories:    CategoryLookup[];
  subCategories: SubCategoryLookup[];
}

interface JobsBatches { jobs: JobLookup[]; batches: BatchLookup[]; }
/** Job lookup row — picker shows JobID + Client + ProjectID columns
 *  (matches GoLive Job ID picker layout, user feedback 2026-05-26). */
interface JobLookup   { id: string; name: string; projectId: string; clientId: string | null; }
/** QC Batch lookup row — picker shows Name + Test + Method columns. */
interface BatchLookup { id: string; name: string; testName: string; methodNumber: string; }

/** One distinct relation tuple from AI_MorAdhoc_RelationIndex_SP. */
interface RelationRow {
  clientId:      string | null;
  projectId:     string | null;
  testId:        string | null;
  methodNumber:  string | null;
  parameterId:   string | null;
  jobId:         string | null;
  batchId:       string | null;  // AnalyticalBatch.Oid (QC Batch picker)
  siteId:        string | null;  // Sample site/source
  siteName:      string | null;  // Pavithra drop 2026-06-01 — name-based site narrow
  categoryId:    number | null;
  subCategoryId: number | null;
}

/** One row from AI_MorAdhoc_Retrieve_SP — mirrors the BTLIMS XAF MOR Adhoc
 *  detail grid. IsQc flag splits Sample tab vs QC tab. */
interface RetrieveRow {
  sampleParameterId:  string;
  sampleLoginId:      string;
  jobOid:             string;
  jobId:              string | null;
  sampleNo:           number;
  sampleID:           string;
  qcBatchID:          string | null;
  sampleName:         string | null;
  siteName:           string | null;
  sampleMatrix:       string | null;
  dateCollected:      string | null;
  dateReceived:       string | null;
  dateAnalyzed:       string | null;
  sampleLocation:     string | null;
  client:             string | null;
  clientId:           string | null;
  projectId:          string | null;
  projectName:        string | null;
  test:               string | null;
  method:             string | null;
  parameter:          string | null;
  result:             string | null;
  finalResult:        string | null;
  units:              string | null;
  status:             string | null;
  qcTypeName:         string | null;
  isQc:               boolean;
  category:           string | null;
  subCategory:        string | null;
  // Bench-level data review columns surfaced on the Sample/QC grids per
  // user feedback 5/30/2026 #3 + #4.  Backend already supplies these via
  // MorAdhocRetrieveRow (Application/Common/Interfaces/IMorAdhocReader.cs).
  df?:                string | null;
  mrl?:               string | null;  // RL column
  mdl?:               string | null;
  loq?:               string | null;
  uql?:               string | null;
  sql?:               string | null;   // matches API JSON property; SampleParameter.FinalRptLimit ?? RptLimit
  qualifier?:         string | null;
  prepBatchID?:       string | null;
  analyst?:           string | null;
  // QC measurement columns (FB4 5/30/2026) — populated only on QC rows
  // (isQc = true).  Sample rows return null.
  spikeAmount?:       string | null;
  pctRec?:            string | null;
  rpd?:               string | null;
  recCtrlLimit?:      string | null;
  rpdCtrlLimit?:      string | null;
}

/** One historic row from BTLIMSDC — shape returned by /historic/retrieve
 *  (mirrors C# MorAdhocHistoricRow).  Columns map cleanly to a subset of the
 *  live RetrieveRow shape so the same grid component can render both. */
interface HistoricRow {
  rowId:         number;
  sampleID:      string | null;
  client:        string | null;
  projectId:     string | null;
  jobId:         string | null;
  siteName:      string | null;
  sampleMatrix:  string | null;
  test:          string | null;
  method:        string | null;
  parameter:     string | null;
  result:        string | null;
  finalResult:   string | null;
  units:         string | null;
  dateCollected: string | null;
  dateReceived:  string | null;
  dateAnalyzed:  string | null;
  approvedDate:  string | null;
  analyst:       string | null;
  reviewedBy:    string | null;
  reviewedDate:  string | null;
  qualifier:     string | null;
  mrl:           string | null;
  df:            string | null;
  qcTypeName:    string | null;
  isQc:          boolean;
}

type ViewMode = 'list' | 'detail';

/** One row from /api/v1/mor-adhoc/report-templates — feeds the "Report
 *  Template" picker (active rows in tbl_Public_CustomReportDesignerDetails). */
interface ReportTemplate {
  name:        string;  // internal name (XtraReport class name in GoLive)
  displayName: string;  // friendly label shown to the user
}

/** Compact filter state: comma-separated id strings (matches BTLIMS pattern). */
interface QueryFilters {
  clientIds:        Set<string>;
  projectIds:       Set<string>;
  testIds:          Set<string>;  // TestMethod.Oid
  methodNumbers:    Set<string>;  // cascades from testIds; uses method number text
  parameterIds:     Set<string>;
  jobIds:           Set<string>;
  batchIds:         Set<string>;
  siteIds:          Set<string>;
  categoryIds:      Set<number>;  // Regulatory Compliance categories
  subCategoryIds:   Set<number>;  // Regulatory Compliance sub-categories
}

const EMPTY_FILTERS: QueryFilters = {
  clientIds: new Set(), projectIds: new Set(),
  testIds: new Set(), methodNumbers: new Set(), parameterIds: new Set(),
  jobIds: new Set(), batchIds: new Set(), siteIds: new Set(),
  categoryIds: new Set(), subCategoryIds: new Set(),
};

/** Build a local `yyyy-MM-dd` string for an API date param (NOT display). */
function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
/** Props for embedded mode — when MOR Pending Approval / Pending Delivery
 *  list pages click an adhoc row, they render this component inline with
 *  these props set.  The nav highlight stays on the host page (no route
 *  change) and the saved report opens in read-only detail view directly.
 *
 *  When all three props are omitted, the component behaves as a standalone
 *  page (URL-driven, with its own internal saved-reports list view). */
export interface MorAdhocReportingPageProps {
  /** Saved-report id to auto-open in read-only detail mode. */
  initialSavedId?: string;
  /** Override the "Back to list" button — invoked instead of returning
   *  the component to its own internal list view.  Used by an embedding
   *  parent to swap back to its grid. */
  onBack?: () => void;
  /** Override the page title + breadcrumb tail when embedded. */
  pageCaption?: { breadcrumb: string; title: string; subtitle?: string };
}

export default function MorAdhocReportingPage(props: MorAdhocReportingPageProps = {}) {
  const { initialSavedId, onBack, pageCaption } = props;
  const notify = useNotify();
  const runSave = useSaveFlow();
  const gateReason = useReasonGate();
  const { confirm: confirmDlg } = useConfirm();
  // Ad Hoc Reporting = 'morreport'. New + Save mint / persist a report → Create-
  // gated: hidden for a user whose MAC level lacks Create. Load / Preview /
  // Export are read-only and stay available. Falls open for admins / no MAC data.
  const perm = useNavActions('morreport');
  const embedded = !!initialSavedId;
  // ── List / detail view ───────────────────────────────────────────────────
  // When embedded with a saved-report id, start straight in detail mode —
  // the host list page already shows the picker.
  const [mode, setMode]         = useState<ViewMode>(embedded ? 'detail' : 'list');

  // ── List data ────────────────────────────────────────────────────────────
  const [reports, setReports]   = useState<MorReport[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listMs,  setListMs]    = useState<number | null>(null);
  // FB 5/31/2026 — Match Custom Reporting layout: Date Range + Search in
  // the section-hdr toolbar.  These narrow the saved-reports list client-side.
  type ListDateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';
  const [listDateRange, setListDateRange] = useState<ListDateRangeKey>('3M');
  const [listSearch,    setListSearch]    = useState<string>('');
  // Saved-report list row selection (BO UI Standard §9: a leading checkbox
  // column is required on any grid whose toolbar acts on ticked rows — the
  // toolbar "Export to" downloads the ticked reports).  Added 2026-07-06.
  const [listSel,       setListSel]       = useState<Set<string | number>>(new Set());
  // Prune ticks whose row disappeared (delete / reload) so Export never acts
  // on a stale id.
  useEffect(() => {
    setListSel(prev => {
      const next = new Set(Array.from(prev).filter(id => reports.some(r => r.id === id)));
      return next.size === prev.size ? prev : next;
    });
  }, [reports]);

  // ── Lookups (loaded once) ────────────────────────────────────────────────
  const [lookups, setLookups]   = useState<Lookups | null>(null);
  const [jobsBatches, setJobsBatches] = useState<JobsBatches>({ jobs: [], batches: [] });
  const [loadingLookups, setLoadingLookups] = useState(false);

  // ── Report templates + batch report selection ────────────────────────────
  // Sourced from tbl_Public_CustomReportDesignerDetails (Active=1).
  // The "Report Template" picker mirrors the ALPACA GoLive XAF dropdown;
  // the DOC + MDL buttons fire pre-configured batch reports.
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // ── Filter state on the query panel ──────────────────────────────────────
  const [filters, setFilters]   = useState<QueryFilters>(EMPTY_FILTERS);

  // ── Retrieve result + loading state ──────────────────────────────────────
  const [retrieved,  setRetrieved]  = useState(false);
  const [retrieving, setRetrieving] = useState(false);
  const [retrieveRows, setRetrieveRows] = useState<RetrieveRow[]>([]);
  const [retrieveMs, setRetrieveMs] = useState<number | null>(null);
  const [retrieveErr, setRetrieveErr] = useState<string | null>(null);
  // On-demand snapshot refresh — POSTs to /api/v1/mor-adhoc/snapshot/refresh
  // which triggers AI_MorAdhocSnapshots_RefreshAll.  The same job runs
  // overnight; this button lets the client see freshly-approved rows
  // before the next scheduled refresh.
  // refreshingSnapshot state retired with the "Refresh Snapshot" button.
  // After a successful Retrieve we collapse the query panel so the results
  // grid uses the full width — the user re-opens it via the "Show Query"
  // button to change filters and re-run.  Reset to false on Clear / Back.
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  // Classic server-side pagination (user feedback 2026-05-30): each click on
  // Retrieve / Prev / Next fetches exactly one page from the server.
  // No background fill, no progressive streaming.  Per-tab page state is
  // remembered so flipping Sample ⇄ QC keeps the user where they left off.
  // PAGE_SIZE is user-tunable via the "Rows" dropdown in the pagination bar
  // (2026-05-30 — matches the GoLive `Rows: 100 ▾  Prev | Page 1 / 1 | Next`
  // pager).  Defaults to 100 per page.
  const [PAGE_SIZE, setPageSize] = useState(100);
  const [samplePage, setSamplePage] = useState(1);     // 1-based
  const [qcPage,     setQcPage]     = useState(1);
  const [serverSampleCount, setServerSampleCount] = useState(0);
  const [serverQcCount,     setServerQcCount]     = useState(0);
  // Export-to-XLSX flow (independent of the in-flight Retrieve).
  const [exportingGrid, setExportingGrid] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Preview / export render cancellation (prototype fix #3, 2026-07-03): the
  // progress overlay's Stop button aborts the in-flight render request for
  // real — the generation is genuinely cancelled, not just hidden.
  const previewAbortRef = useRef<AbortController | null>(null);
  // RC Monthly Retrieve loads the full categorized sample-detail set once
  // (AI_MorAdhoc_RCSampleDetails_SP — the BTLIMS FillRCSampleDetailsDE_sp port)
  // and pages it client-side.  Cleared on each fresh Retrieve / Clear all.
  // (Restored after a later drop accidentally removed it — drop #17 feature.)
  const rcSampleAllRef = useRef<RetrieveRow[]>([]);
  // When the user clicks the "Query" button after a Retrieve, the panel
  // re-appears as a centred MODAL OVERLAY (not back in its old inline spot).
  // This way the results grid behind it stays visible for cross-reference.
  // Set to false again when the modal closes (user presses ×, hits Retrieve,
  // or clicks the backdrop).
  const [panelAsPopup, setPanelAsPopup] = useState(false);
  // Truncation feedback — API returns meta.truncated=true when the row cap
  // is hit (default 5,000).  We show a yellow banner asking the user to
  // narrow filters; the SP cost of an unfiltered Retrieve dominates the
  // 30–40s slow-server wait the user reported on 2026-05-27.
  const [retrieveTruncated, setRetrieveTruncated] = useState(false);
  const [retrieveCap] = useState<number>(5000);
  const [activeTab, setActiveTab] = useState<'sample' | 'qc'>('sample');
  const [exporting, setExporting] = useState(false);
  // LSI Report download — independent of RC Monthly so the two spinners
  // don't fight each other.
  const [lsiExporting, setLsiExporting] = useState(false);
  // Water Stability Report download — its own busy flag, same reasoning.
  const [wsExporting, setWsExporting] = useState(false);
  // PDF bench-report preview is generated server-side and can take several
  // seconds.  Without a busy flag the Preview button looked frozen — user
  // feedback 2026-05-29: "very slow … preview button is loading".  This drives
  // the "Generating…" spinner + disabled state for the PDF path (the Excel
  // paths already had exporting/lsiExporting).
  const [previewLoading, setPreviewLoading] = useState(false);
  // User feedback 2026-06-01: Preview must reflect the grid's column
  // filters + row selection.  The RetrieveResultsGrid emits the surviving
  // sampleParameterIds here on every filter/selection change; the Preview
  // handler forwards them as `selectedRowIds` to the report-preview API.
  const [gridVisibleRowIds, setGridVisibleRowIds] = useState<string[]>([]);

  // ── Save state ───────────────────────────────────────────────────────────
  // savedId/savedReportId set after a successful Save — second click becomes
  // an update of that record rather than a new insert.
  const [savedId, setSavedId]                 = useState<string | null>(null);
  const [savedReportId, setSavedReportId]     = useState<string | null>(null);
  const [reportName, setReportName]           = useState<string>('');
  // Id of the saved report currently being deleted (spinner + disable).
  const [deletingId, setDeletingId]           = useState<string | null>(null);
  // Key of the saved-report row currently downloading, as `${id}:${ext}` so
  // the PDF and Excel buttons on the same row spin independently.
  const [rowDownloadKey, setRowDownloadKey]   = useState<string | null>(null);
  const [saving, setSaving]                   = useState(false);

  // ── Date Collected range (drives the RC Monthly report) ──────────────────
  // Both start EMPTY — the user picks the range explicitly.  Nothing fetches
  // (no relations, no retrieve, no export) until both bounds are valid.
  // `defaultRange` is kept around so the "reset" link snaps to last full month.
  const defaultRange = useMemo(() => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay   = new Date(now.getFullYear(), now.getMonth(),    0);
    return { from: fmtDate(lastMonth), to: fmtDate(lastDay) };
  }, []);
  void defaultRange;     // kept for potential future "reset" UX
  const [dateCollectedFrom, setDateCollectedFrom] = useState<string>('');
  const [dateCollectedTo,   setDateCollectedTo]   = useState<string>('');

  /** True only when both EXPLICIT Date Collected bounds are filled in.
   *  Note: the RC Monthly + LSI gates use `dateRangeReadyOrChip` (defined
   *  after `dateRange`) — that variant also passes when the Date Range
   *  chip alone is set, since the chip implies a derivable window. */
  const dateRangeReady = !!(dateCollectedFrom && dateCollectedTo
    && dateCollectedFrom <= dateCollectedTo);

  // Date Received range — independent of Date Collected, both optional
  const [dateReceivedFrom, setDateReceivedFrom] = useState<string>('');
  const [dateReceivedTo,   setDateReceivedTo]   = useState<string>('');

  // Date Analyzed range — third independent date filter (PDF feedback #14).
  const [dateAnalyzedFrom, setDateAnalyzedFrom] = useState<string>('');
  const [dateAnalyzedTo,   setDateAnalyzedTo]   = useState<string>('');

  // ── FB2 — Date-range quick-pick (5/31/2026) ─────────────────────────────
  // Replaces the earlier Historic Data checkbox.  Mirrors the dropdown the
  // other list pages use (1M / 3M / 6M / 1Y / 2Y / 5Y / All).  When the
  // user picks a range:
  //   • dateReceivedFrom / dateReceivedTo auto-populate from the cutoff
  //   • Retrieve auto-routes to BTLIMSDC historic source when the cutoff
  //     reaches back further than the snapshot covers (>= 1 year).
  // No separate "Historic" checkbox; the date range drives the source.
  type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';
  const [dateRange, setDateRange] = useState<DateRangeKey>('3M');
  /** Months a range key reaches back.  'All' = unbounded (Infinity). */
  const rangeMonths = useCallback((k: DateRangeKey): number => {
    switch (k) {
      case '1M': return 1;
      case '3M': return 3;
      case '6M': return 6;
      case '1Y': return 12;
      case '2Y': return 24;
      case '5Y': return 60;
      case 'All': return Number.POSITIVE_INFINITY;
    }
  }, []);
  /** Auto-routed: anything BEYOND 3 months reaches data the
   *  AI_MorAdhocSnapshot doesn't cover, so it pivots to the BTLIMSDC
   *  historic source (3.2M-row legacy table).  1M / 3M stay on the
   *  current snapshot (sub-second); 6M / 1Y / 2Y / 5Y / All go historic.
   *
   *  Fix 5/31/2026: previously this threshold was `>= 12` which meant
   *  6M-range Retrieve hit the snapshot (empty for 4-6 month-old rows)
   *  and returned no rows.  User feedback: "when we use more than 3M
   *  not retrieved — needs to come from BTLIMSDC table." */
  const historicMode = rangeMonths(dateRange) > 3;

  /** Returns the lower-bound DateReceived for the chosen range key, or null
   *  for 'All' (no lower bound). */
  const rangeCutoff = useCallback((k: DateRangeKey): Date | null => {
    if (k === 'All') return null;
    const now = new Date();
    const m = rangeMonths(k);
    return new Date(now.getFullYear(), now.getMonth() - m, now.getDate());
  }, [rangeMonths]);

  /** Min date for the calendar pickers — derived from the Date Range chip
   *  so dates older than the cutoff are disabled in the native date picker.
   *  E.g. chip = 3M  → min = today − 3 months; chip = 'All' → no min.
   *  User feedback 2026-05-31: "this is 3m previous also need to disabled". */
  const rangePickerMin: string | undefined = (() => {
    const co = rangeCutoff(dateRange);
    return co ? fmtDate(co) : undefined;
  })();
  /** Max date is always today — future dates can't have results yet. */
  const rangePickerMax: string = fmtDate(new Date());

  /** RC Monthly + LSI gate — passes when EITHER the explicit Date Collected
   *  pickers are both set OR the Date Range chip is set to a finite window
   *  (1M/3M/6M/1Y/2Y/5Y).  Resolves the user feedback 2026-05-31 "RC monthly
   *  report not showing": the gate hard-blocked when only the chip was set,
   *  even though `effectiveCollectedRange` could derive From/To from it. */
  const dateRangeReadyOrChip = dateRangeReady || dateRange !== 'All';

  /** Returns the effective {from, to} Date Collected window the report
   *  should use — explicit pickers win; otherwise derive from the chip;
   *  null means "no window set" (only happens with chip='All' + no
   *  pickers, which the dateRangeReadyOrChip gate rejects upstream). */
  function effectiveCollectedRange(): { from: string; to: string } | null {
    if (dateCollectedFrom && dateCollectedTo) {
      return { from: dateCollectedFrom, to: dateCollectedTo };
    }
    const co = rangeCutoff(dateRange);
    if (!co) return null;
    return { from: fmtDate(co), to: fmtDate(new Date()) };
  }

  /** One-shot seed flag — see the seed effect placed AFTER `readOnly` is
   *  declared (the effect references it).  Default to `false`; the effect
   *  flips it after the first paint so subsequent renders don't re-seed. */
  const didSeedDateRef = useRef(false);

  // Relation index for cross-faceted filtering. Loaded once whenever the Date
  // Collected range changes.
  const [relations, setRelations] = useState<RelationRow[]>([]);
  const [relationsLoading, setRelationsLoading] = useState(false);
  void relationsLoading;  // no longer shown inline on Date Collected — kept for the loader

  // Delete a saved report (soft-delete on the server).  Confirms first,
  // shows a spinner on the row's trash icon, then reloads the list.
  // Server returns 409 if Status='Approved' — surfaces a friendly message.
  const handleDeleteReport = useCallback(async (id: string, reportId: string) => {
    // Shared centred danger confirm (standard dialog format, 2026-07-03 — was native confirm).
    const okDel = await confirmDlg({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Delete saved report "${reportId}"?  This cannot be undone from the UI.`,
    });
    if (!okDel) return;
    // Reason-for-change gate (standing rule 11) — prompts only when "MOR Adhoc
    // Reporting" has ReasonRequired ON; null = cancelled → abort.
    const reason = await gateReason('MOR Adhoc Reporting', 'delete');
    if (reason === null) return;
    setDeletingId(id);
    try {
      // axios resolves for 2xx; 204 No Content from /mor-adhoc/{id} means
      // the row was soft-deleted server-side.  Update local state + force
      // a list refetch so the user sees the row disappear immediately.
      await runWithAuditReason(reason, () => api.delete(`/api/v1/mor-adhoc/${id}`));
      setReports(rs => rs.filter(r => r.id !== id));
      // Belt-and-braces: refetch from server so any concurrent edits also
      // reconcile.  (User feedback 2026-05-27: "delete also not working" —
      // suspected stale local state.)
      void loadList();
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } } };
      const code = err.response?.data?.error?.code;
      console.error('Delete failed:', err.response?.status, code, err.response?.data);
      if (err.response?.status === 404) {
        notify.warning(`${reportId} was already removed. Refreshing list.`);
        void loadList();
      } else {
        notify.error(`Delete failed: ${err.response?.data?.error?.message ?? 'check the API logs.'}`);
      }
    } finally {
      setDeletingId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Direct PDF / Excel download from the saved-reports list ───────────
  // Generates and downloads a saved report straight from its list row —
  // no need to open it first.  Self-contained: it fetches the saved record,
  // rebuilds the same query string the Preview/Excel handlers use from the
  // persisted filter snapshot, and dispatches to the matching endpoint:
  //   • Bench / QC  → report-preview      (PDF, or xlsx when asExcel)
  //   • RC Monthly  → rc-monthly/excel    (Excel only)
  //   • LSI Report  → lsi-report/excel     (Excel only, name-mapped filters)
  // `savedId` is forwarded so the renderer drops the Draft watermark, exactly
  // like opening the report and clicking Preview.
  /** Toolbar "Export to" — downloads every TICKED saved report (BO UI
   *  Standard §9: the leading checkbox column backs this action; prototype
   *  screenAdhoc adhocExportSelected).  LSI / RC Monthly have no PDF on the
   *  backend, so those two download as Excel; everything else as PDF. */
  async function exportSelectedReports() {
    const ids = Array.from(listSel).map(String);
    if (ids.length === 0) { notify.warning('Tick at least one report to enable Export.'); return; }
    for (const id of ids) {
      const row = reports.find(r => r.id === id);
      const rn = (row?.reportName ?? '').trim().toLowerCase();
      const excelOnly = rn === 'lsi report' || rn === 'rc monthly report'
        || rn === 'ccpp report' || rn === 'water stability report';
      // Sequential on purpose — parallel blob downloads trip popup blockers.
      await downloadSavedReport(id, excelOnly);
    }
    notify.success('Exported successfully.');
  }

  async function downloadSavedReport(id: string, asExcel: boolean) {
    const ext = asExcel ? 'xlsx' : 'pdf';
    const key = `${id}:${ext}`;
    setRowDownloadKey(key);
    try {
      const res = await api.get(`/api/v1/mor-adhoc/${id}`);
      const d   = (res.data as { data: MorSavedReportDetail }).data;
      const tpl = d.reportTemplateName ?? '';

      // RC Monthly, LSI and Water Stability are Excel-only on the backend — no PDF.
      if (!asExcel && (tpl === TPL_RC_MONTHLY || tpl === TPL_LSI_REPORT || tpl === TPL_WATER_STABILITY)) {
        notify.warning('PDF is not available for this report type — use the Excel download.');
        return;
      }

      type FiltersSnapshot = Partial<{
        clientIds:     string[]; projectIds:  string[]; testIds: string[];
        methodNumbers: string[]; parameterIds: string[]; jobIds: string[];
        batchIds:      string[]; siteIds:     string[];
        categoryIds:   number[]; subCategoryIds: number[];
      }>;
      let f: FiltersSnapshot = {};
      try { f = d.filtersJson ? JSON.parse(d.filtersJson) as FiltersSnapshot : {}; } catch { /* ignore */ }

      const dFrom = d.dateCollectedFrom?.substring(0, 10) ?? '';
      const dTo   = d.dateCollectedTo  ?.substring(0, 10) ?? '';
      const rFrom = d.dateReceivedFrom ?.substring(0, 10) ?? '';
      const rTo   = d.dateReceivedTo   ?.substring(0, 10) ?? '';

      const params = new URLSearchParams();
      const addArr = (k: string, a?: (string | number)[]) => {
        if (a && a.length > 0) params.set(k, a.join(','));
      };

      let urlPath: string;
      if (tpl === TPL_RC_MONTHLY) {
        // Fix (v26.7.2, 2026-07-10): allow a saved Date RECEIVED range as a valid
        // alternative to Date Collected (matching LSI), and ALWAYS send @DateType so
        // AI_MorAdhoc_RCMonthly_SP picks the right date column — a missing dateType
        // made both SP branches evaluate false and the query returned 0 rows.
        if (!dFrom && !dTo && !rFrom && !rTo) {
          notify.warning('This RC Monthly report has no saved date range; reopen it to regenerate.');
          return;
        }
        if (dFrom && dTo) {
          params.set('dateFrom',  dFrom);
          params.set('dateTo',    dTo);
          params.set('dateType',  'Collected');
          if (rFrom) params.set('receivedFrom', rFrom);
          if (rTo)   params.set('receivedTo',   rTo);
        } else if (rFrom && rTo) {
          params.set('receivedFrom', rFrom);
          params.set('receivedTo',   rTo);
          params.set('dateType',     'Received');
        }
        addArr('categoryIds',    f.categoryIds);
        addArr('subCategoryIds', f.subCategoryIds);
        addArr('clientIds',      f.clientIds);
        addArr('projectIds',     f.projectIds);
        addArr('testIds',        f.testIds);
        addArr('methodNumbers',  f.methodNumbers);
        addArr('parameterIds',   f.parameterIds);
        addArr('jobIds',         f.jobIds);
        params.set('savedId', id);
        urlPath = '/api/v1/mor-adhoc/rc-monthly/excel';
      } else if (tpl === TPL_LSI_REPORT) {
        // Regenerate using the date AXIS the report was SAVED with — Collected,
        // else Received, else Analyzed — matching the live generate flow and the
        // SP's @DateBasis.  Rev 8 (2026-06-26): previously this only honoured
        // Date Collected, so a report SAVED with a Received/Analyzed range
        // errored on download ("no saved Date Collected range") even though the
        // range was persisted.  We send the saved window + its basis.
        const aFrom = d.dateAnalyzedFrom?.substring(0, 10) ?? '';
        const aTo   = d.dateAnalyzedTo  ?.substring(0, 10) ?? '';
        let basis: 'collected' | 'received' | 'analyzed';
        let wFrom: string, wTo: string;
        if (dFrom && dTo)      { basis = 'collected'; wFrom = dFrom; wTo = dTo; }
        else if (rFrom && rTo) { basis = 'received';  wFrom = rFrom; wTo = rTo; }
        else if (aFrom && aTo) { basis = 'analyzed';  wFrom = aFrom; wTo = aTo; }
        else {
          notify.warning('This LSI report has no saved date range; reopen it to regenerate.');
          return;
        }
        params.set('dateFrom',  wFrom);
        params.set('dateTo',    wTo);
        params.set('dateBasis', basis);
        params.set('savedId',   id);
        // Rev 7 (2026-06-23): the LSI endpoint matches by GUID, so the saved
        // filter GUIDs go straight through — no lookups, no name/code mapping
        // (which was lossy: non-unique Project CODE / Customer name leaked
        // sibling-project rows).  Same keys the grid uses; Job is now included
        // too (it's just a GUID from the saved filter JSON).
        addArr('clientIds',  f.clientIds);
        addArr('projectIds', f.projectIds);
        addArr('jobIds',     f.jobIds);
        addArr('siteIds',    f.siteIds);
        urlPath = '/api/v1/mor-adhoc/lsi-report/excel';
      } else if (tpl === TPL_WATER_STABILITY) {
        // Same saved-range regeneration as LSI (Collected → Received → Analyzed),
        // just the Water Stability endpoint.
        const aFrom = d.dateAnalyzedFrom?.substring(0, 10) ?? '';
        const aTo   = d.dateAnalyzedTo  ?.substring(0, 10) ?? '';
        let basis: 'collected' | 'received' | 'analyzed';
        let wFrom: string, wTo: string;
        if (dFrom && dTo)      { basis = 'collected'; wFrom = dFrom; wTo = dTo; }
        else if (rFrom && rTo) { basis = 'received';  wFrom = rFrom; wTo = rTo; }
        else if (aFrom && aTo) { basis = 'analyzed';  wFrom = aFrom; wTo = aTo; }
        else {
          notify.warning('This CCPP report has no saved date range — open it, set a Date Range, and Save again to regenerate.');
          return;
        }
        params.set('dateFrom',  wFrom);
        params.set('dateTo',    wTo);
        params.set('dateBasis', basis);
        params.set('savedId',   id);
        addArr('clientIds',  f.clientIds);
        addArr('projectIds', f.projectIds);
        addArr('jobIds',     f.jobIds);
        addArr('siteIds',    f.siteIds);
        urlPath = '/api/v1/mor-adhoc/water-stability-report/excel';
      } else {
        // Bench Results / QC Combo — the standard preview endpoint.
        params.set('template', tpl);
        if (dFrom) params.set('dateFrom', dFrom);
        if (dTo)   params.set('dateTo',   dTo);
        if (rFrom) params.set('receivedFrom', rFrom);
        if (rTo)   params.set('receivedTo',   rTo);
        addArr('jobIds',         f.jobIds);
        addArr('batchIds',       f.batchIds);
        addArr('clientIds',      f.clientIds);
        addArr('projectIds',     f.projectIds);
        addArr('testIds',        f.testIds);
        addArr('methodNumbers',  f.methodNumbers);
        addArr('parameterIds',   f.parameterIds);
        addArr('siteIds',        f.siteIds);
        addArr('categoryIds',    f.categoryIds);
        addArr('subCategoryIds', f.subCategoryIds);
        // NB: deliberately NOT forwarding the saved `selectedRowIdsJson`.
        // The preview endpoint treats selectedRowIds as a live grid-narrowing
        // allow-list — for the QC Combo it resolves them into batch+row+param
        // tuples and keeps ONLY matching QC rows.  The saved snapshot holds
        // sample-tab Oids, which don't map to QC rows, so passing them filters
        // the report down to nothing ("No QC rows for the selected batches").
        // The normal "open report → Preview" path also omits selectedRowIds
        // on a freshly-opened saved report (the grid isn't narrowed), so the
        // report regenerates from its persisted filters — we mirror that here.
        params.set('savedId', id);
        if (asExcel) params.set('format', 'xlsx');
        urlPath = '/api/v1/mor-adhoc/report-preview';
      }

      const blobRes = await api.get(`${urlPath}?${params.toString()}`, { responseType: 'blob' });
      const mime = asExcel
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const blob = new Blob([blobRes.data as BlobPart], { type: mime });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(d.reportId || tpl || 'report').replace(/[^\w.-]+/g, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      console.error(e);
      let msg: string | undefined;
      const body = (e as { response?: { data?: unknown } })?.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text()) as { error?: { message?: string } };
          msg = parsed?.error?.message;
        } catch { /* fall through */ }
      } else if (body && typeof body === 'object') {
        msg = (body as { error?: { message?: string } })?.error?.message;
      }
      notify.error(`${asExcel ? 'Excel' : 'PDF'} download failed: ${msg ?? 'check the API logs.'}`);
    } finally {
      setRowDownloadKey(null);
    }
  }

  // ── Read-only view of a saved MOR Adhoc Report ────────────────────────
  // When the user clicks a Report ID in the saved list we load the saved
  // record, populate ALL filter / date / template state from it, switch to
  // the detail page, and lock every input.  Re-uses the existing detail
  // layout — no duplicate UI — so the user sees exactly the same screen
  // they'd see when building a fresh report, just frozen.  The action
  // toolbar (Retrieve/Save/Preview/Clear) is hidden; a banner across the
  // top shows status + Approve + Back to list.
  const [readOnly, setReadOnly]           = useState(false);
  const [viewedReport, setViewedReport]   = useState<MorSavedReportDetail | null>(null);
  // One-shot flag → after entering read-only mode, fire Retrieve once
  // lookups have loaded so the saved row set actually re-materialises.
  const [pendingRetrieve, setPendingRetrieve] = useState(false);

  // User feedback 2026-05-31: do NOT auto-seed Date Received when the
  // detail page opens.  The previous build pre-filled "3 months ago → today"
  // which confused users into thinking they had to clear the dates manually.
  // Now Date Received starts empty.  The Date Range chip in the top toolbar
  // still WRITES into Date Received when the user picks a range explicitly.
  // (Saved reports open in read-only mode and keep their persisted dates;
  // that path doesn't go through this seed effect anyway.)
  void didSeedDateRef;   // kept declared in case other code still imports it

  const handleViewReport = useCallback(async (id: string) => {
    try {
      const res = await api.get(`/api/v1/mor-adhoc/${id}`);
      const d   = (res.data as { data: MorSavedReportDetail }).data;

      // Parse the filter snapshot back into the page's filter state.
      type FiltersSnapshot = Partial<{
        clientIds:      string[]; projectIds: string[]; testIds: string[];
        methodNumbers:  string[]; parameterIds: string[]; jobIds: string[];
        batchIds:       string[]; siteIds: string[];
        categoryIds:    number[]; subCategoryIds: number[];
      }>;
      let f: FiltersSnapshot = {};
      try { f = d.filtersJson ? JSON.parse(d.filtersJson) as FiltersSnapshot : {}; } catch { /* ignore */ }
      setFilters({
        clientIds:      new Set(f.clientIds      ?? []),
        projectIds:     new Set(f.projectIds     ?? []),
        testIds:        new Set(f.testIds        ?? []),
        methodNumbers:  new Set(f.methodNumbers  ?? []),
        parameterIds:   new Set(f.parameterIds   ?? []),
        jobIds:         new Set(f.jobIds         ?? []),
        batchIds:       new Set(f.batchIds       ?? []),
        siteIds:        new Set(f.siteIds        ?? []),
        categoryIds:    new Set<number>(f.categoryIds    ?? []),
        subCategoryIds: new Set<number>(f.subCategoryIds ?? []),
      });
      setDateCollectedFrom(d.dateCollectedFrom?.substring(0, 10) ?? '');
      setDateCollectedTo  (d.dateCollectedTo  ?.substring(0, 10) ?? '');
      setDateReceivedFrom (d.dateReceivedFrom ?.substring(0, 10) ?? '');
      setDateReceivedTo   (d.dateReceivedTo   ?.substring(0, 10) ?? '');
      // Restore the Date Analyzed range that was used when the report
      // was saved (added 2026-05-28 — bug fix: was previously dropped).
      setDateAnalyzedFrom (d.dateAnalyzedFrom ?.substring(0, 10) ?? '');
      setDateAnalyzedTo   (d.dateAnalyzedTo   ?.substring(0, 10) ?? '');
      setSelectedTemplate(d.reportTemplateName ?? '');
      setSavedId(d.id);
      setSavedReportId(d.reportId);
      setReportName(d.reportName);
      setViewedReport(d);
      setReadOnly(true);
      setRetrieved(false);
      setRetrieveRows([]);
      setMode('detail');
      setPendingRetrieve(true);   // useEffect below picks this up
      // Lookups loaded by a separate effect (see below) that watches `mode`.
    } catch (e: unknown) {
      console.error(e);
      notify.error('Could not load saved report — check the API logs.');
    }
  }, []);

  // ── Auto-open a saved report ─────────────────────────────────────────────
  // Two paths into the read-only detail view:
  //   1. Embedded mode — parent passes `initialSavedId`.
  //   2. URL deep-link mode — `?id=<guid>` on the MOR Adhoc Reporting page.
  // Runs ONCE per mount.
  useEffect(() => {
    if (embedded) {
      void handleViewReport(initialSavedId!);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const id     = params.get('id');
    if (!id) return;
    void handleViewReport(id);
    // Clear the params so a manual refresh doesn't re-trigger and the
    // user's URL stays tidy.
    try {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Initial loads ────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoadingList(true);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/mor-adhoc');
      setReports((res.data as { data: MorReport[] }).data ?? []);
    } catch (e) {
      console.error(e); setReports([]);
    } finally {
      setListMs(performance.now() - t0);
      setLoadingList(false);
    }
  }, []);

  // `silent` = keep this OUT of the global top loading bar. The on-mount eager
  // preload passes silent=true so the bar settles once the saved-report LIST is
  // shown; an explicit "+ New" that has to wait for lookups shows the bar normally.
  const loadLookups = useCallback(async (silent = false) => {
    if (lookups) return;
    setLoadingLookups(true);
    try {
      // Run lookups + report templates in parallel — both feed the query panel
      // and the templates list is tiny so the extra round-trip is cheap.
      const [lookupsRes, templatesRes] = await Promise.all([
        api.get('/api/v1/mor-adhoc/lookups', { silentLoading: silent } as never),
        api.get('/api/v1/mor-adhoc/report-templates', { silentLoading: silent } as never),
      ]);
      const rawLookups = (lookupsRes.data as { data: Lookups }).data;
      // ── Dedupe sampleSites by name + client ────────────────────────────
      // The backend SP returns one row per Site record, but in the legacy
      // ALPACA DB the same SiteName often exists 2-3× with different Oids
      // (one row per project linkage, plus orphan rows with no Client).
      // The retrieve filter (line ~1395) uses s.name — not s.id — so
      // collapsing duplicates here is safe.  When duplicates exist, we
      // prefer the row that carries the richest metadata (ProjectId set).
      //
      // 2026-06-25: key is NAME + CLIENT, not name alone.  The same SiteName
      // (e.g. "D1") exists under several DIFFERENT clients — City of Houston,
      // City of Houston - EWPP, City of Houston - Testing — and a name-only
      // key collapsed all of them into ONE row, so only one client's "D1"
      // survived and the others vanished from the picker (and were wrongly
      // hidden when filtering by the dropped client).  Keying by name+client
      // keeps one row per (SiteName × Client); same-name rows WITHIN one
      // client still collapse to one.
      const sitesByName = new Map<string, typeof rawLookups.sampleSites[number]>();
      for (const s of rawLookups.sampleSites ?? []) {
        const name = (s.name ?? '').trim().toLowerCase();
        if (!name) continue;
        const key = name + '|' + (s.clientId ?? '');
        const existing = sitesByName.get(key);
        if (!existing) { sitesByName.set(key, s); continue; }
        // Tie-break within the same (name, client): prefer a populated ProjectId.
        const existingScore = (existing.projectId ?? '').trim() ? 1 : 0;
        const candidateScore = (s.projectId ?? '').trim() ? 1 : 0;
        if (candidateScore > existingScore) sitesByName.set(key, s);
      }
      // 2026-06-25: suppress blank-client duplicate rows.  The picker's Client
      // column only resolves when the site's clientId is a customer in the
      // Client dropdown (lookups.clients = active + reportable customers).
      // Sites owned by a soft-deleted or non-reportable/duplicate customer
      // therefore show a BLANK client.  When a SiteName already has at least
      // one row whose client DOES resolve, drop its blank-client duplicates so
      // the picker stays clean; keep the unresolved rows only when a name has
      // NO resolvable owner at all (so the site is still selectable).
      const resolvableClientIds = new Set((rawLookups.clients ?? []).map(c => c.id));
      const dedupedSites = Array.from(sitesByName.values());
      const namesWithResolvableClient = new Set<string>();
      for (const s of dedupedSites) {
        if (s.clientId && resolvableClientIds.has(s.clientId))
          namesWithResolvableClient.add((s.name ?? '').trim().toLowerCase());
      }
      const visibleSites = dedupedSites.filter(s => {
        const nm = (s.name ?? '').trim().toLowerCase();
        const resolvable = !!s.clientId && resolvableClientIds.has(s.clientId);
        return resolvable || !namesWithResolvableClient.has(nm);
      });
      const dedupedLookups: Lookups = {
        ...rawLookups,
        sampleSites: visibleSites
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
      };
      setLookups(dedupedLookups);
      // Whitelist: only the two MOR Adhoc bench reports are exposed in the
      // dropdown (per user feedback 2026-05-26).  Matches the internal report
      // names in tbl_Public_CustomReportDesignerDetails — their friendly
      // displayName values are "Bench QCResults" and "Bench Results".
      // Picker now shows TWO templates only:
      //   • Bench Results     (QCBatchResult_PeerReview)   — sample-row report
      //   • Bench QC Report   (xrEnvTRRPQCComborptnew)     — 5-section QC report
      // The older "Bench QCResults" (QCBatchResult_Main) was dropped per
      // user feedback 2026-05-26 — the Combo report supersedes it.
      // LSI + CCPP are now TABLE-DRIVEN (tbl_Public_CustomReportDesignerDetails,
      // Active=1) instead of hardcoded in baseSet, so each deployment shows only
      // the index report it has turned on (owner decision 2026-10-06): COA shows
      // CCPP, GoLive shows LSI.  Their rows carry the sentinel names so the
      // existing LSI/CCPP excel handlers fire; they're whitelisted here and given
      // friendly display names.  (Supersedes the interim CcppReportEnabled flag.)
      const ALLOWED_TEMPLATES = new Set([
        'QCBatchResult_PeerReview',
        'xrEnvTRRPQCComborptnew',
        '__LSI_REPORT__',
        '__WATER_STABILITY__',
      ]);
      const DISPLAY_OVERRIDE: Record<string, string> = {
        'xrEnvTRRPQCComborptnew': 'Bench QC Report',
        '__LSI_REPORT__': 'LSI Report',
        '__WATER_STABILITY__': 'CCPP Report',
      };
      const all = (templatesRes.data as { data: ReportTemplate[] }).data ?? [];
      const labeled = all
        .filter(t => ALLOWED_TEMPLATES.has(t.name))
        .map(t => {
          const disp = DISPLAY_OVERRIDE[t.name];
          return disp ? { ...t, displayName: disp } : t;
        });
      setTemplates(labeled);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLookups(false);
    }
  }, [lookups]);

  // ── Preview handler — open the rendered Bench PDF in a new tab.
  // Sentinel template names — when the user picks these from the Report
  // dropdown the Preview button downloads an Excel file instead of opening
  // a PDF.  Per user feedback 2026-05-27: RC Monthly + LSI moved into the
  // dropdown so we only have one action button ("Preview") on the toolbar.
  const TPL_RC_MONTHLY = '__RC_MONTHLY__';
  const TPL_LSI_REPORT = '__LSI_REPORT__';
  // Water Stability Report — superset of LSI (adds Magnesium → Total Hardness +
  // CCPP per SM 2330C).  Wired in parallel with the LSI template everywhere.
  const TPL_WATER_STABILITY = '__WATER_STABILITY__';
  // (TPL_QC_COMBO constant removed 2026-05-30 — QC Combo no longer has a
  // required-field gate, so we don't need to branch on its template name.)

  // RC Monthly: when this template is selected, the Retrieve grid loads from
  // AI_MorAdhoc_RCSampleDetails_SP instead of the generic Retrieve SP.  Used by
  // loadServerPage below + the cache reset in clearAll.  (Restored after a
  // later drop accidentally removed it — drop #17 feature.)
  const rcMonthlySelected = selectedTemplate === TPL_RC_MONTHLY;

  // Mirrors the ALPACA GoLive "Preview" button.  Routes by template name.
  // Precondition rules (user feedback 2026-05-30):
  //   • RC Monthly + LSI  → Date Collected From / To is required (range scope)
  //   • Every other report → NO required field; user can run it with
  //                          whatever filters they have set
  async function handlePreviewReport(templateName: string, opts?: { download?: boolean; excel?: boolean }) {
    if (!templateName) {
      notify.warning('Pick a Report from the dropdown first.');
      return;
    }
    // Excel branches first — they have their own Date-Collected guards
    // inside handleExportExcel / handleLsiReport.
    if (templateName === TPL_RC_MONTHLY) {
      await handleExportExcel();
      return;
    }
    if (templateName === TPL_LSI_REPORT) {
      await handleLsiReport();
      return;
    }
    if (templateName === TPL_WATER_STABILITY) {
      await handleWaterStabilityReport();
      return;
    }
    // All other reports (Bench Results, QC Combo, …) have no required
    // field — the handler runs them with whatever the user picked.

    // Build the query string from the same filters Retrieve uses.
    const params = new URLSearchParams();
    params.set('template', templateName);
    if (dateCollectedFrom) params.set('dateFrom', dateCollectedFrom);
    if (dateCollectedTo)   params.set('dateTo',   dateCollectedTo);
    // Date Received range — the report now honours it (the SP always did; the
    // preview endpoint just wasn't forwarding it before 2026-05-30).
    if (dateReceivedFrom)  params.set('receivedFrom', dateReceivedFrom);
    if (dateReceivedTo)    params.set('receivedTo',   dateReceivedTo);
    const addStr = (k: string, set: Set<string>) => { if (set.size > 0) params.set(k, Array.from(set).join(',')); };
    const addNum = (k: string, set: Set<number>) => { if (set.size > 0) params.set(k, Array.from(set).join(',')); };
    addStr('jobIds',        filters.jobIds);
    addStr('batchIds',      filters.batchIds);
    addStr('clientIds',     filters.clientIds);
    addStr('projectIds',    filters.projectIds);
    addStr('testIds',       filters.testIds);
    addStr('methodNumbers', filters.methodNumbers);
    addStr('parameterIds',  filters.parameterIds);
    addStr('siteIds',       filters.siteIds);
    addNum('categoryIds',    filters.categoryIds);
    addNum('subCategoryIds', filters.subCategoryIds);
    // Pass the saved-report id when previewing a saved report — the
    // server uses it to look up Status and decide whether to render the
    // "UnApproved" watermark (Submitted → watermark; Approved/Delivered
    // → no watermark, matching ALPACA).
    if (savedId) params.set('savedId', savedId);
    // Forward the grid's column-filter / row-selection result so the
    // Preview shows exactly what the user is looking at.  Only sent when
    // the visible set is NARROWER than what the SP would return (i.e. the
    // user actually clicked a column filter / unchecked a row); when the
    // grid shows every retrieved row we omit the param so the SP keeps
    // streaming the full filtered set per query-panel filters.
    if (gridVisibleRowIds.length > 0 && gridVisibleRowIds.length < retrieveRows.length) {
      params.set('selectedRowIds', gridVisibleRowIds.join(','));
    }
    // Excel export — same endpoint, format=xlsx switches the renderer to the
    // ClosedXML writers.  The result is always downloaded (Excel can't open
    // inline in a browser tab like a PDF).
    const asExcel = opts?.excel === true;
    if (asExcel) params.set('format', 'xlsx');

    setPreviewLoading(true);
    previewAbortRef.current?.abort();
    const ac = new AbortController();
    previewAbortRef.current = ac;
    try {
      // Fetch the report as a blob so we can open/save it without
      // auth-stripping (the JWT is on the api client; a window.open would
      // skip it).
      const res = await api.get(`/api/v1/mor-adhoc/report-preview?${params.toString()}`, {
        responseType: 'blob',
        signal: ac.signal,
      });
      const mime = asExcel
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const blob = new Blob([res.data as BlobPart], { type: mime });
      const url  = URL.createObjectURL(blob);
      if (asExcel || opts?.download) {
        // Save the generated file (Excel always downloads; PDF Download button).
        // Adhoc reports have no pre-stored binary, so we generate on demand.
        const ext = asExcel ? 'xlsx' : 'pdf';
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(savedReportId || templateName).replace(/[^\w.-]+/g, '_')}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank');
      }
      // Revoke the object URL once the tab/save has had time to use it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      if (ac.signal.aborted) return;   // Stop clicked — cancel silently
      console.error(e);
      // When the request was responseType:'blob' the server's JSON error
      // body comes back as a Blob — read it and pull the message.  Without
      // this, users see "Preview failed: check the API logs." for what is
      // actually a clean validation error like "Job ID or QC Batch ID is
      // required for the QC report."  (User feedback 2026-05-30 —
      // "qcreport show error".)
      let msg: string | undefined;
      const errResp = (e as { response?: { data?: unknown } })?.response;
      const body    = errResp?.data;
      if (body instanceof Blob) {
        try {
          const text = await body.text();
          const parsed = JSON.parse(text) as { error?: { message?: string } };
          msg = parsed?.error?.message;
        } catch { /* fall through to default message */ }
      } else if (body && typeof body === 'object') {
        msg = (body as { error?: { message?: string } })?.error?.message;
      }
      notify.error((opts?.excel ? 'Excel export' : opts?.download ? 'Download' : 'Preview') + ' failed: ' + (msg ?? 'check the API logs.'));
    } finally {
      setPreviewLoading(false);
    }
  }

  // (`hasJobOrBatch` helper removed 2026-05-30 — the Preview button no
  // longer disables when Job/Batch is missing.  The handler itself
  // still alerts the user if the report it's about to render needs
  // a scope.)

  // ── Save handler — persists the report header + filter / selection state.
  // First click inserts (auto-assigned MOR-YYYY-NNNN); subsequent clicks
  // update the same record so the user can keep refining.
  async function handleSave() {
    if (!retrieved) {
      notify.warning('Hit Retrieve before saving — Save records the current result set.');
      return;
    }
    // Auto-derive the Report Name from the currently selected entry in
    // the Report dropdown — no prompt.  User feedback 2026-05-27:
    // "don't ask me you can save the report name. what we selected in the combo".
    //   1. Existing saved report → reuse stored name (update path).
    //   2. Otherwise → use the DisplayName of the selected Report template.
    //   3. Both sentinel pseudo-templates (RC Monthly / LSI) get friendly
    //      names so the saved row still reads cleanly in the queues.
    //   4. Fallback when nothing is picked → block with a helpful message
    //      (Save shouldn't be available without Retrieve, but defend anyway).
    let name = reportName;
    if (!name || !savedId) {
      let derived = '';
      if (selectedTemplate === TPL_RC_MONTHLY)      derived = 'RC Monthly Report';
      else if (selectedTemplate === TPL_LSI_REPORT) derived = 'LSI Report';
      else if (selectedTemplate === TPL_WATER_STABILITY) derived = 'CCPP Report';
      else if (selectedTemplate) {
        const t = templates.find(t => t.name === selectedTemplate);
        derived = (t?.displayName || t?.name || selectedTemplate).trim();
      }
      if (!derived) {
        notify.warning('Pick a report from the Report dropdown first — Save uses that as the report name.');
        return;
      }
      name = derived;
      setReportName(name);
    }

    setSaving(true);
    try {
      // Shared ALPACA-style save flow: "Do you want to Save?" confirmation →
      // POST the report header + filter / selection snapshot → success
      // acknowledgement → return to the saved-reports list.  The message echoes
      // the minted ReportID — "Report {id} saved successfully." (legacy
      // ReportingViewController.cs line 3565) — under the §18.6 Custom /
      // Ad Hoc Reporting exception, shown as a top toast so it stays visible
      // after we navigate back.  (Replaces the previous window.confirm("Do you
      // want to save this MOR Adhoc Report?") — user feedback 2026-05-27 still
      // honoured by the flow's confirm.)
      let successMsg = 'Report saved successfully.';
      await runSave({
        save: async () => {
          // Serialize the current query-panel state.  The picker filters are
          // stored as arrays of ids so the saved snapshot can be reloaded later.
          const filtersSnapshot = {
            clientIds:      Array.from(filters.clientIds),
            projectIds:     Array.from(filters.projectIds),
            testIds:        Array.from(filters.testIds),
            methodNumbers:  Array.from(filters.methodNumbers),
            parameterIds:   Array.from(filters.parameterIds),
            jobIds:         Array.from(filters.jobIds),
            batchIds:       Array.from(filters.batchIds),
            siteIds:        Array.from(filters.siteIds),
            categoryIds:    Array.from(filters.categoryIds),
            subCategoryIds: Array.from(filters.subCategoryIds),
          };
          const selectedRowIds = retrieveRows.map(r => r.sampleParameterId);

          // Date-window reports (RC Monthly / LSI / CCPP) can be generated from
          // just the Date Range chip — effectiveCollectedRange() derives a window
          // from it.  The chip itself is NOT persisted, so without this fallback a
          // report saved with only the chip stored NO date range and could not be
          // regenerated from the list ("no saved date range"; e.g. a CCPP report
          // saved under a 3M chip).  When no explicit From/To is set, persist the
          // chip-derived Collected window so the saved report regenerates.
          const isDateWindowReport = selectedTemplate === TPL_RC_MONTHLY
            || selectedTemplate === TPL_LSI_REPORT
            || selectedTemplate === TPL_WATER_STABILITY;
          const noExplicitDates = !(dateCollectedFrom || dateCollectedTo
            || dateReceivedFrom || dateReceivedTo || dateAnalyzedFrom || dateAnalyzedTo);
          const chipFallback = (isDateWindowReport && noExplicitDates)
            ? effectiveCollectedRange() : null;

          const body = {
            id:                  savedId,
            reportName:          name,
            dateCollectedFrom:   dateCollectedFrom || chipFallback?.from || null,
            dateCollectedTo:     dateCollectedTo   || chipFallback?.to   || null,
            dateReceivedFrom:    dateReceivedFrom  || null,
            dateReceivedTo:      dateReceivedTo    || null,
            // Date Analyzed range — added 2026-05-28.  Was previously dropped
            // on Save, so saved reports came back with empty Analyzed inputs.
            dateAnalyzedFrom:    dateAnalyzedFrom  || null,
            dateAnalyzedTo:      dateAnalyzedTo    || null,
            filtersJson:         JSON.stringify(filtersSnapshot),
            selectedRowIdsJson:  JSON.stringify(selectedRowIds),
            reportTemplateName:  selectedTemplate || null,
          };
          const res = await api.post('/api/v1/mor-adhoc/save', body);
          const data = (res.data as { data: { id: string; reportId: string } }).data;
          setSavedId(data.id);
          setSavedReportId(data.reportId);
          successMsg = data.reportId
            ? `Report ${data.reportId} saved successfully.`
            : 'Report saved successfully.';
        },
        get successMessage() { return successMsg; },
        successAs: 'toast',
        onSaved: () => {
          // ALPACA SaveReport → View.Close(): return to the saved-reports list
          // (backToList hands control to the embedded host's list when embedded).
          backToList();
        },
        onError: (e) => {
          const msg = (e as { response?: { data?: { error?: { message?: string } } } })
            ?.response?.data?.error?.message;
          notify.error('Save failed: ' + (msg ?? 'unknown error — check the API logs.'));
        },
      });
    } finally {
      setSaving(false);
    }
  }

  // Pull jobs+batches whenever the relevant date floor changes.
  // Pre-fires on initial mount (background load — user feedback 2026-05-30:
  // "load in background first") so by the time the user opens the detail
  // panel the Job ID and QC Batch ID pickers are already populated.
  // Floor preference: receivedFrom → collectedFrom → 1 year back.
  useEffect(() => {
    const since = dateReceivedFrom || dateCollectedFrom
      || fmtDate(new Date(new Date().getFullYear() - 1, new Date().getMonth(), 1));
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/v1/mor-adhoc/jobs-batches', { params: { sinceDate: since }, silentLoading: true } as never);
        if (!cancelled) setJobsBatches((res.data as { data: JobsBatches }).data);
      } catch {
        if (!cancelled) setJobsBatches({ jobs: [], batches: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [mode, dateReceivedFrom, dateCollectedFrom]);

  useEffect(() => { void loadList(); }, [loadList]);

  // ── Filter helpers ───────────────────────────────────────────────────────
  function toggleId(set: Set<string>, id: string): Set<string> {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }
  // Used by the Regulatory Compliance pickers (currently hidden but kept
  // wired so the API still receives the filter when we re-enable them).
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function toggleNumId(set: Set<number>, id: number): Set<number> {
    const n = new Set(set);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }
  void toggleNumId; // keep referenced so TS strict-noUnusedLocals doesn't fail
  function clearFilter<K extends keyof QueryFilters>(field: K) {
    setFilters(f => {
      const v = f[field];
      if (v instanceof Set) return { ...f, [field]: new Set() };
      return f;
    });
  }
  function clearAll() {
    setFilters(EMPTY_FILTERS);
    setDateReceivedFrom('');
    setDateReceivedTo('');
    setDateAnalyzedFrom('');
    setDateAnalyzedTo('');
    setRetrieved(false);
    setRetrieveRows([]);
    setRetrieveMs(null);
    setRetrieveErr(null);
    // Wipe the cached RC sample-detail rows (drop #17 — restored 2026-06-01).
    // Without this the next Retrieve under a different filter set would reuse
    // stale rows from the previous run and the field/QC counts on the RC
    // Monthly Excel would be wrong.
    rcSampleAllRef.current = [];
    // Clearing also re-opens the query panel so the user can edit filters
    // before the next Retrieve.
    setPanelCollapsed(false);
    setPanelAsPopup(false);
  }

  // ── Load the relation index whenever the Date Range / Date Collected
  // changes.  Order of precedence for the lookup window:
  //   1. Explicit Date Collected From + To  (user typed both)
  //   2. Explicit Date Received From + To   (chip-driven or hand-entered)
  //   3. The Date Range chip cutoff         (always present, default 3M)
  //
  // User feedback 2026-05-31: "3M means show data filter [where] start date
  // has data, [the rest disabled, only shows the available data not others]".
  // i.e. the picker dropdowns must narrow to the rows that ACTUALLY exist in
  // the current Date Range window.  Re-loading relations on dateRange change
  // makes the cross-faceted facets do exactly that.
  useEffect(() => {
    if (mode !== 'detail') return;
    let cancelled = false;
    (async () => {
      setRelationsLoading(true);
      try {
        let from: string | undefined;
        let to:   string | undefined;
        if (dateCollectedFrom && dateCollectedTo) {
          from = dateCollectedFrom; to = dateCollectedTo;
        } else if (dateReceivedFrom && dateReceivedTo) {
          from = dateReceivedFrom;  to = dateReceivedTo;
        } else {
          // Date Range chip drives the lookup window.
          const co = rangeCutoff(dateRange);
          from = co ? fmtDate(co) : fmtDate(new Date(new Date().getFullYear() - 5, 0, 1));
          to   = fmtDate(new Date());
        }
        const res = await api.get('/api/v1/mor-adhoc/relations', {
          params: { dateFrom: from, dateTo: to },
        });
        if (!cancelled) setRelations((res.data as { data: RelationRow[] }).data ?? []);
      } catch {
        if (!cancelled) setRelations([]);
      } finally {
        if (!cancelled) setRelationsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, dateCollectedFrom, dateCollectedTo, dateReceivedFrom, dateReceivedTo, dateRange, rangeCutoff]);

  // ── Cross-faceted "available IDs" for every dropdown ─────────────────────
  // For each pivot field we filter relations by EVERY OTHER current selection,
  // then collect the distinct values for that field. That set becomes the
  // narrowing filter for the picker (PickerField hides items not in the set).
  //
  // While the relation index is loading OR returned zero rows, we pass
  // `undefined` for every facet so the pickers fall back to "show all" —
  // otherwise an empty-set narrow would (correctly but uselessly) hide every
  // option in every dropdown.
  const NO_FACETS = useMemo(() => ({
    clients:       undefined as Set<string> | undefined,
    projects:      undefined as Set<string> | undefined,
    tests:         undefined as Set<string> | undefined,
    methods:       undefined as Set<string> | undefined,
    parameters:    undefined as Set<string> | undefined,
    jobs:          undefined as Set<string> | undefined,
    batches:       undefined as Set<string> | undefined,
    siteNames:     undefined as Set<string> | undefined,  // normalized site NAMES (picker is name-deduped)
    categories:    undefined as Set<number> | undefined,
    subCategories: undefined as Set<number> | undefined,
  }), []);

  const available = useMemo(() => {
    if (relations.length === 0) return NO_FACETS;

    const ci = filters.clientIds, pi = filters.projectIds, ti = filters.testIds;
    const mn = filters.methodNumbers, pa = filters.parameterIds, ji = filters.jobIds;
    const bi = filters.batchIds, si = filters.siteIds;
    const ca = filters.categoryIds, sc = filters.subCategoryIds;

    // Sites are matched BY NAME, not Oid: the same SiteName has several Oids in
    // this DB and the picker is de-duped by name, so the sample's actual
    // StationLocation Oid often isn't the Oid the picker kept.  Translate the
    // selected site Oids → normalized names to compare against r.siteName.
    // (Pavithra drop, 2026-06-01.)
    const norm = (s: string) => s.trim().toLowerCase();
    const selSiteNames = new Set<string>();
    if (si.size && lookups) {
      for (const s of lookups.sampleSites) {
        if (si.has(s.id) && s.name) selSiteNames.add(norm(s.name));
      }
    }

    // Per-pivot test functions that ignore the pivot itself
    const matchExcept = (r: RelationRow, except: string) => {
      if (except !== 'client'  && ci.size && (!r.clientId  || !ci.has(r.clientId)))   return false;
      if (except !== 'project' && pi.size && (!r.projectId || !pi.has(r.projectId)))  return false;
      if (except !== 'test'    && ti.size && (!r.testId    || !ti.has(r.testId)))     return false;
      if (except !== 'method'  && mn.size && (!r.methodNumber || !mn.has(r.methodNumber))) return false;
      if (except !== 'param'   && pa.size && (!r.parameterId || !pa.has(r.parameterId))) return false;
      if (except !== 'job'     && ji.size && (!r.jobId       || !ji.has(r.jobId)))    return false;
      if (except !== 'batch'   && bi.size && (!r.batchId     || !bi.has(r.batchId)))  return false;
      if (except !== 'site'    && selSiteNames.size && (!r.siteName || !selSiteNames.has(norm(r.siteName)))) return false;
      if (except !== 'cat'     && ca.size && (r.categoryId == null || !ca.has(r.categoryId))) return false;
      if (except !== 'sub'     && sc.size && (r.subCategoryId == null || !sc.has(r.subCategoryId))) return false;
      return true;
    };

    const collect = <V,>(except: string, pick: (r: RelationRow) => V | null | undefined): Set<V> => {
      const out = new Set<V>();
      for (const r of relations) {
        if (!matchExcept(r, except)) continue;
        const v = pick(r);
        if (v != null) out.add(v);
      }
      return out;
    };

    return {
      clients:       collect<string>('client',  r => r.clientId)     as Set<string> | undefined,
      projects:      collect<string>('project', r => r.projectId)    as Set<string> | undefined,
      tests:         collect<string>('test',    r => r.testId)       as Set<string> | undefined,
      methods:       collect<string>('method',  r => r.methodNumber) as Set<string> | undefined,
      parameters:    collect<string>('param',   r => r.parameterId)  as Set<string> | undefined,
      jobs:          collect<string>('job',     r => r.jobId)        as Set<string> | undefined,
      batches:       collect<string>('batch',   r => r.batchId)      as Set<string> | undefined,
      siteNames:     collect<string>('site',    r => (r.siteName ? norm(r.siteName) : null)) as Set<string> | undefined,
      categories:    collect<number>('cat',     r => r.categoryId)   as Set<number> | undefined,
      subCategories: collect<number>('sub',     r => r.subCategoryId) as Set<number> | undefined,
    };
  }, [relations, filters, lookups, NO_FACETS]);

  // ── LSI-report narrowing ─────────────────────────────────────────────────
  // When the user picks the LSI Report template, restrict the Parameter,
  // Test, and Method pickers to the rows the LSI SP actually queries.  The
  // seven LSI-formula inputs are:
  //     Temperature · Conductivity · TDS · Calcium (Ca)
  //     · Calcium Hardness as CaCO3 · Alkalinity · pH
  //
  // Sites are NOT scoped here any more — feedback FB5 (5/30/2026) called for
  // any site that has the required inputs, so the LSI SP itself returns the
  // full site list and the picker stays unrestricted.
  //
  // User feedback 2026-05-31 ("while retrieve for LSI report only show the
  // data need for LSI report no need others"): the LSI param list is also
  // pushed into the Retrieve filter set so the Sample tab itself contains
  // only LSI-relevant rows when LSI is the active template.
  const LSI_PARAM_NAMES = useMemo(() => new Set<string>([
    'Temperature',
    'Conductivity',
    'TDS',
    'Calcium (Ca)',
    'Calcium Hardness as CaCO3',
    'Alkalinity',
    'pH',
  ]), []);

  // CCPP (Water Stability) Report inputs — the raw parameters the report derives
  // everything from (TDS / hardness / CCPP are computed, not read).  Used to scope
  // the Retrieve grid exactly like LSI_PARAM_NAMES scopes the LSI grid.  The set is
  // the SUPERSET of both lab naming conventions (matching the SP's Parameter IN
  // list, rev 2): the Abilene v3 names AND the COA-lab names ("T Alkalinity",
  // "Calcium Hardness", measured "TDS").  A deployment only has its own names, so
  // the grid shows exactly that lab's inputs — no behaviour change for Abilene.
  const WS_PARAM_NAMES = useMemo(() => new Set<string>([
    'pH',
    'Temperature',
    'Conductivity',
    // alkalinity: Abilene "Alkalinity" | COA "T Alkalinity" (Total)
    'Alkalinity',
    'T Alkalinity',
    // calcium: Abilene elemental "Calcium (Ca)" / COA "Ca-Calcium" (code 0074),
    // or the measured hardness COA actually enters: "Hardness as CaCO3" (0027)
    'Calcium (Ca)',
    'Ca-Calcium',
    'Calcium Hardness',
    'Calcium Hardness as CaCO3',
    'Hardness as CaCO3',
    // magnesium: Abilene "Magnesium (Mg)" / COA "Mg-Magnesium" (code 0076)
    'Magnesium (Mg)',
    'Mg-Magnesium',
    'Magnesium',
    // measured TDS (COA labs; Abilene derives it)
    'TDS',
  ]), []);

  const lsiActive = selectedTemplate === TPL_LSI_REPORT;
  const wsActive  = selectedTemplate === TPL_WATER_STABILITY;
  // Both LSI and Water Stability are single-index, sample-only reports that
  // scope the grid to a fixed parameter set — several grid behaviours are shared.
  const indexReportActive = lsiActive || wsActive;

  // The parameter-name set for whichever index report is active (null if none).
  const indexParamNames = lsiActive ? LSI_PARAM_NAMES : wsActive ? WS_PARAM_NAMES : null;

  // Parameter / test / method sets for whichever index report (LSI or Water
  // Stability) is active — used to scope BOTH the Retrieve grid (buildRcParams)
  // and the picker dropdowns (availableNarrowed) to that report's fixed inputs.
  // Generalises the former LSI-only `lsiAllowed` so the two reports behave the
  // same (LSI = seven inputs, Water Stability = six).
  const indexAllowed = useMemo(() => {
    if (!indexParamNames || !lookups) return null;
    const paramIds = new Set(lookups.parameters.filter(p => indexParamNames.has(p.name)).map(p => p.id));
    // Tests/methods whose name matches one of the report's input parameters
    // (Temperature, pH, Magnesium etc. are often standalone tests).
    const testIds = new Set<string>();
    const methods = new Set<string>();
    for (const t of lookups.tests) {
      if (indexParamNames.has(t.testName)) {
        testIds.add(t.id);
        if (t.methodNumber) methods.add(t.methodNumber);
      }
    }
    return { paramIds, testIds, methods };
  }, [indexParamNames, lookups]);

  /** Helper: AND two optional Set narrows.  When either is undefined,
   *  returns the other.  Used to layer the index-report override on top of the
   *  cross-faceted set. */
  function intersect<T>(a: Set<T> | undefined, b: Set<T> | undefined): Set<T> | undefined {
    if (!a) return b;
    if (!b) return a;
    const out = new Set<T>();
    for (const x of a) if (b.has(x)) out.add(x);
    return out;
  }

  const availableNarrowed = useMemo(() => {
    if (!indexAllowed) return available;
    return {
      ...available,
      parameters: intersect(available.parameters, indexAllowed.paramIds),
      tests:      intersect(available.tests,      indexAllowed.testIds),
      methods:    intersect(available.methods,    indexAllowed.methods),
    };
  }, [available, indexAllowed]);

  // ── Distinct test names (one row per unique TestName) ────────────────────
  const distinctTestNames = useMemo(() => {
    if (!lookups) return [];
    const seen = new Map<string, TestLookup>();
    for (const t of lookups.tests) if (!seen.has(t.testName)) seen.set(t.testName, t);
    return Array.from(seen.values()).sort((a, b) => a.testName.localeCompare(b.testName));
  }, [lookups]);

  // Distinct method numbers (string set the Method picker iterates over)
  const allMethodNumbers = useMemo(() => {
    if (!lookups) return [] as string[];
    return Array.from(new Set(lookups.tests.map(t => t.methodNumber).filter(Boolean))).sort();
  }, [lookups]);
  void allMethodNumbers;  // superseded by methodPickerRows below

  /** Map of customerId → client name for the Project ID / Project Name
   *  pickers (PDF feedback #8, #9, #10 — Project picker has a "Client" column). */
  const clientNameById = useMemo(() => {
    if (!lookups) return new Map<string, string>();
    return new Map(lookups.clients.map(c => [c.id, c.name]));
  }, [lookups]);

  // ── Project Name groups (dedupe) ─────────────────────────────────────────
  // User feedback 2026-05-29: "project name have lot of duplicate records".
  // Many distinct project GUIDs share ONE display name, so the Project Name
  // picker repeated the same name dozens of times.  Collapse to ONE row per
  // distinct (case-insensitive) ProjectName.  Selecting a group toggles every
  // underlying project GUID into filters.projectIds, so the cross-faceted
  // narrowing + the Retrieve scope stay correct.  (The Project ID picker is
  // grouped the same way — by ProjectID code+client — via projectIdGroups
  // below; IDs are NOT genuinely distinct, several projects share one code.)
  type ProjectNameGroup = {
    key: string;            // normalized name — keyFn for the picker
    name: string;           // display name (first-seen original casing)
    idLabel: string;        // distinct ProjectID codes, comma-joined
    projectGuids: string[]; // every project GUID sharing this name
  };
  const projectNameGroups = useMemo<ProjectNameGroup[]>(() => {
    if (!lookups) return [];
    const map = new Map<string, { name: string; ids: Set<string>; guids: string[] }>();
    for (const p of lookups.projects) {
      const display = p.projectName || p.projectId || '(no name)';
      const key = display.trim().toLowerCase();
      let g = map.get(key);
      if (!g) { g = { name: display, ids: new Set<string>(), guids: [] }; map.set(key, g); }
      g.guids.push(p.id);
      if (p.projectId) g.ids.add(p.projectId);
    }
    return Array.from(map.entries())
      .map(([key, g]) => ({
        key,
        name: g.name,
        idLabel: Array.from(g.ids).sort().join(', '),
        projectGuids: g.guids,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [lookups]);

  /** The project GUIDs that are "in context" for the Project Name combo —
   *  every project sharing a (ProjectID code × Client) pair that is currently
   *  selected.  Used to scope the Project Name checkbox + toggle so a name that
   *  spans several codes (e.g. "Friday" exists under both EWPP Plant Daily and
   *  NEWPP Plant 2 Daily) is judged ONLY by the projects under the selected
   *  code(s) — not by its other-code siblings the user never picked.  Undefined
   *  when nothing is selected (then the full name group is used, as before).
   *  (User feedback 2026-06-25: after selecting a Project ID, single-code names
   *  showed checked but multi-code names showed unchecked.) */
  const contextProjectGuids = useMemo<Set<string> | undefined>(() => {
    if (!lookups || filters.projectIds.size === 0) return undefined;
    const pairOf = (p: { projectId: string | null; customerId: string | null }) =>
      (p.projectId || '(no id)').trim().toLowerCase() + '|' + (p.customerId ?? '');
    const selectedPairs = new Set<string>();
    for (const p of lookups.projects)
      if (filters.projectIds.has(p.id)) selectedPairs.add(pairOf(p));
    if (selectedPairs.size === 0) return undefined;
    const guids = new Set<string>();
    for (const p of lookups.projects)
      if (selectedPairs.has(pairOf(p))) guids.add(p.id);
    return guids;
  }, [lookups, filters.projectIds]);

  /** Name-group keys whose in-context project GUIDs are ALL selected.  When a
   *  Project ID code is in play we judge only the GUIDs under the selected
   *  code+client (contextProjectGuids); otherwise the whole name group. */
  const selectedProjectNameKeys = useMemo(() => {
    const set = new Set<string>();
    for (const g of projectNameGroups) {
      const relevant = contextProjectGuids
        ? g.projectGuids.filter(id => contextProjectGuids.has(id))
        : g.projectGuids;
      if (relevant.length > 0 && relevant.every(id => filters.projectIds.has(id)))
        set.add(g.key);
    }
    return set;
  }, [projectNameGroups, filters.projectIds, contextProjectGuids]);

  /** Narrow the project pickers ONLY when another facet is actually selected.
   *
   *  The cross-faceted `available.projects` set is built from the relation
   *  index, which only contains projects that have sample data inside the
   *  current date window.  Using it as the baseline (even with no other
   *  filter chosen) silently HID every project without recent data — so the
   *  Project Name list looked incomplete.  User feedback 2026-05-30:
   *  "some project name missing … don't miss any project name".
   *
   *  Fix: when no OTHER facet (Client / Test / Method / Param / Job / Batch /
   *  Site / Category / Sub-category) is selected, return `undefined` so the
   *  pickers show EVERY distinct project.  Once the user picks another facet,
   *  resume genuine cross-faceted narrowing. */
  const projectNarrow = useMemo<Set<string> | undefined>(() => {
    const f = filters;
    const otherFacetActive =
      f.clientIds.size      > 0 || f.testIds.size        > 0 ||
      f.methodNumbers.size  > 0 || f.parameterIds.size   > 0 ||
      f.jobIds.size         > 0 || f.batchIds.size        > 0 ||
      f.siteIds.size        > 0 || f.categoryIds.size     > 0 ||
      f.subCategoryIds.size > 0;
    return otherFacetActive ? availableNarrowed.projects : undefined;
  }, [filters, availableNarrowed.projects]);

  /** Names reachable from the currently-selected Project ID code(s).
   *  User request 2026-06-25: "if the user selects Project ID first, the
   *  Project Name combo is filled based on the selected filter."  When a code
   *  is picked in the Project ID combo, narrow the Project Name combo to the
   *  names that carry one of the selected codes (so e.g. picking "EWPP Plant
   *  Daily" hides the EWPP Testing / NEWPP-only names).  Driven by the codes
   *  PRESENT in filters.projectIds — not whole-group selection — so it survives
   *  the user unchecking individual weekday names. */
  const projectCodeNameNarrow = useMemo<Set<string> | undefined>(() => {
    if (!lookups || filters.projectIds.size === 0) return undefined;
    const guidCode = new Map<string, string>();
    const selectedCodes = new Set<string>();
    for (const p of lookups.projects) {
      const code = (p.projectId || '(no id)').trim().toLowerCase();
      guidCode.set(p.id, code);
      if (filters.projectIds.has(p.id)) selectedCodes.add(code);
    }
    if (selectedCodes.size === 0) return undefined;
    const names = new Set<string>();
    for (const g of projectNameGroups)
      if (g.projectGuids.some(id => selectedCodes.has(guidCode.get(id) ?? '')))
        names.add(g.key);
    return names;
  }, [lookups, filters.projectIds, projectNameGroups]);

  /** Cross-faceted narrow for the Project Name combo — combines the other-facet
   *  narrow (Client / Test / Job / …) with the Project ID code narrow above.
   *  `undefined` => show every name. */
  const availableProjectNameKeys = useMemo(() => {
    let keys: Set<string> | undefined;
    if (projectNarrow) {
      keys = new Set<string>();
      for (const g of projectNameGroups)
        if (g.projectGuids.some(id => projectNarrow.has(id))) keys.add(g.key);
    }
    if (projectCodeNameNarrow) {
      keys = keys
        ? new Set([...keys].filter(k => projectCodeNameNarrow.has(k)))
        : new Set(projectCodeNameNarrow);
    }
    return keys;
  }, [projectNameGroups, projectNarrow, projectCodeNameNarrow]);

  /** Cross-faceted narrow for the Site picker — based on the picker's own
   *  clientId / projectId fields, not on the relation-index `available.sites`
   *  set (which is unreliable because SampleLogIn.SiteSource is NULL for most
   *  rows in this DB).  Behaviour:
   *    • No Client and no Project selected → undefined (show every site)
   *    • Client picked → show only sites whose clientId is in the selection
   *    • Project picked → show only sites whose projectId code matches
   *    • Both picked → intersection
   *  User feedback 2026-06-01: "site name not filtered properly based on
   *  previous selection."  */
  const siteNarrow = useMemo<Set<string> | undefined>(() => {
    const noClient   = filters.clientIds.size  === 0;
    const noProject  = filters.projectIds.size === 0;
    if (noClient && noProject) return undefined;
    if (!lookups) return undefined;

    // Map selected project GUIDs → their projectId code strings so we can
    // compare against site.projectId (which is the code, not the GUID).
    const selectedProjectIdCodes = new Set<string>();
    if (!noProject) {
      for (const p of lookups.projects) {
        if (filters.projectIds.has(p.id) && p.projectId) {
          selectedProjectIdCodes.add(p.projectId.trim());
        }
      }
    }
    const allow = new Set<string>();
    for (const s of lookups.sampleSites) {
      // Client narrow is strict — sites without a clientId are excluded when
      // a Client is selected (legacy ALPACA's site→client link is populated).
      const clientOk  = noClient  || (s.clientId ? filters.clientIds.has(s.clientId) : false);
      // Project narrow is LENIENT — SampleSites.ProjectID is NULL on most
      // rows in this DB, so sites without a project mapping are passed
      // through unfiltered.  Only sites that DO have a projectId mapping
      // (and the mapping does NOT match) get excluded.  This avoids the
      // "selecting a Project hides every site" problem the user reported.
      const sp = (s.projectId ?? '').trim();
      const projectOk = noProject || (sp === '' ? true : selectedProjectIdCodes.has(sp));
      if (clientOk && projectOk) allow.add(s.id);
    }
    return allow;
  }, [filters.clientIds, filters.projectIds, lookups]);

  /** Final narrow for the Site picker — Pavithra drop, 2026-06-01.
   *
   *  When a Job ID and/or QC Batch is selected we narrow the Site picker to the
   *  exact set of sites that occur for that job, taken from the relation index's
   *  site NAMES (`available.siteNames`).  We match by name because the picker
   *  is de-duped by name (the same name has several Oids) so Oid matching is
   *  unreliable.  User request: "retrieve a particular JobID → only that job's
   *  site names load in the combobox."
   *
   *  With no Job/Batch selected, we fall back to the Client/Project narrow
   *  (`siteNarrow`) so the picker still respects those facets. */
  const siteAvailableIds = useMemo<Set<string> | undefined>(() => {
    const jobOrBatchSelected = filters.jobIds.size > 0 || filters.batchIds.size > 0;
    if (jobOrBatchSelected && available.siteNames && available.siteNames.size > 0 && lookups) {
      const allowed = available.siteNames;  // already normalized (trim + lowercase)
      const ids = new Set<string>();
      for (const s of lookups.sampleSites) {
        if (s.name && allowed.has(s.name.trim().toLowerCase())) ids.add(s.id);
      }
      if (ids.size > 0) {
        if (!siteNarrow) return ids;
        const out = new Set<string>();
        for (const id of ids) if (siteNarrow.has(id)) out.add(id);
        return out.size > 0 ? out : ids;
      }
    }
    return siteNarrow;
  }, [filters.jobIds, filters.batchIds, available.siteNames, lookups, siteNarrow]);

  /** Toggle the project GUIDs of a Project Name group.  When a Project ID code
   *  is in play, operate ONLY on the in-context GUIDs (the name's projects under
   *  the selected code+client), so unchecking "Friday" doesn't also pull in the
   *  same name under other codes/clients the user never picked. */
  const toggleProjectNameGroup = useCallback((key: string) => {
    const g = projectNameGroups.find(x => x.key === key);
    if (!g) return;
    const targets = contextProjectGuids
      ? g.projectGuids.filter(id => contextProjectGuids.has(id))
      : g.projectGuids;
    const scope = targets.length > 0 ? targets : g.projectGuids;
    setFilters(f => {
      const next = new Set(f.projectIds);
      const allSelected = scope.every(id => next.has(id));
      if (allSelected) scope.forEach(id => next.delete(id));
      else             scope.forEach(id => next.add(id));
      return { ...f, projectIds: next };
    });
  }, [projectNameGroups, contextProjectGuids]);

  // ── Project ID groups (dedupe) ───────────────────────────────────────────
  // User feedback 2026-06-25: the Project ID picker showed the SAME ProjectID
  // code many times (e.g. "SEWPP Plant Daily" appeared 6×) because several
  // distinct project records share ONE ProjectID code — they differ only by
  // ProjectName (Friday / Monday / …).  Collapse to ONE row per distinct
  // (ProjectID code × Client).  Selecting a code toggles EVERY project GUID
  // that carries it into filters.projectIds, so the GUID-based Retrieve / LSI
  // scope still narrows correctly and stays in sync with the Project Name
  // picker (which groups the same GUIDs by name).
  type ProjectIdGroup = {
    key: string;            // normalized "<code> <customerId>" — keyFn for the picker
    code: string;           // display ProjectID code (first-seen casing)
    clientLabel: string;    // owning client name
    projectGuids: string[]; // every project GUID sharing this code+client
  };
  const projectIdGroups = useMemo<ProjectIdGroup[]>(() => {
    if (!lookups) return [];
    const map = new Map<string, { code: string; client: string; guids: string[] }>();
    for (const p of lookups.projects) {
      const code = p.projectId || '(no id)';
      const cid  = p.customerId ?? '';
      const key  = code.trim().toLowerCase() + ' ' + cid;
      let g = map.get(key);
      if (!g) { g = { code, client: clientNameById.get(cid) ?? '', guids: [] }; map.set(key, g); }
      g.guids.push(p.id);
    }
    return Array.from(map.entries())
      .map(([key, g]) => ({ key, code: g.code, clientLabel: g.client, projectGuids: g.guids }))
      .sort((a, b) => a.code.localeCompare(b.code) || a.clientLabel.localeCompare(b.clientLabel));
  }, [lookups, clientNameById]);

  /** Project ID group keys whose every underlying project GUID is selected. */
  const selectedProjectIdKeys = useMemo(() => {
    const set = new Set<string>();
    for (const g of projectIdGroups)
      if (g.projectGuids.length > 0 && g.projectGuids.every(id => filters.projectIds.has(id)))
        set.add(g.key);
    return set;
  }, [projectIdGroups, filters.projectIds]);

  /** Cross-faceted narrow translated from project GUIDs → Project ID group keys. */
  const availableProjectIdKeys = useMemo(() => {
    if (!projectNarrow) return undefined;
    const set = new Set<string>();
    for (const g of projectIdGroups)
      if (g.projectGuids.some(id => projectNarrow.has(id))) set.add(g.key);
    return set;
  }, [projectIdGroups, projectNarrow]);

  /** Toggle every project GUID belonging to a Project ID (code) group. */
  const toggleProjectIdGroup = useCallback((key: string) => {
    const g = projectIdGroups.find(x => x.key === key);
    if (!g) return;
    setFilters(f => {
      const next = new Set(f.projectIds);
      const allSelected = g.projectGuids.every(id => next.has(id));
      if (allSelected) g.projectGuids.forEach(id => next.delete(id));
      else             g.projectGuids.forEach(id => next.add(id));
      return { ...f, projectIds: next };
    });
  }, [projectIdGroups]);

  // ── Auto-fill on single-option narrow — REMOVED 2026-05-27 ───────────────
  // User feedback: "when i select qcbatch test and other details no need to
  // select automatically just filter fine.  because it is not clear properly".
  // The cross-faceted `available` map STILL narrows the picker options (so
  // when you select a QC Batch, the Test/Method/Parameter pickers only show
  // the relevant rows), but we no longer tick those filters' checkboxes
  // automatically.  The user clicks them explicitly if they want them in
  // the Retrieve scope.

  /** Method picker rows — `Method Number` + list of Tests + list of Matrices
   *  that use that method (PDF feedback #12 — Method + Test + Matrix columns). */
  const methodPickerRows = useMemo(() => {
    if (!lookups) return [] as { id: string; name: string; tests: string; matrices: string }[];
    const testMap   = new Map<string, Set<string>>();
    const matrixMap = new Map<string, Set<string>>();
    for (const t of lookups.tests) {
      if (!t.methodNumber) continue;
      const ts = testMap.get(t.methodNumber)   ?? new Set<string>(); ts.add(t.testName);   testMap.set(t.methodNumber, ts);
      if (t.matrixName) {
        const ms = matrixMap.get(t.methodNumber) ?? new Set<string>(); ms.add(t.matrixName); matrixMap.set(t.methodNumber, ms);
      }
    }
    return Array.from(testMap.entries())
      .map(([method, tests]) => ({
        id:       method,
        name:     method,
        tests:    Array.from(tests).sort().join(', '),
        matrices: Array.from(matrixMap.get(method) ?? []).sort().join(', '),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [lookups]);

  // ── Switch to detail mode (loads lookups) ────────────────────────────────
  async function openNewReport() {
    setMode('detail');
    setReadOnly(false);
    setViewedReport(null);
    setRetrieved(false);
    setRetrieveRows([]);
    setRetrieveMs(null);
    setRetrieveErr(null);
    setFilters(EMPTY_FILTERS);
    // "+ New" always starts with the query panel open so the user can pick
    // filters before the first Retrieve.
    setPanelCollapsed(false);
    setPanelAsPopup(false);
    // Reset save state — opening "+ New" starts a fresh report header.
    setSavedId(null);
    setSavedReportId(null);
    setReportName('');
    if (!lookups) await loadLookups();
  }
  function backToList() {
    // In embedded mode the parent (MOR Pending Approval / Delivery list)
    // owns the Back navigation — hand control back so the user lands on
    // the host list, not this component's internal saved-reports list.
    if (onBack) { onBack(); return; }
    setMode('list');
    setReadOnly(false);
    setViewedReport(null);
    setPendingRetrieve(false);
    // Refresh list so any status change is visible immediately.
    void loadList();
  }

  // Build the query string the API expects for the RC Monthly endpoints —
  // every populated filter is sent as a CSV; empty filters are omitted so the
  // SP treats them as "no filter".
  function buildRcParams(): Record<string, string> {
    // Date Collected is optional for Retrieve.  Only include the params if
    // the user actually filled the inputs — the API/SP will skip the date
    // filter when they're absent.  (The RC Monthly Report button enforces
    // them separately via dateRangeReady.)
    const p: Record<string, string> = {};
    if (dateCollectedFrom) p.dateFrom = dateCollectedFrom;
    if (dateCollectedTo)   p.dateTo   = dateCollectedTo;
    // Date Received — user-typed values win.  If both are blank, derive
    // the implicit window from the Date Range chip so Retrieve still has a
    // bounded filter (matches Custom Reporting's chip-driven behaviour).
    // Fix 2026-05-31: previously the chip wrote into these inputs on every
    // pick; user feedback was to keep the inputs blank, so we now derive
    // the range silently here at request-build time.
    if (dateReceivedFrom)        p.receivedFrom = dateReceivedFrom;
    else {
      const co = rangeCutoff(dateRange);
      if (co)                    p.receivedFrom = fmtDate(co);
    }
    if (dateReceivedTo)          p.receivedTo   = dateReceivedTo;
    else if (dateRange !== 'All') p.receivedTo  = fmtDate(new Date());
    // Date Analyzed — optional, user-typed only (no chip-derived fallback).
    // Matched against SampleParameter.AnalyzedDate by the RC Monthly SPs.
    if (dateAnalyzedFrom) p.analyzedFrom = dateAnalyzedFrom;
    if (dateAnalyzedTo)   p.analyzedTo   = dateAnalyzedTo;
    const addStr  = (k: string, set: Set<string>) => {
      if (set.size > 0) p[k] = Array.from(set).join(',');
    };
    const addNum  = (k: string, set: Set<number>) => {
      if (set.size > 0) p[k] = Array.from(set).join(',');
    };
    addNum('categoryIds',    filters.categoryIds);
    addNum('subCategoryIds', filters.subCategoryIds);
    addStr('clientIds',      filters.clientIds);
    addStr('projectIds',     filters.projectIds);
    addStr('testIds',        filters.testIds);
    addStr('methodNumbers',  filters.methodNumbers);
    // Parameter scoping rule (user feedback 2026-05-31): when an LSI Report
    // is selected the Retrieve grid must show ONLY the LSI-formula inputs,
    // never the rest of the lab.  If the user already ticked Parameter IDs
    // we intersect with the LSI seven; otherwise we inject all seven so the
    // grid mirrors what the LSI workbook will consume.  Other report
    // templates fall through to the user's explicit Parameter picks
    // unchanged.
    if (indexReportActive && indexAllowed) {
      // Scope the Retrieve grid to the active index report's fixed inputs (LSI's
      // seven, or Water Stability's six) so the grid mirrors what the workbook
      // consumes.  User ticks intersect with that set; otherwise inject the set.
      const idxIds = indexAllowed.paramIds;
      const userParams = filters.parameterIds;
      const scoped = userParams.size > 0
        ? new Set(Array.from(userParams).filter(id => idxIds.has(id)))
        : new Set(idxIds);
      if (scoped.size > 0) p.parameterIds = Array.from(scoped).join(',');
    } else {
      addStr('parameterIds', filters.parameterIds);
    }
    addStr('jobIds',         filters.jobIds);
    addStr('batchIds',       filters.batchIds);   // QC Batch IDs — narrows by qbs.UQABID
    addStr('siteIds',        filters.siteIds);
    // Tell the RC Monthly SP which date column to filter on.
    // Date Collected takes priority; Date Received (explicit or chip-derived) is second.
    if (dateCollectedFrom || dateCollectedTo) {
      p.dateType = 'Collected';
    } else if (dateReceivedFrom || dateRange !== 'All') {
      p.dateType = 'Received';
    }
    return p;
  }

  // ── Retrieve action — mirrors BTLIMS MORAdhocRetrieve_Execute ────────────
  // Fetches SampleParameter rows matching the filter set; the response is
  // split client-side into Sample tab vs QC tab via the IsQc flag.
  //
  // Retrieve has NO mandatory column — Date Collected is optional here
  // (only the RC Monthly Report button requires a date range).  When dates
  // are blank the API/SP skips the Date Collected filter entirely.
  /** True when the user has picked at least one query filter — any picker
   *  selection OR any of the three date ranges.  Retrieve requires this so we
   *  never run an unbounded query (which pulls the whole table + hits the row
   *  cap).  User feedback 2026-05-28: "while click retrieve need to select any
   *  one query if not show the message to select". */
  function hasAnyFilter(): boolean {
    const f = filters;
    const anyPicker =
      f.clientIds.size      > 0 || f.projectIds.size   > 0 ||
      f.testIds.size        > 0 || f.methodNumbers.size > 0 ||
      f.parameterIds.size   > 0 || f.jobIds.size        > 0 ||
      f.batchIds.size       > 0 || f.siteIds.size       > 0 ||
      f.categoryIds.size    > 0 || f.subCategoryIds.size > 0;
    const anyDate =
      !!dateCollectedFrom || !!dateCollectedTo ||
      !!dateReceivedFrom  || !!dateReceivedTo  ||
      !!dateAnalyzedFrom  || !!dateAnalyzedTo;
    // 2026-06-01 (user feedback): The Date Range chip in the top-right
    // toolbar is a default scope guardrail (3M on first paint) — it is NOT
    // a filter the user actively picked.  Treating it as a filter caused
    // Retrieve to proceed on a freshly-opened form with nothing selected,
    // running a multi-thousand-row 3-month sweep the user didn't ask for.
    //
    // From now on Retrieve requires AT LEAST ONE explicit picker or AT
    // LEAST ONE explicit date field.  If the user only changes the chip
    // (e.g. picks 1Y or 6M), they still have to pair it with a picker /
    // date.  This matches the user's mental model: "I want to retrieve
    // when I've actually chosen something."
    return anyPicker || anyDate;
  }

  // `skipFilterCheck` is set by the programmatic auto-retrieve that runs when
  // a saved report is opened — that path has already populated the filters
  // from the saved snapshot, so it must not be blocked by the guard.
  // ── Server-side page loader ──────────────────────────────────────────────
  // One round-trip → one 200-row page of `tab` rows + per-tab totals.
  // Used by Retrieve, tab-switch, and the Prev / Next buttons.
  async function loadServerPage(tab: 'sample'|'qc', page: number, ac: AbortController) {
    // ── RC Monthly: grid from FillRCSampleDetailsDE_sp port (drop #17) ─────
    // When RC Monthly is selected, both the Sample and QC grids are filled
    // from the RC sample-detail SP (full grid columns, incl. Field Method)
    // instead of the generic Retrieve SP.  We fetch the whole result set once
    // (a single bounded month — Sample + QC rows) and page each tab
    // client-side by the IsQc flag.
    if (rcMonthlySelected) {
      if (rcSampleAllRef.current.length === 0) {
        const res = await api.get('/api/v1/mor-adhoc/rc-sample-details', {
          params: {
            dateFrom: dateCollectedFrom || undefined,
            dateTo:   dateCollectedTo   || undefined,
            // Optional Date Received / Date Analyzed criteria (added 2026-06-09).
            // Date Received is matched against SC_ReceivedDates; Date Analyzed
            // against SampleParameter.AnalyzedDate.  Blank = no narrowing.
            receivedFrom: dateReceivedFrom || undefined,
            receivedTo:   dateReceivedTo   || undefined,
            analyzedFrom: dateAnalyzedFrom || undefined,
            analyzedTo:   dateAnalyzedTo   || undefined,
          },
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        rcSampleAllRef.current = ((res.data as { data?: RetrieveRow[] })?.data ?? []);
      }
      const all     = rcSampleAllRef.current;
      const tabRows  = all.filter(r => tab === 'qc' ? r.isQc : !r.isQc);
      const start    = (page - 1) * PAGE_SIZE;
      setRetrieveRows(tabRows.slice(start, start + PAGE_SIZE));
      setServerSampleCount(all.filter(r => !r.isQc).length);
      setServerQcCount(all.filter(r => r.isQc).length);
      return;
    }
    // FB2 (5/31/2026) — historic mode pivots to the BTLIMSDC SP and its
    // POST endpoint (CSV picker lists too long for a GET query string).
    if (historicMode) {
      // Same chip-driven fallback as buildRcParams — explicit Date Received
      // wins, otherwise derive from the Date Range chip so the historic SP
      // always has a bounded window (it RAISERRORs without one).
      let recvFrom = dateReceivedFrom || undefined;
      let recvTo   = dateReceivedTo   || undefined;
      if (!recvFrom) {
        const co = rangeCutoff(dateRange);
        if (co) recvFrom = fmtDate(co);
      }
      if (!recvTo && dateRange !== 'All') {
        recvTo = fmtDate(new Date());
      }
      // ── Translate snapshot-shaped (Guid) picker filters to BTLIMSDC
      // name-based filters (user feedback 2026-05-31: "jobid, test,
      // qcbatchid also not showing date based also not filter").  BTLIMSDC
      // is a flat legacy dump where every "id" is actually a string name —
      // CustomerID, ProjectID, Test, Param, Method, Site, SampleNumber,
      // Matrix.  Without this translation the historic SP ignores every
      // picker the user ticks.
      //
      // QC Batch IDs (filters.batchIds) have NO equivalent in BTLIMSDC
      // (the legacy table doesn't carry the analytical batch GUID) so they
      // are silently dropped on the historic path.
      const csvFromIds = <TKey extends string | number, TItem>(
        ids: ReadonlySet<TKey>,
        items: TItem[] | undefined,
        idOf: (t: TItem) => string | number,
        labelOf: (t: TItem) => string | null | undefined,
      ): string | undefined => {
        if (!items || ids.size === 0) return undefined;
        const idSet = new Set<string>();
        ids.forEach(x => idSet.add(String(x)));
        const labels: string[] = [];
        for (const it of items) {
          if (idSet.has(String(idOf(it)))) {
            const lbl = labelOf(it);
            if (lbl) labels.push(lbl);
          }
        }
        return labels.length > 0 ? labels.join(',') : undefined;
      };
      const customerCsv = csvFromIds(filters.clientIds,    lookups?.clients,     c => c.id,        c => c.name);
      const projectCsv  = csvFromIds(filters.projectIds,   lookups?.projects,    p => p.id,        p => p.projectId);
      const testCsv     = csvFromIds(filters.testIds,      lookups?.tests,       t => t.id,        t => t.testName);
      const paramCsv    = csvFromIds(filters.parameterIds, lookups?.parameters,  p => p.id,        p => p.name);
      const siteCsv     = csvFromIds(filters.siteIds,      lookups?.sampleSites, s => s.id,        s => s.name);
      const jobCsv      = csvFromIds(filters.jobIds,       jobsBatches.jobs,     j => j.id,        j => j.name);
      const methodCsv   = filters.methodNumbers.size > 0
        ? Array.from(filters.methodNumbers).join(',')
        : undefined;

      const body = {
        dateFrom:       dateCollectedFrom || undefined,
        dateTo:         dateCollectedTo   || undefined,
        receivedFrom:   recvFrom,
        receivedTo:     recvTo,
        analyzedFrom:   dateAnalyzedFrom  || undefined,
        analyzedTo:     dateAnalyzedTo    || undefined,
        customerIds:    customerCsv,
        projectIds:     projectCsv,
        testNames:      testCsv,
        parameterNames: paramCsv,
        methodNames:    methodCsv,
        siteNames:      siteCsv,
        sampleNumbers:  jobCsv,    // BTLIMSDC.SampleNumber == ALPACA JobID
        tab, page, pageSize: PAGE_SIZE,
      };
      const res = await api.post('/api/v1/mor-adhoc/historic/retrieve', body, { signal: ac.signal });
      if (ac.signal.aborted) return;
      const payload = res.data?.data as {
        rows: HistoricRow[];
        sampleCount: number;
        qcCount: number;
      } | undefined;
      // Adapt historic rows → existing RetrieveRow shape so the grid renders
      // unchanged.  Empty/N-A columns (Status, MDL/LOQ/UQL, Category) stay
      // blank in the UI; the user sees a "Historic Data" badge in the header.
      const rows: RetrieveRow[] = (payload?.rows ?? []).map(h => ({
        sampleParameterId: String(h.rowId),
        sampleLoginId:     '',
        jobOid:            '',
        jobId:             h.jobId,
        sampleNo:          0,
        sampleID:          h.sampleID ?? '',
        qcBatchID:         null,
        sampleName:        null,
        siteName:          h.siteName,
        sampleMatrix:      h.sampleMatrix,
        dateCollected:     h.dateCollected,
        dateReceived:      h.dateReceived,
        dateAnalyzed:      h.dateAnalyzed,
        sampleLocation:    null,
        client:            h.client,
        clientId:          null,
        projectId:         h.projectId,
        projectName:       null,
        test:              h.test,
        method:            h.method,
        parameter:         h.parameter,
        result:            h.result,
        finalResult:       h.finalResult,
        units:             h.units,
        status:            null,
        qcTypeName:        h.qcTypeName,
        isQc:              h.isQc,
        category:          null,
        subCategory:       null,
        df:                h.df,
        mrl:               h.mrl,
        qualifier:         h.qualifier,
        analyst:           h.analyst,
      }));
      setRetrieveRows(rows);
      setServerSampleCount(payload?.sampleCount ?? 0);
      // RC Monthly + LSI always show 0 on the QC tab — those reports are
      // sample-only and the QC tab pill should reflect that (drop #17 —
      // restored 2026-06-01).
      setServerQcCount((rcMonthlySelected || indexReportActive) ? 0 : (payload?.qcCount ?? 0));
      return;
    }

    // ── QC-tab carve-out for LSI parameter narrow ──────────────────────────
    // User feedback 2026-06-01: opening a saved LSI report and clicking the
    // QC tab shows zero rows.  Root cause: when LSI is the selected report
    // template, buildRcParams() injects the seven LSI parameter Oids
    // (Temperature, pH, …) into `parameterIds`.  Those parameters are
    // sample-level field measurements — QC controls almost never carry them,
    // so the QC tab returns 0.
    //
    // Fix: when fetching the QC tab AND LSI is active AND the user hasn't
    // explicitly ticked any Parameters themselves, drop the parameter filter
    // entirely so the QC tab shows every QC control in scope.  The Sample
    // tab still gets the LSI narrow (correct — that's what feeds the LSI
    // workbook).  When the user explicitly picked Parameters, we keep their
    // selection on both tabs (their explicit choice wins).
    const rcParams = buildRcParams();
    const qcLsiWidened = tab === 'qc' && indexReportActive && filters.parameterIds.size === 0;
    if (qcLsiWidened) {
      delete rcParams.parameterIds;
    }
    const res = await api.get('/api/v1/mor-adhoc/retrieve', {
      params: { ...rcParams, tab, page, pageSize: PAGE_SIZE },
      signal: ac.signal,
    });
    const body = res.data as {
      data: RetrieveRow[];
      meta?: { sampleCount?: number; qcCount?: number; totalCount?: number; paged?: boolean };
    };
    if (ac.signal.aborted) return;
    setRetrieveRows(body.data ?? []);
    // When we widened the QC query (dropped LSI param narrow), the response's
    // sampleCount reflects the WIDER filter — not the LSI-narrow that drives
    // the Sample tab badge.  So only update qcCount in that case; leave the
    // Sample tab badge untouched.
    if (!qcLsiWidened) {
      setServerSampleCount(body.meta?.sampleCount ?? 0);
    }
    // RC Monthly + LSI always show 0 on the QC tab (drop #17 — restored 2026-06-01).
    setServerQcCount((rcMonthlySelected || indexReportActive) ? 0 : (body.meta?.qcCount ?? 0));
  }

  async function handleRetrieve(opts?: { skipFilterCheck?: boolean }) {
    if (!opts?.skipFilterCheck && !hasAnyFilter()) {
      notify.warning('Please select at least one filter (a picker value or a date range) before clicking Retrieve.');
      return;
    }
    // User feedback 2026-05-31: "no need these validations".  Historic Data
    // mode (6M+) used to require an explicit Date Collected or Date Received
    // From+To pair.  That guard is removed — buildRcParams + the historic
    // POST body already derive a bounded receivedFrom/receivedTo from the
    // Date Range chip (e.g. 6M → today-6M..today), so the SP never sees an
    // unbounded query.  If the user picks 'All' the chip-derived fallback
    // uses a 5-year window.
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setRetrieving(true);
    setRetrieveErr(null);
    setRetrieveTruncated(false);
    setRetrieveRows([]);
    setSamplePage(1);
    setQcPage(1);
    // Drop the cached RC sample-detail rows so this Retrieve refetches fresh
    // (drop #17 — restored 2026-06-01).  Critical for RC Monthly + LSI tabs
    // where the field/QC counts depend on the current Retrieve's row set.
    rcSampleAllRef.current = [];
    // RC Monthly + LSI/Water Stability always land on the Sample tab (QC empty).
    if (rcMonthlySelected || indexReportActive) setActiveTab('sample');
    const t0 = performance.now();

    try {
      // Probe Sample tab page 1 first so we know which tab has data.
      await loadServerPage('sample', 1, ac);
      if (ac.signal.aborted) return;

      // ── LSI: probe the QC count with the WIDENED filter ────────────────
      // When LSI is active, the Sample probe applied the 7-parameter narrow
      // and the server reported qcCount = 0 (no QC row carries those
      // parameters).  That makes the QC tab badge show a misleading "0"
      // even when QC controls exist for the date/client/project scope.
      // Fire a second probe for QC with parameterIds stripped so the badge
      // shows the real count up-front.  Cheap — single SP call, indexed.
      if (indexReportActive && filters.parameterIds.size === 0) {
        try {
          await loadServerPage('qc', 1, ac);
          if (ac.signal.aborted) return;
          // loadServerPage just set the QC page rows — but we want the
          // user to land on the Sample tab.  Re-load Sample page 1 so
          // retrieveRows shows the Sample tab again.
          await loadServerPage('sample', 1, ac);
          if (ac.signal.aborted) return;
        } catch { /* probe is best-effort; ignore */ }
      }

      setRetrieved(true);
      setPanelCollapsed(true);
      setPanelAsPopup(false);
      setRetrieveMs(performance.now() - t0);
    } catch (e: unknown) {
      if (ac.signal.aborted) return;
      console.error(e);
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setRetrieveErr(msg ?? 'Query failed — check the API logs.');
      setPanelCollapsed(false);
      setPanelAsPopup(false);
    } finally {
      if (abortRef.current === ac) setRetrieving(false);
    }
  }

  // Tab switch — fetch the new tab's saved page if we haven't already.
  async function switchTab(next: 'sample'|'qc') {
    if (next === activeTab) return;
    setActiveTab(next);
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      await loadServerPage(next, next === 'sample' ? samplePage : qcPage, ac);
    } catch (e) {
      if (!ac.signal.aborted) console.error(e);
    }
  }

  // Prev / Next page on the current tab.
  async function gotoServerPage(newPage: number) {
    if (newPage < 1) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    if (activeTab === 'sample') setSamplePage(newPage); else setQcPage(newPage);
    try {
      await loadServerPage(activeTab, newPage, ac);
    } catch (e) {
      if (!ac.signal.aborted) console.error(e);
    }
  }

  // ── Export both tabs to a single .xlsx ───────────────────────────────────
  // Fetches the FULL unbounded result set once, splits into Sample / QC,
  // writes one workbook with two sheets.  Mirrors the export pattern used
  // by ModuleListView (uses the same xlsx library that's already bundled).
  async function handleExportGrid() {
    if (exportingGrid) return;
    setExportingGrid(true);
    try {
      const res = await api.get('/api/v1/mor-adhoc/retrieve', {
        params: buildRcParams(),     // no pageSize → legacy unbounded path
      });
      const rows = (res.data?.data ?? []) as RetrieveRow[];
      const sample = rows.filter(r => !r.isQc);
      const qc     = rows.filter(r =>  r.isQc);

      // Columns mirror what the grid renders.  RetrieveRow (local TS
      // interface) only declares the grid-essential fields — the API
      // returns more, but TS won't let us index by string here.
      const cols: { key: keyof RetrieveRow; header: string }[] = [
        { key: 'client',            header: 'Client' },
        { key: 'projectId',         header: 'ProjectID' },
        { key: 'jobId',             header: 'JobID' },
        { key: 'qcBatchID',         header: 'QCBatchID' },
        { key: 'sampleID',          header: 'SysSampleCode' },
        { key: 'sampleName',        header: 'SampleName' },
        { key: 'siteName',          header: 'SiteName' },
        { key: 'sampleMatrix',      header: 'SampleMatrix' },
        { key: 'dateCollected',     header: 'DateCollected' },
        { key: 'dateReceived',      header: 'DateReceived' },
        { key: 'dateAnalyzed',      header: 'DateAnalyzed' },
        { key: 'test',              header: 'Test' },
        { key: 'method',            header: 'Method' },
        { key: 'parameter',         header: 'Parameter' },
        { key: 'result',            header: 'Result' },
        { key: 'finalResult',       header: 'FinalResult' },
        { key: 'units',             header: 'Units' },
        { key: 'category',          header: 'Category' },
        { key: 'subCategory',       header: 'SubCategory' },
        { key: 'qcTypeName',        header: 'QCType' },
        { key: 'status',            header: 'Status' },
      ];

      // Date columns export as MM/DD/YYYY HH:mm in the LAB timezone (fmtLabDateTime)
      // so a Houston collect time reads the same for any viewer — instead of the
      // raw ISO "2026-06-11T09:10:00.000" Excel was dumping.
      const DATE_KEYS = new Set<keyof RetrieveRow>(['dateCollected', 'dateReceived', 'dateAnalyzed']);
      const toAoa = (set: RetrieveRow[]) => [
        cols.map(c => c.header),
        ...set.map(r => cols.map(c => {
          const v = r[c.key];
          if (v == null) return '';
          if (DATE_KEYS.has(c.key)) return fmtLabDateTime(v as string);
          return typeof v === 'object' ? JSON.stringify(v) : v;
        })),
      ];

      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(toAoa(sample)), 'Sample Results');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(toAoa(qc)),     'QC Sample Results');

      const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..*$/, '').replace('T','_');
      XLSX.writeFile(wb, `MOR_Adhoc_${stamp}.xlsx`);
    } catch (e: unknown) {
      console.error(e);
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Export failed — check the API logs.');
    } finally {
      setExportingGrid(false);
    }
  }

  // ── Auto-Retrieve when entering read-only mode ───────────────────────────
  // handleViewReport populates the filter state synchronously, sets
  // pendingRetrieve = true, then waits for lookups.  Once both lookups are
  // loaded AND the filter state has been committed by React, this effect
  // fires Retrieve exactly once so the saved row-set re-materialises.
  useEffect(() => {
    if (pendingRetrieve && lookups && !retrieving) {
      setPendingRetrieve(false);
      // Opening a saved report — filters are already populated from the
      // snapshot, so skip the "select a filter" guard.
      void handleRetrieve({ skipFilterCheck: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRetrieve, lookups]);

  // ── Pre-load lookups + report templates + jobs/batches on mount ──────────
  // User feedback 2026-05-30: "load in background first load the report
  // template, refresh snapshot, refresh lookups".  Kick the slow API calls
  // off as soon as the page mounts — by the time the user clicks "+ New" or
  // a saved row, lookups + templates are already in cache so the detail
  // view paints instantly with all picker options ready.
  // Snapshot refresh is NOT auto-fired (it's a 2-min SP) — user can hit the
  // explicit "Refresh Snapshot" toolbar button when they want fresh data.
  useEffect(() => {
    if (!lookups && !loadingLookups) {
      void loadLookups(true);   // silent background preload — must NOT animate the global top bar
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Export the current filters straight to a multi-sheet XLSX ────────────
  async function handleExportExcel() {
    // RC Monthly date rule (2026-06-09): Date Collected is now OPTIONAL — the
    // report can be driven by ANY of Date Collected / Date Received / Date
    // Analyzed.  Require at least ONE date input so the query stays bounded.
    const colFrom = (dateCollectedFrom ?? '').trim();
    const colTo   = (dateCollectedTo   ?? '').trim();
    const hasAnyDate =
      !!colFrom || !!colTo ||
      !!dateReceivedFrom || !!dateReceivedTo ||
      !!dateAnalyzedFrom || !!dateAnalyzedTo ||
      (dateRange && dateRange !== 'All');   // the Date Range chip supplies a Received window
    if (!hasAnyDate) {
      notify.warning('Select at least one date range — Date Collected, Date Received, or Date Analyzed — for the RC Monthly Report.');
      return;
    }
    // Sanity-check the Date Collected range only when both ends are filled.
    if (colFrom && colTo && colFrom > colTo) {
      notify.warning('Date Collected From cannot be after Date Collected To. Please correct the range.');
      return;
    }
    setExporting(true);
    try {
      // buildRcParams() already includes whichever of the three ranges the
      // user filled (plus the chip-derived Received window) — no forcing.
      const params = buildRcParams();
      // Forward savedId so the watermark logic on the API can drop the
      // "Draft" stamp when previewing a SAVED report (user feedback
      // 2026-05-30: "before save four reports need draft water mark").
      if (savedId) params.savedId = savedId;
      const qs     = new URLSearchParams(params).toString();
      const res    = await api.get(`/api/v1/mor-adhoc/rc-monthly/excel?${qs}`, {
        responseType: 'blob',
        signal: (previewAbortRef.current = new AbortController()).signal,
      });

      // Server returned 200 — but if it accidentally serialized JSON (e.g.
      // some unexpected middleware), peek at the content-type header.
      const blob = res.data as Blob;
      const contentType = (res.headers?.['content-type'] as string | undefined) ?? '';
      if (!contentType.includes('spreadsheet')) {
        const text = await blob.text();
        console.error('Excel endpoint returned non-spreadsheet content:', text);
        notify.error('Excel export failed: ' + text.slice(0, 200));
        return;
      }

      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url;
      // Filename period: prefer Collected, else Received, else Analyzed.
      const lblFrom = (params.dateFrom || params.receivedFrom || params.analyzedFrom || '').replace(/-/g, '');
      const lblTo   = (params.dateTo   || params.receivedTo   || params.analyzedTo   || '').replace(/-/g, '');
      a.download = `RC_Monthly_${lblFrom || 'report'}${lblTo ? '_' + lblTo : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      if (previewAbortRef.current?.signal.aborted) return;   // Stop clicked
      console.error(e);
      // When responseType='blob', axios stuffs the error body into a Blob even
      // for 4xx/5xx — read it as text so we can surface the real reason.
      const err = e as { response?: { data?: unknown; status?: number } };
      let msg = 'Excel export failed.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text) as { error?: { message?: string } };
          msg = json?.error?.message ?? text.slice(0, 200);
        } catch { /* not JSON, ignore */ }
      } else if (data && typeof data === 'object' && 'error' in data) {
        msg = (data as { error?: { message?: string } }).error?.message ?? msg;
      }
      notify.error(`Excel export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  }

  // ── EWPP LSI Report — single-sheet XLSX matching sheet 3 of the
  // reference workbook.  Date Collected From/To is REQUIRED — the sites
  // and parameters come from the DWQRReportTemplateSetup row
  // "EWPP-LSIReport" (DWRT52), so other query-panel filters don't apply.
  async function handleLsiReport() {
    // Pick the date AXIS + window from whichever range the user filled in the
    // query panel (user request 2026-06-25 — "the date selected in the Query
    // Panel should be displayed on the report", and Received/Analyzed must
    // actually filter, not show the full Collected window).  Priority: an
    // explicit Collected / Received / Analyzed picker pair wins, in that order;
    // otherwise fall back to the Date Range chip on Date Collected.  The window
    // is sent as dateFrom/dateTo (so the Excel header prints it) plus a
    // dateBasis flag the SP uses to filter the matching column.
    let dateBasis: 'collected' | 'received' | 'analyzed' = 'collected';
    let window: { from: string; to: string } | null;
    if (dateCollectedFrom && dateCollectedTo) {
      dateBasis = 'collected'; window = { from: dateCollectedFrom, to: dateCollectedTo };
    } else if (dateReceivedFrom && dateReceivedTo) {
      dateBasis = 'received';  window = { from: dateReceivedFrom,  to: dateReceivedTo  };
    } else if (dateAnalyzedFrom && dateAnalyzedTo) {
      dateBasis = 'analyzed';  window = { from: dateAnalyzedFrom,  to: dateAnalyzedTo  };
    } else {
      window = effectiveCollectedRange();   // Date Range chip → Collected window
    }
    if (!window) {
      notify.warning('Pick a Date Range — or a Date Collected / Received / Analyzed range — to generate the LSI Report.');
      return;
    }
    setLsiExporting(true);
    try {
      const params = new URLSearchParams({
        dateFrom:  window.from,
        dateTo:    window.to,
        dateBasis,
      });
      // Same watermark logic — saved reports drop the "Draft" stamp.
      if (savedId) params.set('savedId', savedId);
      // Forward the query-panel filters as the SAME raw GUID CSVs the Retrieve
      // grid sends (buildRcParams).  Rev 7 (2026-06-23): the LSI endpoint now
      // matches by GUID (ClientId / ProjectOid / JobOid / SampleSites.Oid), not
      // by the picker's display strings.  Mapping to Customer name / Project
      // CODE was lossy — one Project CODE (e.g. "SEWPP Plant Daily") is shared
      // by many distinct projects, so the report leaked sibling-project rows
      // the grid had excluded (job 260401057).  Sending GUIDs keeps the two
      // in lock-step.
      const addGuidCsv = (k: string, ids: ReadonlySet<string>) => {
        if (ids.size > 0) params.set(k, Array.from(ids).join(','));
      };
      addGuidCsv('clientIds',  filters.clientIds);
      addGuidCsv('projectIds', filters.projectIds);
      addGuidCsv('jobIds',     filters.jobIds);
      addGuidCsv('siteIds',    filters.siteIds);
      const res = await api.get(`/api/v1/mor-adhoc/lsi-report/excel?${params.toString()}`, {
        responseType: 'blob',
        signal: (previewAbortRef.current = new AbortController()).signal,
      });
      const blob = res.data as Blob;
      const contentType = (res.headers?.['content-type'] as string | undefined) ?? '';
      if (!contentType.includes('spreadsheet')) {
        const text = await blob.text();
        console.error('LSI endpoint returned non-spreadsheet content:', text);
        notify.error('LSI Report failed: ' + text.slice(0, 200));
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `EWPP_LSI_Report_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      console.error(e);
      const err = e as { response?: { data?: unknown } };
      if (previewAbortRef.current?.signal.aborted) return;   // Stop clicked
      let msg = 'LSI Report failed.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text) as { error?: { message?: string } };
          msg = json?.error?.message ?? text.slice(0, 200);
        } catch { /* not JSON */ }
      }
      notify.error(`LSI Report failed: ${msg}`);
    } finally {
      setLsiExporting(false);
    }
  }

  // ── Water Stability Report — single-sheet XLSX matching the approved v3
  // reference workbook.  Superset of LSI (adds Magnesium → Total Hardness +
  // CCPP per SM 2330C).  Same date-axis / filter / watermark behaviour as
  // handleLsiReport; only the endpoint and file name differ.
  async function handleWaterStabilityReport() {
    let dateBasis: 'collected' | 'received' | 'analyzed' = 'collected';
    let window: { from: string; to: string } | null;
    if (dateCollectedFrom && dateCollectedTo) {
      dateBasis = 'collected'; window = { from: dateCollectedFrom, to: dateCollectedTo };
    } else if (dateReceivedFrom && dateReceivedTo) {
      dateBasis = 'received';  window = { from: dateReceivedFrom,  to: dateReceivedTo  };
    } else if (dateAnalyzedFrom && dateAnalyzedTo) {
      dateBasis = 'analyzed';  window = { from: dateAnalyzedFrom,  to: dateAnalyzedTo  };
    } else {
      window = effectiveCollectedRange();   // Date Range chip → Collected window
    }
    if (!window) {
      notify.warning('Pick a Date Range — or a Date Collected / Received / Analyzed range — to generate the CCPP Report.');
      return;
    }
    setWsExporting(true);
    try {
      const params = new URLSearchParams({
        dateFrom:  window.from,
        dateTo:    window.to,
        dateBasis,
      });
      if (savedId) params.set('savedId', savedId);
      const addGuidCsv = (k: string, ids: ReadonlySet<string>) => {
        if (ids.size > 0) params.set(k, Array.from(ids).join(','));
      };
      addGuidCsv('clientIds',  filters.clientIds);
      addGuidCsv('projectIds', filters.projectIds);
      addGuidCsv('jobIds',     filters.jobIds);
      addGuidCsv('siteIds',    filters.siteIds);
      const res = await api.get(`/api/v1/mor-adhoc/water-stability-report/excel?${params.toString()}`, {
        responseType: 'blob',
        signal: (previewAbortRef.current = new AbortController()).signal,
      });
      const blob = res.data as Blob;
      const contentType = (res.headers?.['content-type'] as string | undefined) ?? '';
      if (!contentType.includes('spreadsheet')) {
        const text = await blob.text();
        console.error('Water Stability endpoint returned non-spreadsheet content:', text);
        notify.error('CCPP Report failed: ' + text.slice(0, 200));
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `CCPP_Report_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      console.error(e);
      const err = e as { response?: { data?: unknown } };
      if (previewAbortRef.current?.signal.aborted) return;   // Stop clicked
      let msg = 'CCPP Report failed.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text) as { error?: { message?: string } };
          msg = json?.error?.message ?? text.slice(0, 200);
        } catch { /* not JSON */ }
      }
      notify.error(`CCPP Report failed: ${msg}`);
    } finally {
      setWsExporting(false);
    }
  }

  // ── Phase 2.3 · Keyboard shortcuts for the MOR Adhoc action bar (CLAUDE.md
  //    standing rule 14, §18.6, ADR-0013).  Each key MIRRORS the visible primary
  //    button for the CURRENT mode/sub-state — same handler, same disabled — so a
  //    binding is inert + greyed in the ? sheet exactly when its button is.  The
  //    array is gated by mode/state (the buttons live in gated sub-views); the
  //    useHotkeys CALL is unconditional, placed before the return.  List New/
  //    Export use the shared reserved keys; detail Load/Save/Preview mirror each
  //    state's solid primary.  (Refresh's Alt+R comes from the shared AppToolbar.)
  const morKeys: HotkeyDef[] = [];
  {
    const G = 'MOR Adhoc Reporting';
    if (mode === 'list') {
      if (perm.create) morKeys.push({ combo: 'alt+n', label: 'New', group: G, order: 1,
        run: () => { void openNewReport(); } });
      morKeys.push({ combo: 'alt+x', label: 'Export', group: G, order: 7,
        disabled: listSel.size === 0 || rowDownloadKey !== null,
        run: () => { void exportSelectedReports(); } });
    } else if (mode === 'detail') {
      if (!readOnly && !panelCollapsed) {
        // Query panel open → Load (solid primary) · Save
        morKeys.push({ combo: 'alt+l', label: 'Load', group: G, order: 1, disabled: retrieving,
          run: () => { void handleRetrieve(); } });
        if (!panelAsPopup && perm.create) morKeys.push({ combo: 'mod+s', label: 'Save', group: G, order: 2,
          disabled: saving, run: () => { void handleSave(); } });
      } else if (panelCollapsed && !readOnly) {
        // Collapsed (post-Load) → Save (solid primary) · Export to
        if (perm.create) morKeys.push({ combo: 'mod+s', label: 'Save', group: G, order: 2,
          disabled: saving, run: () => { void handleSave(); } });
        morKeys.push({ combo: 'alt+x', label: 'Export', group: G, order: 3, disabled: exportingGrid,
          run: () => { void handleExportGrid(); } });
      } else if (readOnly && viewedReport) {
        // Read-only saved report → Preview (solid primary)
        const vr = viewedReport;
        morKeys.push({ combo: 'alt+p', label: 'Preview', group: G, order: 4,
          disabled: !vr.reportTemplateName || exporting || lsiExporting || wsExporting || previewLoading,
          run: () => { void handlePreviewReport(vr.reportTemplateName ?? ''); } });
      }
    }
  }
  useHotkeys(morKeys);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Generation overlay with animated progress bar ──
          User feedback 5/30/2026 #9: "Check whether we can add a dynamic
          progress bar when previewing a large report".  Tier-1 deterministic
          approach — animate 0 → 85% over an estimated 25 s window, hold at
          85% until the response arrives, then snap to 100% on completion.
          No backend changes required; perceived speed improves materially
          on the 30–60 s heavy reports (RC Monthly cold, large bench PDF).
          The previous bottom-right toast becomes a top-center progress card. */}
      {(previewLoading || exporting || lsiExporting || wsExporting) && (
        <ReportProgressOverlay
          label={wsExporting ? 'Generating CCPP Report…'
              : lsiExporting ? 'Generating LSI Report…'
              : exporting   ? 'Generating RC Monthly Excel…'
              :               'Generating report…'}
          onStop={() => previewAbortRef.current?.abort()}
        />
      )}
      <style>{`
        .mor-page { display:flex; flex-direction:column; height:100%; overflow:hidden; background:var(--ai-bg); }

        /* Tighter page chrome — match the prototype's .content{padding:9px 14px}
           so there's less empty space around the navigation, heading and form
           (user request 2026-07-04: "too much space near navigation and form
           andalso near heading … like second image").  Scoped to .mor-page so no
           other form is affected:
             • left/right gutter 20px → 14px  (.bl-top)
             • space above the heading 16px → 8px, below 10px → 6px (.dp-page-hdr)  */
        .mor-page .bl-top { padding-left:14px; padding-right:14px; }
        .mor-page .dp-page-hdr { padding:8px 0 6px; }

        /* ── Query panel ── */
        .mor-qp { padding:8px 14px 14px; overflow-y:auto; }

        /* Horizontal label layout — label on the left, control on the right
           (matches BTLIMS XAF DevExpress chrome).  Used everywhere inside the
           MOR Adhoc 2-column query panel.  Was column-stacked, switched per
           user feedback 2026-05-26 to save vertical space. */
        .mor-field {
          display:grid; grid-template-columns:108px 1fr;
          gap:6px; align-items:center;
          margin-top:3px;
        }
        .mor-field:first-of-type { margin-top:0; }
        .mor-field > label {
          font-size:11px; font-weight:600; color:var(--ai-ink);
          /* RIGHT-aligned so the colon sits next to its input — matches the
             prototype's .adform label { text-align:right } (user request
             2026-07-04: "refer first image, the control alignments are good").
             Was left-aligned, which left a big gap after short labels
             (Client / Test / Method). */
          text-align:right; padding-right:4px;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .mor-field > label::after { content:":"; }

        /* Yellow/cream input background — matches BTLIMS XAF DevExpress chrome
           (per user-supplied screenshot 2026-05-26).  Applied to date inputs
           and any plain input inside the 2-column query form.
           token-exempt: legacy GoLive cream input chrome (user 2026-05-26). */
        .mor-field input[type="date"],
        .mor-field input[type="text"] {
          background: #FFFEF0 !important;
        }

        /* Hide the "mm/dd/yyyy" placeholder text on EMPTY date inputs
           (per user feedback 2026-05-26 — show empty until the user picks).
           Once a value is set the date renders normally; the placeholder
           also returns while the input is focused so the user can type. */
        .mor-field input[type="date"].date-empty:not(:focus)::-webkit-datetime-edit {
          color: transparent;
        }

        /* ── Closed combobox button (the new compact UI) ──
           Cream fills are token-exempt: legacy GoLive cream input chrome
           (user 2026-05-26). */
        .mor-combo {
          display:flex; align-items:center; gap:6px;
          width:100%; box-sizing:border-box;
          background:#FFFEF0; border:1px solid var(--ai-line); border-radius:3px;
          padding:4px 8px; font-size:12px; color:var(--ai-ink);
          cursor:pointer; text-align:left;
          transition: border-color .12s, background-color .12s;
        }
        .mor-combo:hover:not(:disabled) { border-color: var(--brand-accent, var(--ai-orange)); background:#FFFAE0; }
        .mor-combo.open { border-color: var(--brand-accent, var(--ai-orange)); background:#FFFEF0; }
        .mor-combo.on { border-color:var(--brand-accent, var(--ai-orange)); background:#FFF8E0; }
        .mor-combo:disabled { background:var(--ai-readonly-bg); color:var(--ai-muted); cursor:not-allowed; }
        .mor-combo-text {
          flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
        }
        .mor-combo-chevron { font-size:12px; color:var(--ai-muted); flex-shrink:0; }
        .mor-combo.open .mor-combo-chevron { color:var(--ai-blue); transform: rotate(180deg); }

        /* Popover that opens beneath the combobox */
        /* COMPACT picker popover — aggressively tight per user feedback
           ("still it long").  Reduced padding everywhere — header,
           toolbar, list, footer, row cells, and column header heights. */
        .mor-combo-pop {
          position:absolute; z-index:5000;
          background:var(--ai-card); border:1px solid var(--ai-line); border-radius:10px;
          box-shadow:0 8px 28px rgba(0,0,0,.18);
          display:flex; flex-direction:column;
          max-height:340px; overflow:hidden;
        }
        .mor-combo-pop-hdr {
          display:flex; align-items:center; gap:4px;
          padding:2px 6px; background:var(--ai-header-bg);
          border-bottom:1px solid var(--ai-line-soft);
        }
        .mor-combo-pop-hdr i { font-size:11px; color:var(--ai-muted); }
        .mor-combo-pop-hdr input {
          flex:1; border:none; background:transparent;
          font-size:11px; outline:none; padding:0;
        }
        .mor-combo-clear-q {
          background:transparent; border:none; color:var(--ai-muted);
          cursor:pointer; font-size:13px; line-height:1;
          padding:0 3px;
        }
        .mor-combo-clear-q:hover { color:var(--ai-ink); }
        .mor-combo-pop-list {
          flex:1; overflow-y:auto; padding:0;
        }
        .mor-combo-pop-list::-webkit-scrollbar { width:8px; }
        .mor-combo-pop-list::-webkit-scrollbar-thumb { background:var(--ai-line); border-radius:4px; }
        .mor-combo-pop-ftr {
          display:flex; align-items:center; gap:4px;
          padding:2px 5px; background:var(--ai-header-bg);
          border-top:1px solid var(--ai-line-soft);
        }
        .mor-combo-ftr-btn {
          background:var(--ai-card); color:var(--ai-ink); border:1px solid var(--ai-line); border-radius:3px;
          padding:1px 7px; font-size:10.5px; cursor:pointer;
        }
        .mor-combo-ftr-btn:hover:not(:disabled) { background:var(--ai-row-hover); border-color: var(--brand-accent, var(--ai-orange)); color:var(--ai-blue); }
        .mor-combo-ftr-btn.red { color:var(--ai-red); border-color:color-mix(in srgb, var(--ai-red) 30%, #fff); }
        .mor-combo-ftr-btn.red:hover:not(:disabled) { background:color-mix(in srgb, var(--ai-red) 6%, #fff); border-color:color-mix(in srgb, var(--ai-red) 55%, #fff); }
        .mor-combo-ftr-btn:disabled { opacity:.4; cursor:not-allowed; }
        .mor-combo-ftr-ok {
          background:var(--brand-accent, var(--ai-orange)); color:#fff; border:none; border-radius:3px;
          padding:1px 12px; font-size:10.5px; font-weight:600; cursor:pointer;
        }
        .mor-combo-ftr-ok:hover { background:var(--brand-accent-hover, var(--ai-orange-dark)); }

        .mor-picker-empty {
          padding:10px; text-align:center; color:var(--ai-muted); font-style:italic; font-size:11px;
        }

        /* Multi-column picker table — AGGRESSIVELY tight per user
           feedback "still it long".  Every padding minimized; line
           height reduced to ~14pt per data row.
           User feedback 2026-05-26: "line alignment first column fine
           second and third column between line have a problem" — fixed
           by standardising border-right color + box-sizing across the
           header row, search row and data row so the vertical grid
           lines render at the exact same X pixel in every row. */
        .mor-picker-tbl-hdr,
        .mor-picker-tbl-search,
        .mor-picker-tbl-row {
          display:grid; gap:0;
        }
        .mor-picker-tbl-hdr > span,
        .mor-picker-tbl-search > input,
        .mor-picker-tbl-search > span,
        .mor-picker-tbl-cell {
          box-sizing:border-box;
          border-right:1px solid var(--ai-line);
        }
        .mor-picker-tbl-hdr {
          padding:0;
          font-size:10px; font-weight:800; color:var(--ai-ink);
          background:var(--ai-header-bg); border-bottom:1px solid var(--ai-line);
          position:sticky; top:0;
        }
        .mor-picker-tbl-hdr > span {
          padding:1px 5px; line-height:1.4;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .mor-picker-tbl-hdr > span:last-child { border-right:none; }

        /* Per-column search row — each input fills its column edge-to-edge
           so the vertical grid lines line up with the header + body rows. */
        .mor-picker-tbl-search {
          background:var(--ai-card); border-bottom:1px solid var(--ai-line);
          position:sticky; top:16px;
        }
        .mor-picker-tbl-search > input {
          margin:0; padding:0 5px; font-size:10.5px; height:18px;
          border:none; background:var(--ai-card); outline:none; min-width:0;
          width:100%;
        }
        .mor-picker-tbl-search > input:last-child { border-right:none; }
        .mor-picker-tbl-search > input:focus {
          background:var(--ai-row-hover); outline:1px solid var(--ai-blue); outline-offset:-1px;
        }

        .mor-picker-tbl-body { padding:0; }
        .mor-picker-tbl-row {
          align-items:center;
          font-size:11px; cursor:pointer; color:var(--ai-ink);
          border-bottom:1px solid var(--ai-line-soft);
          user-select:none;
        }
        .mor-picker-tbl-row:hover { background:var(--ai-row-hover); }
        .mor-picker-tbl-row.on { background:color-mix(in srgb, var(--brand-accent, var(--ai-orange)) 10%, #fff); color:var(--brand-accent-hover, var(--ai-orange-dark)); font-weight:600; }
        .mor-picker-tbl-row > input[type=checkbox] {
          width:11px; height:11px; cursor:pointer; accent-color: var(--brand-accent, var(--ai-orange));
          margin:0 4px; justify-self:center;
        }
        .mor-picker-tbl-cell {
          padding:1px 5px; line-height:1.4;
          white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        }
        .mor-picker-tbl-row > .mor-picker-tbl-cell:last-child { border-right:none; }

        .mor-qp-actions {
          display:flex; gap:10px; align-items:center; flex-wrap:wrap;
          padding:14px 0 4px;
        }

        /* Full-viewport centred loading overlay (user feedback 2026-06-02:
           "need loading symbol in the center of the application"). */
        .mor-retrieve-loading {
          position:fixed; inset:0; z-index:4000;
          background:rgba(255,255,255,.55); backdrop-filter:blur(1px);
          display:flex; align-items:center; justify-content:center;
          text-align:center;
        }
        .mor-retrieve-loading-box {
          background:var(--ai-card); border:1px solid var(--ai-line); border-radius:10px;
          padding:30px 40px; box-shadow:0 8px 28px rgba(0,0,0,.20);
          display:flex; flex-direction:column; align-items:center; justify-content:center;
        }

        .mor-results {
          margin-top:14px; background:var(--ai-card); border:1px solid var(--ai-line);
          border-radius:10px; padding:16px; box-shadow:var(--ai-shadow-card);
        }
        .mor-results-hdr {
          display:flex; align-items:center; justify-content:space-between;
          margin-bottom:10px;
        }
        .mor-results-title {
          font-size:13px; font-weight:700; color:var(--ai-ink);
          display:flex; align-items:center; gap:7px;
        }
        .mor-results-empty {
          padding:30px; text-align:center; color:var(--ai-muted); font-style:italic; font-size:13px;
        }
      `}</style>

      <div className="bl-page mor-page">
        {/* ─── Top header (always visible) ───────────────────────────────
            When embedded inside MOR Pending Approval / Pending Delivery,
            the host passes a `pageCaption` so the user sees the queue
            they came from instead of "MOR Adhoc Reporting". */}
        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Reporting</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">
                {pageCaption
                  ? pageCaption.breadcrumb
                  : `Ad Hoc Reporting${mode === 'detail' ? ' — Query Panel' : ''}`}
              </span>
            </div>
            {/* Heading is the ONE shared <FormTitle> (BO UI Standard §7) — ON
                the grey page for BOTH modes.  Nav icon/colour + the TOP
                load-time chip + the config-gated Audit Trail chip all come from
                the shared component — no hand-rolled h1. */}
            {mode === 'detail' ? (
              <>
                {/* Detail header — FormTitle LEFT, primary workflow actions at the
                    TOP-RIGHT of the title row (prototype parity, user request
                    2026-07-04: "add the button position like this").  Same
                    handlers/state as the old in-panel bars — only the POSITION
                    moved out of the query panel up into the header.  Three states:
                      • query panel open  → Back · Clear all · Load · Preview · Save
                      • collapsed (post-Load) → Back · Query · Export to · Preview · Excel · Save
                      • read-only saved report → Back · Preview · Download · Excel */}
                <div className="dp-title-row">
                  <FormTitle pageId="morreport" title={pageCaption?.title}
                             loadMs={retrieveMs} loading={retrieving}
                             auditFormName="Ad Hoc Reporting" />
                  <div className="dp-title-actions" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <button className="ca-btn" onClick={backToList} title="Back to the saved-reports list">
                      <span className="tb-i tb-i-grey">↩︎</span> Back to list
                    </button>

                    {/* ── Query panel OPEN (inline) ── */}
                    {!readOnly && !panelCollapsed && (
                      <>
                        <button className="ca-btn" onClick={clearAll} title="Clear every filter and start over">
                          <span aria-hidden="true">🧹</span> Clear all
                        </button>
                        <button
                          className="ca-btn ca-btn-solid"
                          onClick={() => handleRetrieve()}
                          disabled={retrieving}
                          title={`Run the query (no field is mandatory) (${formatCombo('alt+l')})`}
                        >
                          {retrieving ? <><LoaderSymbol size={14} /> Loading…</> : <><span aria-hidden="true">⬇️</span> Load</>}
                        </button>
                        {/* No Preview here — previewing before Load produces an
                            empty PDF (the grid isn't populated yet).  Preview
                            appears only AFTER Load, in the collapsed state below.
                            (User request 2026-07-04: "remove preview from here".) */}
                        {!panelAsPopup && perm.create && (
                          <button
                            className="ca-btn"
                            onClick={handleSave}
                            disabled={saving}
                            title={(savedReportId ? `Update saved report ${savedReportId}` : 'Save as a new MOR Adhoc Report') + ` (${formatCombo('mod+s')})`}
                          >
                            {saving
                              ? <><LoaderSymbol size={14} /> Saving…</>
                              : savedReportId
                                ? <><span className="tb-i">💾</span> Update {savedReportId}</>
                                : <><span className="tb-i">💾</span> Save</>}
                          </button>
                        )}
                      </>
                    )}

                    {/* ── Query panel COLLAPSED (after Load) ── */}
                    {panelCollapsed && !readOnly && (
                      <>
                        <button
                          className="ca-btn"
                          onClick={() => { setPanelCollapsed(false); setPanelAsPopup(true); }}
                          title="Open the query panel in a popup to change filters and re-run"
                        >
                          <span aria-hidden="true">🔍</span> Query
                        </button>
                        <button
                          className="ca-btn"
                          onClick={handleExportGrid}
                          disabled={exportingGrid}
                          title={`Export the Sample + QC results to an .xlsx with two sheets (${formatCombo('alt+x')})`}
                        >
                          {exportingGrid ? <><LoaderSymbol size={14} /> Exporting…</> : <><span className="tb-i tb-i-teal">📤</span> Export to</>}
                        </button>
                        {(() => {
                          const isBusy = exporting || lsiExporting || wsExporting || previewLoading;
                          const isExcelReport = selectedTemplate === TPL_RC_MONTHLY
                                             || selectedTemplate === TPL_LSI_REPORT
                                             || selectedTemplate === TPL_WATER_STABILITY;
                          const rcMonthlyDateMissing =
                            selectedTemplate === TPL_RC_MONTHLY
                            && !(dateCollectedFrom || dateCollectedTo
                              || dateReceivedFrom || dateReceivedTo
                              || dateAnalyzedFrom || dateAnalyzedTo
                              || (dateRange && dateRange !== 'All'));
                          const lsiDateMissing =
                            selectedTemplate === TPL_LSI_REPORT && !dateRangeReadyOrChip;
                          const wsDateMissing =
                            selectedTemplate === TPL_WATER_STABILITY && !dateRangeReadyOrChip;
                          const noTemplate = !selectedTemplate;
                          const disabled   = isBusy || noTemplate || rcMonthlyDateMissing || lsiDateMissing || wsDateMissing;
                          const titleText = noTemplate
                            ? 'Pick a Report from the dropdown first.'
                            : rcMonthlyDateMissing
                              ? 'RC Monthly Report needs at least one date range.'
                              : lsiDateMissing
                                ? 'LSI Report requires a Date Range or Date Collected window.'
                                : wsDateMissing
                                  ? 'CCPP Report requires a Date Range or Date Collected window.'
                                : isExcelReport
                                  ? 'Download the selected Excel report'
                                  : 'Preview the selected bench report as PDF';
                          return (
                            <>
                              <button
                                className="ca-btn"
                                onClick={() => handlePreviewReport(selectedTemplate)}
                                disabled={disabled}
                                title={titleText}
                              >
                                {isBusy ? <><LoaderSymbol size={14} /> Generating…</> : <><span aria-hidden="true">👁</span> Preview</>}
                              </button>
                              {!isExcelReport && (
                                <button
                                  className="ca-btn"
                                  onClick={() => handlePreviewReport(selectedTemplate, { excel: true })}
                                  disabled={disabled}
                                  title={noTemplate ? 'Pick a Report from the dropdown first.' : 'Download the selected report as Excel (.xlsx)'}
                                >
                                  {isBusy ? <><LoaderSymbol size={14} /> Generating…</> : <><span aria-hidden="true">📊</span> Excel</>}
                                </button>
                              )}
                            </>
                          );
                        })()}
                        {perm.create && (
                        <button
                          className="ca-btn ca-btn-solid"
                          onClick={handleSave}
                          disabled={saving}
                          title={(savedReportId ? `Update saved report ${savedReportId}` : 'Save as a new MOR Adhoc Report') + ` (${formatCombo('mod+s')})`}
                        >
                          {saving
                            ? <><LoaderSymbol size={14} /> Saving…</>
                            : savedReportId
                              ? <><span className="tb-i">💾</span> Update {savedReportId}</>
                              : <><span className="tb-i">💾</span> Save</>}
                        </button>
                        )}
                      </>
                    )}

                    {/* ── READ-ONLY saved report ── */}
                    {readOnly && viewedReport && (
                      <>
                        <button
                          className="ca-btn ca-btn-solid"
                          onClick={() => handlePreviewReport(viewedReport.reportTemplateName ?? '')}
                          disabled={!viewedReport.reportTemplateName || exporting || lsiExporting || wsExporting || previewLoading}
                          title={(!viewedReport.reportTemplateName ? 'This saved report does not have a Report Template set' : 'Preview the saved report') + ` (${formatCombo('alt+p')})`}
                        >
                          {previewLoading ? <><LoaderSymbol size={14} /> Generating…</> : <><span aria-hidden="true">👁</span> Preview</>}
                        </button>
                        <button
                          className="ca-btn"
                          onClick={() => handlePreviewReport(viewedReport.reportTemplateName ?? '', { download: true })}
                          disabled={!viewedReport.reportTemplateName || exporting || lsiExporting || wsExporting || previewLoading}
                          title={!viewedReport.reportTemplateName ? 'This saved report does not have a Report Template set' : 'Download the report as a PDF'}
                        >
                          {previewLoading ? <><LoaderSymbol size={14} /> Generating…</> : <><span aria-hidden="true">⬇️</span> Download</>}
                        </button>
                        {viewedReport.reportTemplateName
                          && viewedReport.reportTemplateName !== TPL_RC_MONTHLY
                          && viewedReport.reportTemplateName !== TPL_LSI_REPORT
                          && viewedReport.reportTemplateName !== TPL_WATER_STABILITY && (
                          <button
                            className="ca-btn"
                            onClick={() => handlePreviewReport(viewedReport.reportTemplateName ?? '', { excel: true })}
                            disabled={exporting || lsiExporting || wsExporting || previewLoading}
                            title="Download the saved report as Excel (.xlsx)"
                          >
                            {previewLoading ? <><LoaderSymbol size={14} /> Generating…</> : <><span aria-hidden="true">📊</span> Excel</>}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="dp-subtitle">
                  {retrieved ? `${retrieveRows.length.toLocaleString()} matching rows · ` : 'New report · '}
                  {pageCaption?.subtitle ?? 'Pick filters then click Retrieve. The Regulatory Compliance Category and SubCategory filters are linked to the Settings tables.'}
                </div>
              </>
            ) : (
              <>
                <div className="dp-title-row">
                  <FormTitle pageId="morreport" loadMs={listMs} loading={loadingList}
                             auditFormName="Ad Hoc Reporting" />
                  {/* New + Refresh moved INTO the toolbar 2026-07-06 (BO UI
                      Standard §6: "New always lives in the toolbar — never a
                      pagehead primary"; prototype screenAdhoc note: this screen
                      was missed in the 2026-07-04 unification pass). */}
                </div>
                {/* List sub-caption — restores the "Read-only · N saved reports"
                    line that sits under the title (user report 2026-07-04: "this
                    is not added").  Count is the full saved-report set. */}
                <div className="dp-subtitle mlv-subcap">
                  Read-only · {reports.length} saved report{reports.length === 1 ? '' : 's'} · Every Ad Hoc report you have generated (MOR IDs).
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contextual guidance strip (shared <StepHint>, BO UI Standard §5) —
            narrates the Ad Hoc workflow: Select report → Load → Preview → Save.
            Added 2026-07-04 for parity with the other reporting forms. */}
        {mode === 'detail' && !readOnly && (
          <StepHint tone={retrieved ? 'complete' : 'guidance'}>
            {!selectedTemplate
              ? <><b>Step 1 · Select a report.</b> Choose a <b>Report</b> template and any filters, then click <b>Load</b>.</>
              : !retrieved
                ? <><b>Step 2 · Load.</b> Click <b>Load</b> to run the query, then <b>Preview</b> the report.</>
                : <><b>Step 3 · Preview &amp; Save.</b> <b>Preview</b> the report (required), then <b>Save</b> it to your saved-reports list.</>}
          </StepHint>
        )}
        {/* Top-bar workflow stepper (shared <StepIsland> via TopbarStepper) —
            Select report → Select fields → Load → Preview → Save (prototype
            parity, user request 2026-07-04).  Computed live from the page state. */}
        {mode === 'detail' && !readOnly && (
          <TopbarStepper steps={((): IslandStep[] => [
            { id: 'report',  label: 'Select report', state: selectedTemplate ? 'done' : 'active' },
            { id: 'fields',  label: 'Select fields',  state: !selectedTemplate ? 'todo' : (retrieved ? 'done' : 'active') },
            { id: 'load',    label: 'Load',           state: retrieved ? 'done' : 'todo' },
            { id: 'preview', label: 'Preview',        state: !retrieved ? 'todo' : (savedId ? 'done' : 'active') },
            { id: 'save',    label: 'Save',           state: savedId ? 'done' : 'todo' },
          ])()} />
        )}
        {/* ─── LIST MODE — toolbar + search sit ON the grey page (BO UI
            Standard v2); only the grid card below is white. ──────────────── */}
        {mode === 'list' && (
          <div className="bl-grid-area">
              {/* List-view guidance banner (prototype parity, user request 2026-07-04).
                  Rendered INSIDE .bl-grid-area so it shares the 20px inset with the
                  toolbar / search / grid below — aligned, not full-bleed to the page
                  edge (alignment fix, user 2026-07-10). */}
              <StepHint tone="guidance">
                <b>Ad Hoc Reporting.</b> This is the saved-report history. Click <b>New</b> to
                build a report, or use a row's <b>PDF / Excel</b> icon to download an existing one.
              </StepHint>
              {/* Toolbar on its OWN full-width row — canonical order (BO UI
                  Standard §6, unified 2026-07-06 per prototype screenAdhoc):
                  Date Range · | · New (the ONE primary) · Refresh (via the
                  standard AppToolbar slot) · Export to (acts on the ticked
                  rows — enabled once ≥1 report is ticked). */}
              <div style={{ margin: '0 0 8px' }}>
                <AppToolbar
                  onRefresh={loadList}
                  refreshing={loadingList}
                  left={
                    <>
                      {/* Date Range — matches the Custom Reporting toolbar
                          pattern; filters the saved-report list by createdDate. */}
                      <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                        value={TRACKING_RANGES.find(r => r.label === listDateRange)?.days ?? null}
                        onChange={(d) => setListDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as ListDateRangeKey)} />
                      {perm.create && (
                      <button className="ca-btn" onClick={openNewReport}
                              title={`Build a new Ad Hoc report (${formatCombo('alt+n')})`}>
                        <span className="tb-i tb-i-green" aria-hidden="true">＋</span> New
                      </button>
                      )}
                    </>
                  }
                  right={
                    <button className="ca-btn"
                            disabled={listSel.size === 0 || rowDownloadKey !== null}
                            onClick={() => void exportSelectedReports()}
                            title={(listSel.size === 0
                              ? 'Tick one or more reports first'
                              : `Download ${listSel.size} ticked report${listSel.size === 1 ? '' : 's'}`) + ` (${formatCombo('alt+x')})`}>
                      <span className="tb-i tb-i-teal" aria-hidden="true">📤</span> Export to
                      {listSel.size > 0 ? ` (${listSel.size})` : ''}
                    </button>
                  }
                />
              </div>

              {/* Search row — shared .list-search-row on the grey page, never inside the card. */}
              <div className="list-search-row">
                <div className="list-search">
                  <span aria-hidden="true">🔍</span>
                  <input
                    type="text"
                    value={listSearch}
                    onChange={e => setListSearch(e.target.value)}
                    placeholder="Search report ID, name, or created-by…"
                  />
                  {listSearch && (
                    <span className="list-search-clear" onClick={() => setListSearch('')}>×</span>
                  )}
                </div>
              </div>

            {/* ONE white grid card — grid + its single footer only. */}
            <div className="bl-card bl-card-flex">

              {(() => {
                // FB 5/31/2026 — Apply Date Range + Search filters client-side to
                // the saved-reports list.  Date Range measures createdDate; Search
                // matches across ID, Name and Created-By.  <AppDataGrid> layers the
                // Excel-style per-column popup filters on top of these (and owns the
                // sort, footer, pagination and load-time — the shared grid, §18.6).
                const months: Record<ListDateRangeKey, number> = {
                  '1M': 1, '3M': 3, '6M': 6, '1Y': 12, '2Y': 24, '5Y': 60, 'All': 0,
                };
                let cutoff: number | null = null;
                if (listDateRange !== 'All') {
                  const c = new Date();
                  c.setMonth(c.getMonth() - months[listDateRange]);
                  cutoff = c.getTime();
                }
                const needle = listSearch.trim().toLowerCase();
                const filtered = reports.filter(r => {
                  if (cutoff !== null) {
                    const t = r.createdDate ? new Date(r.createdDate).getTime() : 0;
                    if (!t || t < cutoff) return false;
                  }
                  if (needle) {
                    const hit =
                      (r.reportId   ?? '').toLowerCase().includes(needle) ||
                      (r.reportName ?? '').toLowerCase().includes(needle) ||
                      (r.createdBy  ?? '').toLowerCase().includes(needle);
                    if (!hit) return false;
                  }
                  return true;
                });

                const listCols: AppColumn<MorReport>[] = [
                  {
                    field: 'reportId', header: 'Report ID', width: 180,
                    text: (r) => r.reportId ?? '',
                    // Click the Report ID → opens the saved report in read-only mode.
                    render: (r) => (
                      <a
                        href="#"
                        onClick={e => { e.preventDefault(); void handleViewReport(r.id); }}
                        style={{ color: 'var(--brand-accent-hover, var(--ai-orange-dark))', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {r.reportId}
                      </a>
                    ),
                  },
                  {
                    field: 'pdf', header: 'PDF', width: 64, align: 'center',
                    sortable: false, filterable: false,
                    render: (r) => {
                      // LSI and RC Monthly are Excel-only on the backend (there's
                      // no PDF), so the PDF download is DISABLED (greyed, not
                      // clickable) for those two — every other report type keeps
                      // its active PDF button.  (User request 2026-06-26.)
                      const rn = (r.reportName ?? '').trim().toLowerCase();
                      // Excel-only reports (no PDF on the backend): LSI, RC Monthly,
                      // and CCPP (incl. its former "Water Stability Report" name) —
                      // PDF is DISABLED/greyed, mirroring the LSI behaviour.
                      const noPdf = rn === 'lsi report' || rn === 'rc monthly report'
                        || rn === 'ccpp report' || rn === 'water stability report';
                      const busy = rowDownloadKey === `${r.id}:pdf`;
                      const off  = noPdf || busy;
                      return (
                        <button
                          onClick={() => { if (!noPdf) void downloadSavedReport(r.id, false); }}
                          disabled={off}
                          title={noPdf
                            ? 'PDF is not available for this report type — use the Excel download.'
                            : `Download ${r.reportId} as PDF`}
                          style={{ background: 'transparent', border: 'none', cursor: noPdf ? 'not-allowed' : busy ? 'wait' : 'pointer', fontSize: 16, padding: '0 6px', lineHeight: 1, opacity: noPdf ? 0.4 : 1 }}
                        >
                          {/* PDF row download — orange download arrow (user request
                              2026-07-04: keep the download-icon style). */}
                          <BusyIcon busy={busy} icon="ti-download"
                            style={{ color: off ? 'var(--ai-muted)' : 'var(--brand-accent, var(--ai-orange))' }} />
                        </button>
                      );
                    },
                  },
                  {
                    field: 'excel', header: 'Excel', width: 64, align: 'center',
                    sortable: false, filterable: false,
                    render: (r) => (
                      <button
                        onClick={() => void downloadSavedReport(r.id, true)}
                        disabled={rowDownloadKey === `${r.id}:xlsx`}
                        title={`Download ${r.reportId} as Excel`}
                        style={{ background: 'transparent', border: 'none', cursor: rowDownloadKey === `${r.id}:xlsx` ? 'wait' : 'pointer', fontSize: 16, padding: '0 6px', lineHeight: 1 }}
                      >
                        {/* Excel row download — green download arrow. */}
                        <BusyIcon busy={rowDownloadKey === `${r.id}:xlsx`} icon="ti-download"
                          style={{ color: 'var(--ai-green)' }} />
                      </button>
                    ),
                  },
                  { field: 'reportName', header: 'Report Name', width: 320, text: (r) => r.reportName ?? '' },
                  { field: 'createdBy', header: 'Created By', width: 200, text: (r) => r.createdBy ?? '' },
                  { field: 'createdDate', header: 'Created Date', width: 180, type: 'datetime' },
                  {
                    field: '_actions', header: '', width: 56, align: 'center',
                    sortable: false, filterable: false,
                    render: (r) => (
                      <button
                        onClick={() => handleDeleteReport(r.id, r.reportId)}
                        disabled={deletingId === r.id}
                        title={`Delete ${r.reportId}`}
                        style={{ background: 'transparent', border: 'none', color: deletingId === r.id ? 'var(--ai-muted)' : 'var(--ai-red)', cursor: deletingId === r.id ? 'wait' : 'pointer', fontSize: 15, padding: '0 6px', lineHeight: 1 }}
                      >
                        <BusyIcon busy={deletingId === r.id} icon="ti-trash" />
                      </button>
                    ),
                  },
                ];

                return (
                  <AppDataGrid<MorReport>
                    rows={filtered}
                    columns={listCols}
                    getRowId={(r) => r.id}
                    loading={loadingList}
                    loadMs={listMs}
                    selectable
                    selectedIds={listSel}
                    onSelectionChange={setListSel}
                    emptyText={reports.length > 0
                      ? 'No saved reports match the current filters.'
                      : 'No saved Adhoc reports yet.'}
                  />
                );
              })()}
            </div>
          </div>
        )}

        {/* ─── DETAIL / QUERY PANEL MODE ────────────────────────────────── */}
        {mode === 'detail' && (
          <div className="bl-grid-area" style={{ overflowY: 'auto' }}>
            <div className="mor-qp">
              {/* Slim in-panel toolbar — the Date Range filter (query panel
                  open) + the read-only "Viewing" info strip only.  ALL action
                  buttons (Back · Clear all · Load · Preview · Save · Query ·
                  Export · Download · Excel) moved UP to the header's
                  dp-title-actions so they sit at the TOP-RIGHT of the title row
                  (prototype parity, user request 2026-07-04). */}
              {((!readOnly && !panelCollapsed) || loadingLookups) && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 6 }}>
                  {/* Date Range — ONLY while the query panel is open (before
                      Retrieve).  Once collapsed the range is baked into the
                      visible data; re-open via the header Query button. */}
                  {!readOnly && !panelCollapsed && (
                    <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                      value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                      onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                  )}
                  {/* Orange "Viewing … read-only" banner removed (user request 2026-09-23). */}
                  {loadingLookups && (
                    <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
                      <LoaderSymbol size={14} /> Loading lookups…
                    </span>
                  )}
                </div>
              )}

              {/* ── FB2 — Date Range quick-pick ────────────────────────────────
                  Same dropdown the other list pages use (1M / 3M / 6M / 1Y /
                  2Y / 5Y / All).  Picking a range:
                    • populates Date Received From / To with that window
                    • auto-routes Retrieve to BTLIMSDC historic when the range
                      reaches back >= 1 year (older data isn't in the snapshot).
                  No separate Historic checkbox — the date range drives source. */}
              {/* Date Range row removed 2026-05-31 — moved to the top toolbar
                  to match the Custom Reporting layout the user asked for.
                  See `dp-title-actions` above; the chip there writes into
                  Date Received From / To when the user picks a range. */}

              {/* ── 2-COLUMN GOLIVE LAYOUT ───────────────────────────────────
                  Field order matches the BTLIMS GoLive XAF MORAdhocReporting
                  detail view (user-supplied screenshot 2026-05-26).  Compact
                  spacing + cream input backgrounds to mirror the DevExpress
                  chrome the client is used to.
                  Read-only mode: pointer-events: none on the grid wrapper +
                  reduced opacity so saved reports show their selections but
                  can't be edited.
                  Two display modes:
                    panelAsPopup = false  → inline at its normal position
                    panelAsPopup = true   → centred modal overlay over the grid
                  The "Query" button after Retrieve sets panelAsPopup = true. */}
              {panelAsPopup && (
                <div
                  onClick={() => { setPanelCollapsed(true); setPanelAsPopup(false); }}
                  style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(15, 23, 42, 0.45)',
                    zIndex: 900,
                  }}
                />
              )}
              {/* Show the query form when the panel is open OR whenever a saved
                  report is being VIEWED read-only.  Read-only auto-retrieve sets
                  panelCollapsed=true (so the grid shows), but the saved report's
                  DETAILS — which Report, which filters, which date ranges it was
                  built with — must still be visible (greyed, non-editable via the
                  pointerEvents:none + opacity styling below).  Without this the
                  read-only view showed only the results grid and the user could
                  not see the report's configuration (user report 2026-07-04:
                  "readonly details not showing"). */}
              {(!panelCollapsed || readOnly) && (<div
                style={panelAsPopup ? {
                  position: 'fixed',
                  top: '50%', left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 901,
                  width: 'min(96vw, 1200px)',
                  maxHeight: '92vh', overflowY: 'auto',
                  background: 'var(--ai-card)',
                  borderRadius: 10,
                  boxShadow: '0 30px 80px rgba(0,0,0,0.30)',
                  border: '1px solid var(--ai-line)',
                  padding: '12px 18px 18px',
                } : undefined}
              >
              {panelAsPopup && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 10, paddingBottom: 8,
                  borderBottom: '1px solid var(--ai-line)',
                }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ai-ink)' }}>
                    <span aria-hidden="true">🔍</span> Query Panel
                  </span>
                  {/* Date Range chip in the panel header's top-right corner
                      (user feedback 2026-05-31: "like this date range" — same
                      chip style as the standard toolbar one: calendar icon +
                      'Date Range' label + orange selected-value pill).  Same
                      `dateRange` state as the rest of the page so changing it
                      here drives Retrieve, historic routing, the picker
                      min/max, and the RC Monthly / LSI date window. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
                    {!readOnly && (
                      <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                        value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                        onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                    )}
                    <button
                      onClick={() => { setPanelCollapsed(true); setPanelAsPopup(false); }}
                      title="Close (keeps the current results)"
                      style={{
                        border: 'none', background: 'transparent',
                        color: 'var(--ai-muted)', cursor: 'pointer',
                        fontSize: 22, lineHeight: 1, padding: '2px 8px',
                      }}
                    >×</button>
                  </div>
                </div>
              )}
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px',
                background: 'var(--ai-card)', border: '1px solid var(--ai-line)', borderRadius: 10,
                boxShadow: 'var(--ai-shadow-card)',
                padding: '8px 12px',
                pointerEvents: readOnly ? 'none' : 'auto',
                opacity:       readOnly ? 0.92 : 1,
              }}>
                {/* ── LEFT COL ROW 1 — Report ID ── */}
                <div className="mor-field">
                  <label>Report ID</label>
                  <input
                    type="text" readOnly disabled
                    value={savedReportId ?? 'Autogenerate'}
                    style={{
                      width: '100%', padding: '5px 8px', fontSize: 12,
                      border: '1px solid var(--ai-line)', borderRadius: 4,
                      /* token-exempt: GoLive cream input chrome (user 2026-05-26) */
                      background: '#FFFEF0', color: 'var(--ai-muted)',
                    }}
                  />
                </div>
                {/* ── RIGHT COL ROW 1 — Created Date ── */}
                <div className="mor-field">
                  <label>Created Date</label>
                  <input
                    type="text" readOnly disabled
                    value={fmtLocalDate(new Date().toISOString())}
                    style={{
                      width: '100%', padding: '5px 8px', fontSize: 12,
                      border: '1px solid var(--ai-line)', borderRadius: 4,
                      /* token-exempt: GoLive cream input chrome (user 2026-05-26) */
                      background: '#FFFEF0', color: 'var(--ai-muted)',
                    }}
                  />
                </div>

                {/* ── LEFT COL ROW 2 — Report (template) ──
                    Per user feedback 2026-05-27: RC Monthly + LSI live in
                    this dropdown alongside the PDF bench reports, so the
                    bottom toolbar only needs one Preview button.  When the
                    user picks an Excel option Preview downloads the XLSX;
                    when they pick a PDF template Preview opens the PDF. */}
                <div className="mor-field">
                  <label>Report <span style={{ color: 'var(--ai-red)', fontWeight: 700 }} title="Required">*</span></label>
                  <select
                    value={selectedTemplate}
                    onChange={e => setSelectedTemplate(e.target.value)}
                    disabled={false}
                    style={{
                      width: '100%', padding: '3px 6px', fontSize: 12,
                      borderRadius: 3,
                      // Required-yet-empty = the standard yellow tint + amber
                      // border (BO UI Standard §10 / prototype .adform) until a
                      // report is picked; then the GoLive cream input chrome
                      // (token-exempt: user 2026-05-26).
                      border: selectedTemplate
                        ? '1px solid var(--ai-line)'
                        : '1px solid var(--ai-required-line)',
                      background: selectedTemplate ? '#FFFEF0' : 'var(--ai-required-bg)',
                    }}
                  >
                    <option value="">— Select a report —</option>
                    {/* User feedback 2026-05-30: no required-column annotations,
                        no optgroups — just one flat alphabetical list of
                        every available report.  The Preview handler still
                        validates the per-template prerequisites and shows
                        a clear alert if anything's missing. */}
                    {(() => {
                      // Hard-coded base set — guarantees the four standard
                      // report names always show as soon as the form opens,
                      // regardless of whether the templates API has finished
                      // (user feedback 2026-05-30: "while open the form show
                      // four report name").  Bench Results / Bench QC Report
                      // are normally returned by the templates endpoint too;
                      // if they are, we dedupe on `name`.
                      // LSI + CCPP are NOT hardcoded here any more — they come from
                      // tbl_Public_CustomReportDesignerDetails (Active=1) per
                      // deployment (table-driven visibility, 2026-10-06), so a lab
                      // shows only the index report it has turned on.
                      const baseSet = [
                        { name: TPL_RC_MONTHLY,             displayName: 'RC Monthly Report' },
                        { name: 'QCBatchResult_PeerReview', displayName: 'Bench Results' },
                        { name: 'xrEnvTRRPQCComborptnew',   displayName: 'Bench QC Report' },
                      ];
                      const seen = new Set<string>();
                      const combined: { name: string; displayName: string }[] = [];
                      for (const t of [...baseSet, ...templates]) {
                        if (seen.has(t.name)) continue;
                        seen.add(t.name);
                        combined.push({ name: t.name, displayName: t.displayName ?? t.name });
                      }
                      // Stable, case-insensitive alphabetical sort.
                      combined.sort((a, b) => (a.displayName || '').localeCompare(
                        b.displayName || '', undefined, { sensitivity: 'base' }));
                      return combined.map(t => (
                        <option key={t.name} value={t.name}>{t.displayName}</option>
                      ));
                    })()}
                  </select>
                </div>
                {/* ── RIGHT COL ROW 2 — Created By ── */}
                <div className="mor-field">
                  <label>Created By</label>
                  <input
                    type="text" readOnly disabled value="Service"
                    style={{
                      width: '100%', padding: '5px 8px', fontSize: 12,
                      border: '1px solid var(--ai-line)', borderRadius: 4,
                      /* token-exempt: GoLive cream input chrome (user 2026-05-26) */
                      background: '#FFFEF0', color: 'var(--ai-muted)',
                    }}
                  />
                </div>

                {/* Date Range dropdown removed from inside the panel
                    2026-05-31 — user feedback "this date range no need must
                    based on the above date range".  The toolbar Date Range
                    chip (top-right of the page header) is the single source
                    of truth and drives:
                      • the Date Received window used by Retrieve
                      • the historic-data routing (>3M flips to BTLIMSDC)
                      • the min/max bounds of the From/To date pickers below
                      • RC Monthly + LSI report Date Collected window
                    Duplicating the dropdown here was confusing — the chip
                    above already shows the active selection and changing it
                    re-runs Retrieve immediately. */}

                {/* ── LEFT COL ROW 3 — QC Batch ID (PDF #5)
                    Picker shows QC Batch ID + Test + Method columns. */}
                <PickerField
                  label="QC Batch ID"
                  items={jobsBatches.batches}
                  keyFn={x => x.id}
                  nameFn={x => x.name}
                  columns={[
                    { header: 'QC Batch ID', value: x => x.name,         width: 130 },
                    { header: 'Test',        value: x => x.testName },
                    { header: 'Method',      value: x => x.methodNumber, width: 130 },
                  ]}
                  selected={filters.batchIds}
                  availableIds={availableNarrowed.batches}
                  onToggle={id => setFilters(f => ({ ...f, batchIds: toggleId(f.batchIds, id) }))}
                  onClear={() => clearFilter('batchIds')}
                />
                {/* ── RIGHT COL ROW 3 — Test (PDF #11)
                    Picker shows Test + Method + Matrix columns. */}
                <PickerField
                  label="Test" items={distinctTestNames}
                  keyFn={x => x.id} nameFn={x => x.testName}
                  columns={[
                    { header: 'Test',   value: x => x.testName },
                    { header: 'Method', value: x => x.methodNumber, width: 140 },
                    { header: 'Matrix', value: x => x.matrixName,   width: 110 },
                  ]}
                  selected={filters.testIds}
                  availableIds={availableNarrowed.tests}
                  onToggle={id => setFilters(f => ({ ...f, testIds: toggleId(f.testIds, id) }))}
                  onClear={() => clearFilter('testIds')}
                />

                {/* ── LEFT COL ROW 4 — Job ID ──
                    3-column picker matching GoLive: JobID | Client | ProjectID.
                    User feedback 2026-05-26 — "jobid need client and projectid". */}
                <PickerField
                  label="Job ID"
                  items={jobsBatches.jobs}
                  keyFn={x => x.id} nameFn={x => x.name}
                  columns={[
                    { header: 'JobID',     value: x => x.name },
                    { header: 'Client',    value: x => clientNameById.get(x.clientId ?? '') ?? '', width: 200 },
                    { header: 'ProjectID', value: x => x.projectId, width: 140 },
                  ]}
                  selected={filters.jobIds}
                  availableIds={availableNarrowed.jobs}
                  onToggle={id => setFilters(f => ({ ...f, jobIds: toggleId(f.jobIds, id) }))}
                  onClear={() => clearFilter('jobIds')}
                />
                {/* ── RIGHT COL ROW 4 — Method (PDF #12)
                    Picker shows Method + Test + Matrix columns. */}
                <PickerField
                  label="Method"
                  items={methodPickerRows}
                  keyFn={x => x.id} nameFn={x => x.name}
                  columns={[
                    { header: 'Method', value: x => x.name,     width: 140 },
                    { header: 'Test',   value: x => x.tests },
                    { header: 'Matrix', value: x => x.matrices, width: 120 },
                  ]}
                  selected={filters.methodNumbers}
                  availableIds={availableNarrowed.methods}
                  onToggle={id => setFilters(f => ({ ...f, methodNumbers: toggleId(f.methodNumbers, id) }))}
                  onClear={() => clearFilter('methodNumbers')}
                />

                {/* ── LEFT COL ROW 5 — Client Name (PDF #6, #7) — single col. */}
                {/* PDF feedback #7 — Client picker shows Client + Address columns */}
                <PickerField
                  label="Client" items={lookups?.clients ?? []}
                  keyFn={x => x.id} nameFn={x => x.name}
                  columns={[
                    { header: 'Client',  value: x => x.name },
                    { header: 'Address', value: x => x.address, width: 200 },
                  ]}
                  selected={filters.clientIds}
                  availableIds={availableNarrowed.clients}
                  onToggle={id => setFilters(f => ({ ...f, clientIds: toggleId(f.clientIds, id) }))}
                  onClear={() => clearFilter('clientIds')}
                />
                {/* ── RIGHT COL ROW 5 — Parameter (PDF #13)
                    Picker shows Parameter + Test + Method columns. */}
                <PickerField
                  label="Parameter" items={lookups?.parameters ?? []}
                  keyFn={x => x.id} nameFn={x => x.name}
                  columns={[
                    { header: 'Parameter', value: x => x.name },
                    { header: 'Test',      value: x => x.tests },
                    { header: 'Method',    value: x => x.methods, width: 140 },
                  ]}
                  selected={filters.parameterIds}
                  availableIds={availableNarrowed.parameters}
                  onToggle={id => setFilters(f => ({ ...f, parameterIds: toggleId(f.parameterIds, id) }))}
                  onClear={() => clearFilter('parameterIds')}
                />

                {/* ── LEFT COL ROW 6 — Project ID (PDF #8)
                    Picker shows ProjectID + Client columns.  Deduped: ONE row
                    per distinct ProjectID code × Client (user feedback
                    2026-06-25 — the same code, e.g. "SEWPP Plant Daily",
                    repeated once per underlying weekday project).  Selecting a
                    code toggles every project GUID that carries it into
                    filters.projectIds, staying in sync with the Project Name
                    picker below. */}
                <PickerField
                  label="Project ID" items={projectIdGroups}
                  keyFn={x => x.key} nameFn={x => x.code}
                  columns={[
                    { header: 'ProjectID', value: x => x.code },
                    { header: 'Client',    value: x => x.clientLabel, width: 200 },
                  ]}
                  selected={selectedProjectIdKeys}
                  availableIds={availableProjectIdKeys}
                  onToggle={toggleProjectIdGroup}
                  onClear={() => clearFilter('projectIds')}
                />
                {/* ── RIGHT COL ROW 6 — Date Analyzed (PDF #14 — new) ── */}
                {/* Calendar pickers honour the Date Range chip:
                    when chip = 3M, dates older than 3 months ago are disabled
                    in the native picker (user feedback 2026-05-31:
                    "this is 3m previous also need to disabled"). */}
                <DateRangeField
                  label="Date Analyzed"
                  from={dateAnalyzedFrom} to={dateAnalyzedTo}
                  onChange={(f, t) => { setDateAnalyzedFrom(f); setDateAnalyzedTo(t); }}
                  min={rangePickerMin}
                  max={rangePickerMax}
                />

                {/* ── LEFT COL ROW 7 — Project Name (PDF #9)
                    Deduped: ONE row per distinct ProjectName (user feedback
                    2026-05-29 — "project name have lot of duplicate records").
                    Selecting a name toggles every project GUID that shares it
                    into filters.projectIds, so it stays in sync with the
                    Project ID picker above.  ProjectID column shows the
                    distinct codes that roll up into the name. */}
                <PickerField
                  label="Project Name" items={projectNameGroups}
                  keyFn={x => x.key} nameFn={x => x.name}
                  columns={[
                    { header: 'ProjectName', value: x => x.name },
                    { header: 'ProjectID',   value: x => x.idLabel, width: 160 },
                  ]}
                  selected={selectedProjectNameKeys}
                  availableIds={availableProjectNameKeys}
                  onToggle={toggleProjectNameGroup}
                  onClear={() => clearFilter('projectIds')}
                />
                {/* ── RIGHT COL ROW 7 — Date Received ── */}
                <DateRangeField
                  label="Date Received"
                  from={dateReceivedFrom} to={dateReceivedTo}
                  onChange={(f, t) => { setDateReceivedFrom(f); setDateReceivedTo(t); }}
                  min={rangePickerMin}
                  max={rangePickerMax}
                />

                {/* ── LEFT COL ROW 8 — Site Name (PDF #10)
                    Picker shows SiteName + ProjectID + Client columns.
                    NOTE: `availableIds` intentionally NOT passed — the
                    cross-faceted filter relies on SampleLogIn.SiteSource
                    which is NULL for the majority of rows in this DB, so
                    constraining to "sites seen in relations" hides almost
                    everything.  Always show all sites; the user filters
                    via the search box and per-column search. */}
                <PickerField
                  label="Site Name" items={lookups?.sampleSites ?? []}
                  keyFn={x => x.id} nameFn={x => x.name}
                  columns={[
                    // ProjectID column dropped 2026-06-01: SampleSites.ProjectID
                    // is NULL on most rows in this DB (no FK populated) so the
                    // column was always blank.  SiteName + Client is the useful
                    // pair the user actually picks from.
                    { header: 'SiteName',  value: x => x.name },
                    { header: 'Client',    value: x => clientNameById.get(x.clientId ?? '') ?? '', width: 200 },
                  ]}
                  selected={filters.siteIds}
                  onToggle={id => setFilters(f => ({ ...f, siteIds: toggleId(f.siteIds, id) }))}
                  onClear={() => clearFilter('siteIds')}
                  // Cross-faceted narrow (Pavithra drop 2026-06-01):
                  //   * Client / Project selected → siteNarrow (clientId/projectId match)
                  //   * Job / QC Batch selected   → siteAvailableIds (name match against relation index)
                  //   * No selection              → show every site (FB5 5/30/2026 behaviour preserved)
                  availableIds={siteAvailableIds}
                />
                {/* ── RIGHT COL ROW 8 — Date Collected (drives RC Monthly) ── */}
                <DateRangeField
                  label="Date Collected"
                  from={dateCollectedFrom} to={dateCollectedTo}
                  onChange={(f, t) => { setDateCollectedFrom(f); setDateCollectedTo(t); }}
                  min={rangePickerMin}
                  max={rangePickerMax}
                  /* No resetTo / hint — Date Collected now lays out identically
                     to Date Analyzed / Date Received per user feedback. */
                />
                {/* 2026-06-09: Date Collected is no longer mandatory for RC
                    Monthly — the report can be driven by any of Date Collected /
                    Date Received / Date Analyzed, so the old red "* Date
                    Collected From and To are mandatory..." caption was removed.
                    The Retrieve / Preview / Export actions require at least one
                    of the three date ranges instead. */}
              </div>

              {/* Regulatory Compliance Category / Sub Category pickers
                  intentionally hidden — user feedback 2026-05-26: only the
                  GoLive 16-field grid should be visible.  The filters are
                  still in state (always empty when hidden) so existing API
                  calls continue to work. */}

              {/* ── Actions moved to the header ── The in-panel action bar
                  (Load · Save · Clear all) was relocated 2026-07-04 to the
                  dp-title-actions group at the TOP-RIGHT of the title row
                  (prototype parity — user request "add the button position
                  like this").  Same handlers/state; only the position moved. */}
              </div>)}

              {/* (The previous inline "Show Query" bar was moved into the
                  top-right of the back-button header above; clicking it now
                  re-opens the panel as a centred modal overlay instead of
                  rendering it back in this slot.) */}

              {/* Report Templates / DOC / MDL toolbar removed per user
                  feedback 2026-05-26.  The Preview button now lives inline
                  next to the "Report" dropdown at the top of the form. */}

              {/* ── Loading overlay while the Retrieve query is in flight ──
                  The Retrieve SP can take a few seconds; show a clear centred
                  spinner in the results area (not just the small button icon)
                  so the user knows data is loading.  User feedback 2026-06-02. */}
              {retrieving && (
                <div className="mor-retrieve-loading" role="status" aria-live="polite">
                  <div className="mor-retrieve-loading-box">
                    <LoaderSymbol size={40} />
                    <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: 'var(--ai-ink)' }}>
                      Retrieving results…
                    </div>
                  </div>
                </div>
              )}

              {/* ── Results area with Sample / QC tabs (mirrors XAF detail view) ── */}
              {retrieved && (
                <div className="mor-results">
                  <div className="mor-results-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="mor-results-title">
                      <i className="ti ti-table" /> Results
                      {/* Load-time lives in ONE place — the TOP <FormTitle> chip
                          (§18.6) — so no "· N ms" duplicate here. */}
                      <span style={{ fontSize: 11, color: 'var(--ai-muted)', fontWeight: 500, marginLeft: 8 }}>
                        ({(serverSampleCount + serverQcCount).toLocaleString()} rows total)
                      </span>
                    </div>
                    {/* Report-name reference pill — user feedback 2026-05-31:
                        "in the grid right corner show the report name they
                        selected in the query panel just for the reference".
                        Read-only label so the user always knows which report
                        the current Retrieve is feeding.  Resolves the friendly
                        display name from the template list when available;
                        falls back to the raw template id. */}
                    {selectedTemplate && (() => {
                      const friendly =
                        selectedTemplate === TPL_RC_MONTHLY ? 'RC Monthly Report' :
                        selectedTemplate === TPL_LSI_REPORT ? 'LSI Report' :
                        selectedTemplate === TPL_WATER_STABILITY ? 'CCPP Report' :
                        (templates.find(t => t.name === selectedTemplate)?.displayName ?? selectedTemplate);
                      return (
                        <span
                          title={`Active report template: ${friendly}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontSize: 11, fontWeight: 600,
                            background: 'var(--ai-pill-info-bg)', color: 'var(--ai-pill-info-fg)',
                            border: '1px solid color-mix(in srgb, var(--ai-pill-info-fg) 25%, #fff)',
                            borderRadius: 999, padding: '3px 10px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <i className="ti ti-report" />
                          Report: {friendly}
                        </span>
                      );
                    })()}
                  </div>

                  {retrieveErr ? (
                    <div className="mor-results-empty" style={{ color: 'var(--ai-red)' }}>
                      <i className="ti ti-alert-triangle" style={{ marginRight: 6 }} />
                      {retrieveErr}
                    </div>
                  ) : retrieveRows.length === 0 ? (
                    <div className="mor-results-empty">
                      No matching rows for the current filters.
                    </div>
                  ) : (
                    <>
                      {retrieveTruncated && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            margin: '6px 8px 10px',
                            padding: '8px 12px',
                            background: 'var(--ai-amber-bg)',
                            border: '1px solid color-mix(in srgb, var(--ai-amber) 35%, #fff)',
                            borderRadius: 4,
                            color: 'var(--ai-amber)',
                            fontSize: 12,
                            lineHeight: 1.4,
                          }}
                        >
                          <i className="ti ti-alert-triangle" style={{ fontSize: 16 }} />
                          <span>
                            <strong>Showing the first {retrieveCap.toLocaleString()} rows.</strong>{' '}
                            Your query matched more data than the page cap.
                            Narrow filters (Date Collected, Client, Project, Test or QC Batch)
                            to see the full result and speed up Retrieve.
                          </span>
                        </div>
                      )}
                      <RetrieveTabs
                        rows={retrieveRows}
                        activeTab={activeTab}
                        onTabChange={switchTab}
                        sampleCount={serverSampleCount}
                        qcCount={serverQcCount}
                        onVisibleRowsChange={setGridVisibleRowIds}
                        /* 2026-06-01 (user feedback): When LSI Report is the
                           selected template, hide the QC Sample Results tab
                           entirely.  LSI is a samples-only report and the QC
                           tab adds no value — previously it just showed "0"
                           which confused users. */
                        hideQcTab={indexReportActive}
                      />
                      {/* ── Pagination bar at the BOTTOM of the grid ──
                          Layout matches the GoLive list pager:
                            Rows: [100 ▾]   « Prev   Page 1 / N   Next »
                          User feedback 2026-05-30 (image): pagination + a
                          page-size dropdown at the bottom, not the top. */}
                      {(() => {
                        const total = activeTab === 'sample' ? serverSampleCount : serverQcCount;
                        const curPage = activeTab === 'sample' ? samplePage : qcPage;
                        const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
                        // Numbered-page window — mirrors the shared <AppDataGrid> footer.
                        const win: (number | 'gap')[] = totalPages <= 7
                          ? Array.from({ length: totalPages }, (_, i) => i + 1)
                          : (() => {
                              const o: (number | 'gap')[] = [1];
                              const s = Math.max(2, curPage - 1), e = Math.min(totalPages - 1, curPage + 1);
                              if (s > 2) o.push('gap');
                              for (let p = s; p <= e; p++) o.push(p);
                              if (e < totalPages - 1) o.push('gap');
                              o.push(totalPages);
                              return o;
                            })();
                        return (
                          // Standard shared grid footer (BO UI Standard §9): Page X of N
                          // (total items) · numbered pager (current in orange) · Page size.
                          <div className="adg-footer">
                            <div className="adg-foot-left">
                              <span>Page {curPage} of {totalPages.toLocaleString()} ({total.toLocaleString()} item{total === 1 ? '' : 's'})</span>
                            </div>
                            <div className="adg-pages">
                              <button className="adg-pg-btn" disabled={curPage <= 1 || retrieving}
                                onClick={() => gotoServerPage(curPage - 1)} aria-label="Previous page">
                                <i className="ti ti-chevron-left" />
                              </button>
                              {win.map((p, idx) => p === 'gap'
                                ? <span key={`gap-${idx}`} className="adg-pg-ellipsis">…</span>
                                : <button key={p} className={`adg-pg-btn${p === curPage ? ' active' : ''}`}
                                    disabled={retrieving} onClick={() => gotoServerPage(p)}
                                    aria-current={p === curPage ? 'page' : undefined}>{p}</button>)}
                              <button className="adg-pg-btn" disabled={curPage >= totalPages || retrieving}
                                onClick={() => gotoServerPage(curPage + 1)} aria-label="Next page">
                                <i className="ti ti-chevron-right" />
                              </button>
                            </div>
                            <div className="adg-foot-right">
                              <span>Page size:</span>
                              <select className="adg-size-sel" value={PAGE_SIZE} disabled={retrieving}
                                onChange={e => { const next = parseInt(e.target.value, 10) || 100; setPageSize(next); setSamplePage(1); setQcPage(1); }}>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                                <option value={500}>500</option>
                              </select>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* View-saved modal removed 2026-05-27 — saved reports now open
          in the same detail page (read-only) so the user sees the full
          query panel + Retrieved Sample/QC tabs with their saved data. */}
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// DateRangeField — From → To inline range input pair used across the GoLive
// 2-column query panel (Date Analyzed / Date Received / Date Collected rows).
// Matches the label position + sizing of PickerField so the two columns align.
// ─────────────────────────────────────────────────────────────────────────────
interface DateRangeFieldProps {
  label:    string;
  from:     string;
  to:       string;
  onChange: (from: string, to: string) => void;
  hint?:    string;
  /** Optional "reset" target; when supplied a small reset link appears. */
  resetTo?: { from: string; to: string };
  /** Lower bound for the calendar picker (ISO yyyy-MM-dd).  Dates before
   *  this are disabled in the native picker.  Used to clamp the visible
   *  range to the Date Range chip's cutoff (e.g. "3M" → only allow dates
   *  in the last 3 months). */
  min?:     string;
  /** Upper bound for the calendar picker (default = today). */
  max?:     string;
}
function DateRangeField({ label, from, to, onChange, hint, resetTo, min, max }: DateRangeFieldProps) {
  return (
    <div className="mor-field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', minWidth: 0 }}>
        <input
          type="date" value={from}
          className={from ? '' : 'date-empty'}
          min={min} max={max}
          onChange={e => onChange(e.target.value, to)}
          style={{ flex: 1, minWidth: 0, padding: '3px 6px', fontSize: 12,
                   border: '1px solid var(--ai-line)', borderRadius: 3 }}
        />
        <i className="ti ti-arrow-right" style={{ color: 'var(--ai-muted)', fontSize: 11 }} />
        <input
          type="date" value={to}
          className={to ? '' : 'date-empty'}
          min={min} max={max}
          onChange={e => onChange(from, e.target.value)}
          style={{ flex: 1, minWidth: 0, padding: '3px 6px', fontSize: 12,
                   border: '1px solid var(--ai-line)', borderRadius: 3 }}
        />
        {hint && <span style={{ fontSize: 10, color: 'var(--ai-muted)', marginLeft: 4 }}>
          <LoaderSymbol size={14} />
        </span>}
        {(from || to) && (
          <button
            type="button"
            style={{
              background: 'transparent', border: 'none', color: 'var(--ai-red)',
              cursor: 'pointer', fontSize: 10, padding: '0 2px',
            }}
            onClick={() => onChange('', '')}
            title={`Clear ${label} filter`}
          >×</button>
        )}
        {resetTo && !from && !to && (
          <button
            type="button"
            style={{
              background: 'transparent', border: 'none', color: 'var(--ai-blue)',
              cursor: 'pointer', fontSize: 10, padding: '0 2px',
            }}
            onClick={() => onChange(resetTo.from, resetTo.to)}
            title="Reset to last full month"
          >↺</button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PickerField — closed-combobox multi-select.
//
// Renders as a single button showing "(All)" or "N selected".  Click opens a
// floating popover with the checkbox list (search at top, scroll, bulk actions).
// Closes on outside click / Escape / OK button.  Same UX as the BTLIMS XAF
// CheckedListBoxEditor that the original MORAdhocReporting screen uses.
// ─────────────────────────────────────────────────────────────────────────────
interface PickerFieldProps<T> {
  label:        string;
  items:        T[];
  keyFn:        (item: T) => string;
  nameFn:       (item: T) => string;
  selected:     Set<string>;
  onToggle:     (id: string) => void;
  onClear:      () => void;
  /** Cross-faceted narrowing: when set, only items whose id is in this set
   *  are shown in the picker (with a count chip "X of Y" reflecting the cut). */
  availableIds?: Set<string>;
  /** Optional secondary text shown next to each row (PDF feedback 2026-05-26:
   *  "QC Batch ID picker needs Test + Method columns").  When supplied, the
   *  picker dropdown becomes 2-column: name on the left, this string on the
   *  right (e.g. "Colilert · SM 9223 B").  Kept for back-compat. */
  subFn?:       (item: T) => string;
  /** Optional column header shown above the rows when subFn is supplied. */
  subHeader?:   string;
  /** Multi-column variant (PDF feedback 2026-05-26 #5–#13).  When supplied,
   *  the picker popover renders as a real table with gridlines, sticky
   *  header row, and each column showing its own value.  The first column
   *  is always the primary `nameFn` value.  Each extra column declares its
   *  own header label, value function, and (optional) min-width. */
  columns?:     {
    header: string;
    value:  (item: T) => string;
    width?: number;
  }[];
}

function PickerField<T>({
  label, items, keyFn, nameFn, selected, onToggle, onClear, availableIds,
  subFn, subHeader, columns,
}: PickerFieldProps<T>) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState('');
  const btnRef          = useRef<HTMLButtonElement>(null);
  const popRef          = useRef<HTMLDivElement>(null);
  const [pos, setPos]   = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 260 });

  // Position the popover under the trigger button
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + window.scrollY + 4,
      left: r.left + window.scrollX,
      width: Math.max(r.width, 260),
    });
  }, [open]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)
          && btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Narrow items to only the cross-facet "available" set (but always keep
  // already-selected items visible so the user never loses sight of their
  // own selections).
  const narrowed = useMemo(() => {
    if (!availableIds) return items;
    return items.filter(i => {
      const id = keyFn(i);
      return availableIds.has(id) || selected.has(id);
    });
  }, [items, availableIds, selected, keyFn]);

  // Per-column search terms (PDF feedback 2026-05-26 — picker should have a
  // search input under EACH column header, not just one global search box).
  // Map keyed by column header lowercase.
  const [colSearch, setColSearch] = useState<Record<string, string>>({});

  const filtered = useMemo(() => {
    let result = narrowed;
    // Apply global search (search box at the top).  Matches the primary name
    // OR any secondary column value, so e.g. typing "EWPP" in the Project Name
    // picker finds groups whose ProjectID column reads "EWPP Plant Daily" even
    // though the project NAME ("Monday", "Iron", …) never contains "EWPP".
    // User feedback 2026-05-30: "see these project not showing even 1".
    if (q.trim()) {
      const term = q.toLowerCase();
      result = result.filter(i => {
        if (nameFn(i).toLowerCase().includes(term)) return true;
        if (subFn && (subFn(i) || '').toLowerCase().includes(term)) return true;
        if (columns) {
          for (const c of columns) {
            if ((c.value(i) || '').toLowerCase().includes(term)) return true;
          }
        }
        return false;
      });
    }
    // Apply per-column search filters (PDF feedback — column-level search)
    if (columns) {
      for (const c of columns) {
        const cs = colSearch[c.header.toLowerCase()];
        if (cs && cs.trim()) {
          const t = cs.toLowerCase();
          result = result.filter(i => (c.value(i) || '').toLowerCase().includes(t));
        }
      }
    }
    return result;
  }, [q, narrowed, nameFn, subFn, columns, colSearch]);

  // Build a short preview of what's selected (first 2 names + "+N more")
  const previewText = useMemo(() => {
    if (selected.size === 0) return '(All)';
    const names = items.filter(i => selected.has(keyFn(i))).map(nameFn);
    if (names.length <= 2) return names.join(', ');
    return `${names[0]}, ${names[1]} +${names.length - 2} more`;
  }, [items, selected, keyFn, nameFn]);

  // Count chip removed per user feedback 2026-05-26 — the "X/Y" labels
  // were noisy; cross-faceted narrowing still happens (items not in
  // `availableIds` get hidden), the user just doesn't see the count.

  // ─── Column layout (lifted out of the JSX so we can use it to size the
  // popup to the data, not stretch it to the trigger button).  User
  // feedback 2026-05-26: "some lookup too much empty space make the popu
  // based on the data size".  We replace the old "1fr" name column with
  // an explicit pixel width so total table width is deterministic. ───
  type PickerCol = { header: string; value: (it: T) => string; width?: number };
  let pickerCols: PickerCol[] = columns ?? [];
  if (pickerCols.length === 0 && subFn) {
    pickerCols = [
      { header: 'Name',             value: nameFn },
      { header: subHeader ?? 'Sub', value: subFn!, width: 140 },
    ];
  } else if (pickerCols.length === 0) {
    pickerCols = [{ header: 'Name', value: nameFn }];
  } else {
    const first = pickerCols[0]!;
    pickerCols = [{ header: first.header, value: nameFn, width: first.width }, ...pickerCols.slice(1)];
  }
  // Default widths: first column gets 200px for multi-col / 260px for
  // single-col; trailing columns default to 160px.  Explicit widths from
  // the call-site always win.
  const isMultiCol = pickerCols.length > 1;
  const colWidthsPx = pickerCols.map((c, i) =>
    c.width ?? (isMultiCol ? (i === 0 ? 200 : 160) : 260)
  );
  const gridColsTpl = ['24px', ...colWidthsPx.map(w => `${w}px`)].join(' ');
  // Total table width = checkbox col + sum of data cols.  Popup width is
  // clamped to a sane min (260) and max (90vw via clamp).
  const tableWidthPx = 24 + colWidthsPx.reduce((a, b) => a + b, 0);
  const popupWidth   = Math.max(260, Math.min(tableWidthPx, Math.round(window.innerWidth * 0.9)));

  return (
    <div className="mor-field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', minWidth: 0 }}>
        <button
          ref={btnRef}
          type="button"
          className={`mor-combo${selected.size > 0 ? ' on' : ''}${open ? ' open' : ''}`}
          onClick={() => setOpen(o => !o)}
          // Never disable based on data load state — user feedback 2026-05-30:
          // "do not disable the controls when it dont have data".  When the
          // lookup list is still loading, the popup just shows "(loading…)"
          // text but the picker stays clickable.
          title={previewText}
          style={{ flex: 1, minWidth: 0 }}
        >
          <span className="mor-combo-text">{previewText}</span>
          <i className="ti ti-chevron-down mor-combo-chevron" />
        </button>
        {selected.size > 0 && (
          <button
            type="button"
            style={{
              background: 'transparent', border: 'none', color: 'var(--ai-red)',
              cursor: 'pointer', fontSize: 12, padding: '0 2px', lineHeight: 1,
            }}
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            title="Clear selection"
          >×</button>
        )}
      </div>

      {open && createPortal(
        <div
          ref={popRef}
          className="mor-combo-pop"
          style={{ top: pos.top, left: pos.left, width: popupWidth }}
        >
          <div className="mor-combo-pop-hdr">
            <span aria-hidden="true">🔍</span>
            <input
              autoFocus
              type="text"
              placeholder="Search…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            {q && <button className="mor-combo-clear-q" onClick={() => setQ('')}>×</button>}
          </div>
          {/* Action toolbar at the TOP of the picker (per user feedback
              2026-05-26 — was at the bottom).  Select all / Clear / OK
              now live up here right under the search box. */}
          <div className="mor-combo-pop-ftr" style={{ borderTop: 'none', borderBottom: '1px solid var(--ai-line-soft)' }}>
            <button
              type="button"
              className="mor-combo-ftr-btn"
              onClick={() => filtered.forEach(it => { const id = keyFn(it); if (!selected.has(id)) onToggle(id); })}
              disabled={filtered.length === 0}
            >Select all</button>
            <button
              type="button"
              className="mor-combo-ftr-btn red"
              onClick={onClear}
              disabled={selected.size === 0}
            >Clear</button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="mor-combo-ftr-ok"
              onClick={() => setOpen(false)}
            >OK</button>
          </div>
          {/* Multi-column table layout — gridlines + sticky header.
              Column widths + popup width are computed above so every row
              (header / search / data) uses the SAME explicit pixel grid,
              and the popup is sized to the data instead of stretching to
              the trigger button width. */}
          <div className="mor-picker-tbl-hdr" style={{ gridTemplateColumns: gridColsTpl }}>
            <span />
            {pickerCols.map(c => <span key={c.header}>{c.header}</span>)}
          </div>
          {/* Per-column search row — only on the multi-column variant. */}
          {isMultiCol && (
            <div className="mor-picker-tbl-search" style={{ gridTemplateColumns: gridColsTpl }}>
              <span />
              {pickerCols.map(c => (
                <input
                  key={c.header}
                  type="text"
                  value={colSearch[c.header.toLowerCase()] || ''}
                  onChange={e => setColSearch(s => ({
                    ...s,
                    [c.header.toLowerCase()]: e.target.value,
                  }))}
                  onClick={e => e.stopPropagation()}
                  placeholder=""
                />
              ))}
            </div>
          )}
          <div className="mor-combo-pop-list mor-picker-tbl-body">
            {filtered.length === 0 ? (
              <div className="mor-picker-empty">{items.length === 0 ? 'No options' : 'No matches'}</div>
            ) : filtered.map(it => {
              const id = keyFn(it);
              const on = selected.has(id);
              return (
                <label
                  key={id}
                  className={`mor-picker-tbl-row${on ? ' on' : ''}`}
                  style={{ gridTemplateColumns: gridColsTpl }}
                  title={pickerCols.map(c => c.value(it)).join('  ·  ')}
                >
                  <input type="checkbox" checked={on} onChange={() => onToggle(id)} />
                  {pickerCols.map((c, i) => (
                    <span key={i} className="mor-picker-tbl-cell">{c.value(it)}</span>
                  ))}
                </label>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RetrieveTabs — Sample / QC tabs mirroring the XAF MOR Adhoc detail view
// (same split BTLIMS's MORAdhocReporting.SampleResults vs QCResults uses).
// ─────────────────────────────────────────────────────────────────────────────
function RetrieveTabs({
  rows, activeTab, onTabChange, sampleCount, qcCount, onVisibleRowsChange,
  hideQcTab = false,
}: {
  rows: RetrieveRow[];
  activeTab: 'sample' | 'qc';
  onTabChange: (t: 'sample' | 'qc') => void;
  /** Total Sample row count from server (used for the tab badge). */
  sampleCount: number;
  /** Total QC row count from server. */
  qcCount: number;
  /** Forwarded to the inner grid — emits filtered+selected ids upward. */
  onVisibleRowsChange?: (ids: string[]) => void;
  /** 2026-06-01 (user feedback): When true, the QC Sample Results tab is
   *  not rendered at all.  Set by the LSI Report path — LSI is samples-only
   *  so the QC tab adds no value.  Caller must also ensure activeTab is
   *  'sample' before passing hideQcTab=true (else the page renders blank). */
  hideQcTab?: boolean;
}) {
  // With server-side pagination, `rows` already contains ONLY the active
  // tab's current page (max 200 rows).  We don't filter by IsQc here
  // anymore — that filter happened on the server.
  const current = rows;

  const tabBtn = (key: 'sample' | 'qc', label: string, n: number) => (
    <button
      key={key}
      type="button"
      onClick={() => onTabChange(key)}
      style={{
        // Active tab = the ONE brand accent (BO UI Standard — no per-form colours).
        background: activeTab === key ? 'var(--brand-accent, var(--ai-orange))' : 'var(--ai-card)',
        color:      activeTab === key ? '#fff'                                  : 'var(--ai-ink)',
        border:     '1px solid ' + (activeTab === key ? 'var(--brand-accent, var(--ai-orange))' : 'var(--ai-line)'),
        borderBottom: activeTab === key ? '1px solid var(--brand-accent, var(--ai-orange))' : '1px solid transparent',
        borderRadius: '6px 6px 0 0',
        padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      <i className={`ti ${key === 'sample' ? 'ti-flask' : 'ti-test-pipe-2'}`} />
      {label}
      <span style={{
        background: activeTab === key ? 'rgba(255,255,255,.25)' : 'var(--ai-pill-neutral-bg)',
        color:      activeTab === key ? '#fff'                  : 'var(--ai-pill-neutral-fg)',
        fontSize: 10, padding: '1px 7px', borderRadius: 8, fontWeight: 700,
      }}>{n.toLocaleString()}</span>
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--ai-line)', marginBottom: 6 }}>
        {tabBtn('sample', 'Sample Results', sampleCount)}
        {/* QC tab hidden for LSI (samples-only report) — see prop comment above. */}
        {!hideQcTab && tabBtn('qc', 'QC Sample Results', qcCount)}
      </div>
      <RetrieveResultsGrid rows={current} isQc={activeTab === 'qc'} onVisibleRowsChange={onVisibleRowsChange} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RetrieveResultsGrid — paginated 50-row preview of retrieved rows.
// QC tab adds a "QC Type" column; Sample tab adds Result + Status.
// ─────────────────────────────────────────────────────────────────────────────
function RetrieveResultsGrid({ rows, isQc, onVisibleRowsChange }: {
  rows: RetrieveRow[];
  isQc: boolean;
  // User feedback 2026-06-01: report Preview should honour the grid's
  // column filters + row selection.  The grid emits the surviving
  // sampleParameterIds whenever filters/selection change so the parent
  // can forward them to the report-preview endpoint.
  onVisibleRowsChange?: (ids: string[]) => void;
}) {
  // Inner pagination removed 2026-05-30 — server-side pagination above
  // owns Prev/Next/Page X of N now; the grid shows ALL rows it received.

  // Per-column Excel-style dropdown filters (user feedback 2026-05-30 — match
  // the GoLive BTLIMS grid: click a column header to open a checklist of the
  // column's distinct values with (All) / (Non blanks) / search / Clear / OK).
  // Each active entry is the SET of values allowed through; an absent key means
  // the column is unfiltered.  Filters apply to the FULL retrieved set BEFORE
  // pagination, so a filter spans every page.  Reset on a fresh result set or
  // when the tab flips.
  const [colFilters, setColFilters] = useState<Record<string, Set<string>>>({});
  // Per-column filter dropdown open/anchor handled internally by
  // <ColFilterButton variant="inline"> (same as Backlog / Subout / Analysis).

  // Click-to-sort (matches every other list grid — ↕ → ↑ → ↓ → ↕).  The
  // Status filter chip row was removed 2026-05-31 (user feedback: "no need
  // status filter") — the Status column now shows the QC pass/fail verdict
  // and is filtered via its Excel-style column dropdown like every other
  // column.  Column filters reset on a fresh result set / tab flip.
  // Default sort: JobID desc (newest jobs first), with SampleID as the
  // implicit tie-break so the same JobID never scatters and samples within
  // a JobID stay in their natural -NNN order.  User feedback 2026-06-01:
  // "jobid is desc not asc".  Composite is handled in applyByFormatted —
  // when the active sort key is jobId or sampleID we return "jobId|sampleID"
  // so the smart string comparator does both passes in one go.
  const sortApi = useSort<RetrieveRow>('jobId', 'desc');
  useEffect(() => { setColFilters({}); }, [rows, isQc]);

  // Row selection — every retrieved row starts selected; the header checkbox
  // toggles all rows (across every page), each row checkbox toggles itself.
  const allKeys = useMemo(() => rows.map(r => r.sampleParameterId), [rows]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(allKeys));
  useEffect(() => setSelected(new Set(rows.map(r => r.sampleParameterId))), [rows]);
  const allSelected  = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allKeys));
  const toggleRow = (id: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // ── Column descriptors — single source of truth for the header label, the
  // inline filter input AND the body cell value.  GoLive Sample Results order
  // (audited from XAF screenshot 2026-05-26):
  //   ClientName | ProjectID | JobID | QCBatchID | SysSampleCode | SampleName
  //   | SiteName | SampleMatrix | Test | Method | Parameter | Result | Units | Status
  // QC tab prepends "QC Type" and ends with Date Analyzed; Sample tab ends
  // with Date Collected + Date Received. ──
  type GridCol = {
    key:    string;
    header: string;
    value:  (r: RetrieveRow) => string;
    mono?:   boolean;
    nowrap?: boolean;
    qcType?: boolean;
    pill?:   boolean;   // render value as a coloured status pill
    link?:   boolean;   // render value in the blue identifier-link colour
  };
  const cols = useMemo<GridCol[]>(() => {
    // QC types whose Site Name should ALWAYS render blank (non-sample QC).
    // User feedback 5/30/2026 #4: "Non-sample-related QC Types should not
    // have Site Names" — Blanks / Standards / Spikes are not tied to a site.
    const blankSiteQcTypes = new Set([
      'Reagent Blank', 'Method Blank', 'Calibration Blank', 'Blank',
      'CCV', 'CCV Low', 'CCV Mid', 'CCV High',
      'CCB', 'ICV', 'ICB', 'LCS', 'LCS, Dup',
      'Blank Spike', 'Method Spike', 'MB Dup',
      'Standard',
    ]);
    if (isQc) {
      // User feedback 5/30/2026 #4 — QC Sample Result column order.
      // Fields currently sourced from the snapshot/SP:
      //   QCBatchID, SampleType(=QC Type), JobID, SysSampleCode, SampleName,
      //   SiteName, SampleMatrix, Test, Method, Parameter, Result, Units,
      //   DF, RL(=MRL), Qual.(=Qualifier), QCStatus(=Status), DateAnalyzed.
      // SpikeAmount / RPD / RPDCtrlLimit / %Rec / RecCtrlLimit are populated
      // only in the QC Combo report SP today — they render as empty cells
      // until the snapshot is extended (separate task).
      return [
        { key: 'qcBatchID',    header: 'QCBatchID',      value: r => r.qcBatchID ?? '', nowrap: true },
        { key: 'qcType',       header: 'SampleType',     value: r => r.qcTypeName ?? '', qcType: true },
        { key: 'jobId',        header: 'JobID',          value: r => r.jobId ?? '', nowrap: true },
        { key: 'sampleID',     header: 'SysSampleCode',  value: r => r.sampleID ?? '', mono: true, nowrap: true },
        { key: 'sampleName',   header: 'SampleName',     value: r => r.sampleName ?? '' },
        { key: 'siteName',     header: 'SiteName',       value: r => blankSiteQcTypes.has((r.qcTypeName ?? '').trim()) ? '' : (r.siteName ?? r.sampleLocation ?? '') },
        { key: 'sampleMatrix', header: 'SampleMatrix',   value: r => r.sampleMatrix ?? '' },
        { key: 'test',         header: 'Test',           value: r => r.test ?? '' },
        { key: 'method',       header: 'Method',         value: r => r.method ?? '' },
        { key: 'parameter',    header: 'Parameter',      value: r => r.parameter ?? '' },
        { key: 'result',       header: 'Result',         value: r => r.finalResult ?? r.result ?? '', mono: true },
        { key: 'units',        header: 'Units',          value: r => r.units ?? '' },
        { key: 'df',           header: 'DF',             value: r => r.df ?? '' },
        { key: 'rl',           header: 'RL',             value: r => r.mrl ?? '' },
        { key: 'spikeAmount',  header: 'SpikeAmount',    value: r => r.spikeAmount ?? '' },
        { key: 'rpd',          header: 'RPD',            value: r => r.rpd ?? '' },
        { key: 'rpdCtrlLimit', header: 'RPDCtrlLimit',   value: r => r.rpdCtrlLimit ?? '' },
        { key: 'pctRec',       header: '%Rec',           value: r => r.pctRec ?? '' },
        { key: 'recCtrlLimit', header: 'RecCtrlLimit',   value: r => r.recCtrlLimit ?? '' },
        { key: 'qual',         header: 'Qual.',          value: r => r.qualifier ?? '' },
        { key: 'qcStatus',     header: 'QCStatus',       value: r => r.status ?? '', pill: true },
        { key: 'dateAnalyzed', header: 'DateAnalyzed',   value: r => fmtLabDate(r.dateAnalyzed), nowrap: true },
      ];
    }
    // User feedback 5/30/2026 #3 — Sample Result column order.
    // Removed Status (workflow status no longer relevant on the data grid).
    // Added RL (MRL), UQL, MCL, DateAnalyzed.  MCL not yet sourced — empty
    // cell until the snapshot is extended (separate task).
    return [
      { key: 'jobId',         header: 'JobID',         value: r => r.jobId ?? '', nowrap: true },
      { key: 'sampleID',      header: 'SysSampleCode', value: r => r.sampleID ?? '', mono: true, nowrap: true },
      { key: 'qcBatchID',     header: 'QCBatchID',     value: r => r.qcBatchID ?? '', nowrap: true },
      { key: 'sampleName',    header: 'SampleName',    value: r => r.sampleName ?? '' },
      { key: 'siteName',      header: 'SiteName',      value: r => r.siteName ?? r.sampleLocation ?? '' },
      { key: 'sampleMatrix',  header: 'SampleMatrix',  value: r => r.sampleMatrix ?? '' },
      { key: 'test',          header: 'Test',          value: r => r.test ?? '' },
      { key: 'method',        header: 'Method',        value: r => r.method ?? '' },
      { key: 'parameter',     header: 'Parameter',     value: r => r.parameter ?? '' },
      { key: 'result',        header: 'Result',        value: r => r.finalResult ?? r.result ?? '', mono: true },
      { key: 'units',         header: 'Units',         value: r => r.units ?? '' },
      { key: 'df',            header: 'DF',            value: r => r.df ?? '' },
      { key: 'rl',            header: 'RL',            value: r => r.mrl ?? '' },
      // SQL (Sample Quantitation Limit) — user feedback 2026-05-31:
      // "Which extra Sample-tab columns should I add?" → SQL.  Sourced
      // from AI_MorAdhocSnapshot.SQL (47% populated on Sample rows).
      { key: 'sql',           header: 'SQL',           value: r => r.sql ?? '' },
      // MCL column reuses the snapshot's MDL value — user feedback
      // 2026-05-31: "MDL is MCL but the caption is MCL".  Data source =
      // SampleParameter.MDL, caption stays as MCL.
      { key: 'mcl',           header: 'MCL',           value: r => r.mdl ?? '' },
      // LOQ (Limit of Quantitation) — user feedback 2026-05-31.  Sourced
      // from AI_MorAdhocSnapshot.LOQ (28% populated).
      { key: 'loq',           header: 'LOQ',           value: r => r.loq ?? '' },
      { key: 'uql',           header: 'UQL',           value: r => r.uql ?? '' },
      { key: 'qual',          header: 'Qual.',         value: r => r.qualifier ?? '' },
      { key: 'dateAnalyzed',  header: 'DateAnalyzed',  value: r => fmtLabDate(r.dateAnalyzed), nowrap: true },
      // Analyst — user feedback 2026-05-31.  94% populated, already shown
      // on the Bench PDF; surface it on the grid too.
      { key: 'analyst',       header: 'Analyst',       value: r => r.analyst ?? '' },
      { key: 'dateReceived',  header: 'DateReceived',  value: r => fmtLabDate(r.dateReceived), nowrap: true },
      { key: 'dateCollected', header: 'DateCollected', value: r => fmtLabDate(r.dateCollected), nowrap: true },
      { key: 'projectId',     header: 'ProjectID',     value: r => r.projectId ?? '' },
      // Project Name — user feedback 2026-05-31 ("project name is
      // different not projectid").  Separate column from ProjectID.
      { key: 'projectName',   header: 'Project Name',  value: r => r.projectName ?? '' },
      { key: 'client',        header: 'Client',        value: r => r.client ?? '' },
    ];
  }, [isQc]);

  // Column model (client 2026-08-05: Ad Hoc Reporting detail grid) — Hide · Show
  // All · Column Chooser · drag-REORDER · RESIZE · persistence, same as every other
  // list grid. Per-tab persistKey (the QC + Sample tabs have different columns).
  // filteredRows/sort keep using the full `cols` (so a filter/sort on a hidden
  // column still works); only the header + body RENDER from visibleColumns.
  const cm = useColumnManager(cols, c => c.key, c => c.header, `mor-adhoc-${isQc ? 'qc' : 'sample'}`);
  const { openHeaderMenu, headerMenu } = useHeaderMenu(sortApi, cm.columnMenu);

  // Apply the active per-column filters — a row passes when, for every column
  // with an active filter, the row's value for that column is in the allowed
  // set chosen in that column's dropdown.
  const filteredRows = useMemo(() => {
    const active = cols.filter(c => colFilters[c.key]);
    let out: RetrieveRow[] = rows.slice();
    if (active.length > 0)
      out = out.filter(r => active.every(c => colFilters[c.key]!.has(c.value(r))));
    // Sort by the active column using its formatted display value, so the
    // user sorts by exactly what they see in the cell.
    const byKey = new Map(cols.map(c => [c.key, c]));

    // Mixed-direction composite for JobID + SampleID — user feedback
    // 2026-06-01: "jobid desc, sample id asc".  Primary key respects the
    // user's direction toggle; secondary key (SampleID) is always ASC so
    // within each JobID group the samples read 001, 002, 003…  This needs
    // its own sort because useSort applies a single multiplier to both
    // halves of the composite — we want the multiplier on JobID only.
    if (sortApi.sort.key === 'jobId' || sortApi.sort.key === 'sampleID') {
      const dir = sortApi.sort.dir;
      if (dir) {
        const jobMult = dir === 'asc' ? 1 : -1;
        out = out.slice().sort((a, b) => {
          const ja = (a.jobId ?? '').toString();
          const jb = (b.jobId ?? '').toString();
          // Numeric-aware compare on JobID so "260512005" > "260428006".
          const jobCmp = ja.localeCompare(jb, undefined, { numeric: true, sensitivity: 'base' });
          if (jobCmp !== 0) return jobMult * jobCmp;
          // Secondary: SampleID ASC, always.
          const sa = (a.sampleID ?? '').toString();
          const sb = (b.sampleID ?? '').toString();
          return sa.localeCompare(sb, undefined, { numeric: true, sensitivity: 'base' });
        });
      }
    } else {
      out = sortApi.applyByFormatted(out, (r, k) => byKey.get(k)?.value(r) ?? '');
    }
    return out;
  }, [rows, cols, colFilters, sortApi]);

  const anyFilter = Object.keys(colFilters).length > 0;

  // Emit the (filter ∩ selection) IDs to the parent on every change so it
  // can forward them to the report-preview endpoint.
  useEffect(() => {
    if (!onVisibleRowsChange) return;
    const ids = filteredRows
      .filter(r => selected.has(r.sampleParameterId))
      .map(r => r.sampleParameterId);
    onVisibleRowsChange(ids);
  }, [filteredRows, selected, onVisibleRowsChange]);


  // Pagination is owned by the OUTER page (200 rows/server-page).  Inside
  // the grid we render every row this server page contains — no inner
  // 50/page splitter — so the user sees one consistent page count below
  // the table instead of two conflicting paginations stacked on top of
  // each other.  Column filters apply BEFORE display.
  const total      = filteredRows.length;
  const slice      = filteredRows;
  const totalCols  = cm.visibleColumns.length + 1; // + leading checkbox column

  // Only short-circuit when the tab genuinely has NO rows — when rows exist
  // but the column filters exclude them all we keep the table (and its filter
  // inputs) on screen so the user can adjust/clear the filters.
  if (rows.length === 0) {
    return (
      <div className="mor-results-empty">
        {isQc
          ? 'No QC controls match the current filters.'
          : 'No sample rows match the current filters.'}
      </div>
    );
  }

  return (
    <div>
      {/* Retrieve results grid — uses the standard .mlv-table look (BO UI
          Standard §9: --ai-header-bg sticky header, NO zebra, grey
          --ai-row-hover) so it matches the other list grids (Sampling
          Allocation / Subout / Analysis / Reporting). */}
      {/* This in-workflow "Retrieve results" preview grid is a hand-rolled
          <table className="mlv-table"> (select-all/per-row selection + column
          filters), NOT the shared <AppDataGrid>. It now has the SAME global column
          features as every other grid via the shared useColumnManager + useHeaderMenu
          hooks (right-click Sort · Hide · Show All · Column Chooser, drag-reorder,
          resize, persistence — 2026-08-05); a full <AppDataGrid> migration is still
          possible later but no longer needed for feature parity. */}
      <div className="mlv-tbl-scroll" style={{ maxHeight: 480, border: '1px solid var(--ai-line)', borderRadius: 6 }}>
        <table className="mlv-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input
                  type="checkbox"
                  title="Select all"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                />
              </th>
              {/* Header layout = `.col-hdr-cell` + `.col-hdr-label` +
                  `<ColFilterButton variant="inline">` — same pattern Backlog
                  / Subout / Analysis / Reporting use, so the column-filter
                  dropdown looks + behaves identically here.  User feedback
                  "5th time the filter option not like other grid" —
                  2026-05-30. */}
              {/* Array-driven from the column model → Hide / reorder keep header +
                  body aligned. Right-click = the shared menu (Sort · Hide · Show All ·
                  Column Chooser); each column draggable + resizable (.adg-resize). */}
              {cm.visibleColumns.map(col => (
                <th
                  key={col.key}
                  className={cm.dragClass(col.key) || undefined}
                  style={{ cursor: 'pointer', ...(cm.colWidth(col.key) ? { width: cm.colWidth(col.key), minWidth: cm.colWidth(col.key) } : {}) }}
                  onContextMenu={(e) => openHeaderMenu(e, { field: col.key, label: col.header })}
                  {...cm.dragProps(col.key)}
                >
                  <div className="col-hdr-cell">
                    <span className="col-hdr-label" onClick={() => sortApi.setSort(col.key)} title="Click to sort">
                      <SortableHeader label={col.header} sortKey={col.key} sort={sortApi.sort} />
                    </span>
                    <ColFilterButton
                      variant="inline"
                      col={col.key}
                      label={col.header}
                      rows={rows}
                      formatter={(r: RetrieveRow) => col.value(r)}
                      /* filter list follows the column sort; default ASC
                         (first column "Client" is not an ID) when nothing is click-sorted */
                      sortDir={sortApi.sort.key === col.key ? sortApi.sort.dir : 'asc'}
                      value={(colFilters[col.key] ?? null) as ColumnFilterValue}
                      onChange={v => setColFilters(s => {
                        const n = { ...s };
                        if (v === null) delete n[col.key];
                        else             n[col.key] = v;
                        return n;
                      })}
                    />
                  </div>
                  {/* Resize handle (shared .adg-resize) — every column resizable. */}
                  <span className="adg-resize" title="Drag to resize"
                    onMouseDown={(e) => cm.startResize(col.key, e)}
                    onClick={(e) => e.stopPropagation()} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 ? (
              <tr>
                <td colSpan={totalCols} style={{ padding: '14px 10px', textAlign: 'center', color: 'var(--ai-muted)' }}>
                  No rows match the column filters.
                </td>
              </tr>
            ) : slice.map(r => (
              <tr key={r.sampleParameterId}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(r.sampleParameterId)}
                    onChange={() => toggleRow(r.sampleParameterId)}
                  />
                </td>
                {cm.visibleColumns.map(col => {
                  const v = col.value(r);
                  const w = cm.colWidth(col.key);
                  return (
                    <td
                      key={col.key}
                      style={{
                        whiteSpace: col.nowrap ? 'nowrap' : undefined,
                        fontFamily: col.mono ? 'monospace' : undefined,
                        fontWeight: (col.qcType || col.link) ? 600 : undefined,
                        color: col.link ? 'var(--ai-blue)' : col.qcType ? 'var(--brand-accent-hover, var(--ai-orange-dark))' : undefined,
                        ...(w ? { width: w, minWidth: w, maxWidth: w } : {}),
                      }}
                    >
                      {col.pill
                        ? (v ? <span className={`bl-pill ${statusVariant(v)}`}>{v}</span> : '')
                        : v}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Header right-click menu (Sort · Hide · Show All · Column Chooser) +
            the Column Chooser popup — both portal to <body>, rendered once. */}
        {headerMenu}
        {cm.chooser}
      </div>


      {/* 'Showing N rows on this page' caption — server-side Prev / Next /
          Page X of N lives below this grid (rendered by the outer page).
          The inner 50/page slicer was removed 2026-05-30: now one server
          page (200 rows) shows as a single scrollable table so the user
          sees ONE pagination control, not two stacked on top of each other. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 4px 0', fontSize: 11, color: 'var(--ai-muted)',
      }}>
        <span>
          {total === 0
            ? 'No rows match the column filters'
            : <>Showing <strong>{total.toLocaleString()}</strong> rows on this page</>}
          {anyFilter && total !== rows.length && (
            <span style={{ color: 'var(--ai-muted)' }}> (filtered from {rows.length.toLocaleString()})</span>
          )}
        </span>
      </div>
    </div>
  );
}

